import { defineStore } from 'pinia'
import axios from 'axios'
import moment from 'moment';
import { nextTick } from 'vue'
import useNotesStore from "@/stores/notes";
import {
  patchOrgCustomFilterValue,
  applyCustomTagFilterToReportRows,
  filteredRosterLearnerEmailSet,
  hasActiveCustomTagSelections,
  sortReportRowsByLearnerName,
} from '@/utils/customFilters'
import { buildModuleSidebarItems, isSupportedKnowledgeCheckType } from '@/utils/knowledgeChecks.js'
import {
  buildModuleSidebarOptions,
  modulesForLearnerEnrollment,
  userCourseHasKnowledgeChecks,
} from '@/utils/courseLicenseAccess.js'
import { calculateProjectedPercentForLicenseType } from '@/utils/courseProgressPercent.js'
import { safeSessionStorage } from '@/utils/safePersistStorage.js'
import {
  createDefaultNfhsDigitalState,
  createNfhsDigitalReader,
  mergeNfhsPublication,
  defaultMaximizedPanel,
  clampNfhsPanel,
  nextDockTop,
  viewportSize,
  clamp as clampNumber,
  NFHS_PANEL_Z_BASE,
  NFHS_DOCK_WIDTH,
  NFHS_DOCK_HEIGHT,
  NFHS_INSET,
} from '@/utils/nfhsDigital.js'
import { requestNfhsEmbedLogout } from '@/utils/nfhsEmbedBridge.js'
import { recordNfhsBookOpen } from '@/utils/nfhsDigitalApi.js'
import {
  findCurrentOrgInstructor,
  instructorHasLimitedSports,
  getInstructorAllowedSports,
  uniqueSportsFromItems,
  contentSportIsAllowed,
} from '@/utils/instructorSportAccess'
import {
  backpackItemKey,
  buildPublicationBackpackItem,
  createDefaultReaderBackpackState,
  isFavoritePromptSuppressed,
  loadBackpackItems,
  saveBackpackItems,
} from '@/utils/readerBackpack.js'
import {
  fetchUnviewedOrganizationCourseUpdates,
  markOrganizationCourseUpdateViewed,
  buildUnviewedCountsByCourseId,
} from '@/services/organizationCourseUpdateLogService.js'
import { buildUnviewedOrgCourseUpdateMaps } from '@/utils/organizationCourseUpdateLog.js'
import {
  generateGroupWatchKnowledgeSessionCode,
  isPlausibleJoinCodeString,
  setGroupWatchKnowledgePreviewCode
} from '@/utils/groupWatchKnowledgePreviewCode.js'
import {
  organizationIsSupplemental,
  organizationSeatLicenseType,
  resolveSeatLicenseType,
} from '@/components/refRepsPortal/portalOrganization.js'
import { organizationIsPublications } from '@/utils/organizationPublications.js'
import { normalizeAssignedExamDataResponse } from '@/utils/examAssignment.js'
import {
  courseNeedsUpdateHoldOn,
  courseUpdateHoldOnApplies,
  normalizeHoldOnAction,
  shouldShowTakeHoldOn,
  buildTakeHoldOnSeenEntry,
  cacheCourseUpdateWindow,
  withCachedCourseUpdateWindow,
  courseShowsUpdateComingSoonBadge as courseShowsUpdateComingSoonBadgeHelper,
  getCourseUpdateExpectations,
} from '@/utils/courseUpdateExpectations.js'
import { normalizeFailAction } from '@/utils/quizFailAction'
import { orderModuleQuizQuestions } from '@/utils/moduleQuizOrder'
import { normalizeGroupWatchFacilitatorAggregates } from '@/utils/groupWatchFacilitatorAggregates.js'
import { isPremiumLearningActivity } from '@/utils/learningActivityAssignment.js'

/** Ensures custom filters always have `id` (APIs may only return `_id`). */
function normalizeCustomFilter(raw) {
  if (!raw) return null
  const id = raw.id != null ? raw.id : raw._id
  if (id == null) return null
  return { ...raw, id }
}

function normalizeKnowledgeCheckProgress (raw) {
  return Array.isArray(raw) ? raw : []
}

function progressModuleId (item) {
  return item?.moduleId ?? item?.module_id ?? null
}

function progressKnowledgeCheckId (item) {
  return item?.knowledgeCheckId ?? item?.knowledge_check_id ?? null
}

function progressIsComplete (item) {
  return item?.isComplete === true || item?.is_complete === true || item?.completed === true
}

/**
 * Optional flags from POST /join-group-watch-knowledge-session when the instructor skipped the learner
 * or marked their activity complete without a normal submission.
 *
 * Do not infer from generic `learnerStatus` / `yourStatus` — those often reflect course completion for this KC,
 * not “completed for you by instructor” in the current group watch.
 */
function parseJoinGroupWatchLearnerResolution (data) {
  const d = data && typeof data === 'object' ? data : {}
  // Do not use wasSkipped / was_skipped here — on resumed sessions it is often persisted for “no longer pending”
  // and is true even when the learner submitted on their own, which wrongly triggers skip UI.
  const skipped = !!(d.skippedByInstructor ?? d.skipped_by_instructor ?? d.instructorSkippedYou ??
    d.instructor_skipped_you)
  const completed = !!(d.markedCompleteByInstructor ?? d.marked_complete_by_instructor ??
    d.instructorCompletedForYou ?? d.instructor_completed_for_you ??
    d.groupWatchMarkedCompleteByInstructor ?? d.group_watch_marked_complete_by_instructor)

  // Session-scoped resolution strings only (not top-level learner course status).
  const resolutionStr = String(
    d.groupWatchLearnerResolutionStatus ??
    d.group_watch_learner_resolution_status ??
    d.groupWatchJoinResolution ??
    d.group_watch_join_resolution ??
    d.groupWatchLearnerStatus ??
    d.group_watch_learner_status ??
    d.currentGroupWatchLearnerResolution ??
    d.current_group_watch_learner_resolution ??
    ''
  )
    .toLowerCase()
    .replace(/-/g, '_')

  const skipped2 =
    skipped ||
    resolutionStr === 'skipped' ||
    resolutionStr === 'skipped_by_instructor'

  const completed2 =
    (completed ||
      resolutionStr === 'completed_by_instructor' ||
      resolutionStr === 'instructor_completed' ||
      resolutionStr === 'marked_complete') &&
    !skipped2

  let msg = d.learnerMessage ?? d.learner_message ?? d.messageForLearner ?? d.message_for_learner ?? null
  if (typeof msg !== 'string' || !msg.trim()) {
    msg = null
  }
  return {
    skippedByInstructor: skipped2,
    completedByInstructor: completed2,
    learnerSessionMessage: msg
  }
}

/**
 * Whether this learner already submitted for *this* group watch activity (rejoin / resume same session).
 * POST /join-group-watch-knowledge-session should use clearly session-scoped field names.
 *
 * Do **not** treat bare `learnerAlreadySubmitted` — many APIs reuse it for course/module completion, which
 * must not lock the UI on a **new** group watch with the same knowledge check id.
 */
function parseJoinGroupWatchLearnerSubmissionState (data) {
  const d = data && typeof data === 'object' ? data : {}
  const direct = !!(
    d.submittedForCurrentActivity ??
    d.submitted_for_current_activity ??
    d.submittedForCurrentGroupWatch ??
    d.submitted_for_current_group_watch ??
    d.groupWatchResponseSubmitted ??
    d.group_watch_response_submitted ??
    d.groupWatchSubmissionRecorded ??
    d.group_watch_submission_recorded ??
    d.learnerSubmittedForGroupWatch ??
    d.learner_submitted_for_group_watch ??
    d.learnerSubmittedForCurrentGroupWatch ??
    d.learner_submitted_for_current_group_watch
  )
  const sessionScopedExplicit = !!(
    d.alreadySubmittedForThisSession ??
    d.already_submitted_for_this_session ??
    d.hasSubmittedForCurrentGroupWatch ??
    d.has_submitted_for_current_group_watch
  )
  const kc = d.knowledgeCheck ?? d.knowledge_check
  const fromKnowledgeCheck =
    kc && typeof kc === 'object'
      ? !!(
          kc.submittedForCurrentGroupWatch ??
          kc.submitted_for_current_group_watch ??
          kc.groupWatchLearnerSubmitted ??
          kc.group_watch_learner_submitted
        )
      : false
  const status = String(
    d.groupWatchSubmissionStatus ??
    d.group_watch_submission_status ??
    d.currentGroupWatchSubmissionStatus ??
    d.current_group_watch_submission_status ??
    d.learnerGroupWatchSubmissionStatus ??
    d.learner_group_watch_submission_status ??
    ''
  )
    .toLowerCase()
    .replace(/-/g, '_')
  const fromStatus = status === 'submitted' || status === 'complete' || status === 'completed' || status === 'recorded'
  /** Explicit restart only — bare `submittedForCurrentActivity: false` is normal “not submitted yet”, not a reset signal. */
  const submissionReset = !!(
    d.groupWatchSubmissionReset === true ||
    d.group_watch_submission_reset === true ||
    d.activityRestarted === true ||
    d.activity_restarted === true
  )
  const learnerAlreadySubmitted = submissionReset
    ? false
    : (direct || sessionScopedExplicit || fromKnowledgeCheck || fromStatus)
  return {
    learnerAlreadySubmitted,
    groupWatchSubmissionReset: submissionReset
  }
}

/** Current live attempt id for this KC — changes when instructor restarts. */
function parseGroupWatchActivityAttemptId (data) {
  const d = data && typeof data === 'object' ? data : {}
  const raw =
    d.activityAttemptId ??
    d.activity_attempt_id ??
    d.knowledgeCheckAttemptId ??
    d.knowledge_check_attempt_id ??
    d.currentActivityAttemptId ??
    d.current_activity_attempt_id ??
    null
  if (raw == null || raw === '') {
    return null
  }
  return String(raw)
}

/**
 * True when join/heartbeat response denies this learner (e.g. not on group watch roster).
 * Backend should set explicit flags or 403 — avoid returning 200 + groupWatchId for non-roster users.
 */
function isJoinGroupWatchRosterDenied (data) {
  const d = data && typeof data === 'object' ? data : {}
  if (d.notOnRoster === true || d.not_on_roster === true) return true
  if (d.rosterDenied === true || d.roster_denied === true) return true
  if (d.onGroupWatchRoster === false || d.on_group_watch_roster === false) return true
  const code = String(d.code ?? d.error ?? '').replace(/-/g, '_').toUpperCase()
  if (code === 'NOT_ON_ROSTER' || code === 'NOT_ON_GROUP_WATCH_ROSTER') return true
  if (code === 'LICENSE_MISMATCH' || code === 'SEAT_LICENSE_MISMATCH') return true
  const msg = String(d.message ?? '').toLowerCase()
  if (msg.includes('not on the roster') || msg.includes('not on roster')) return true
  if (msg.includes('seat license') && msg.includes('does not match')) return true
  if (msg.includes('license') && msg.includes('does not match')) return true
  return false
}

function joinGroupWatchKnowledgeDeniedResult (data) {
  const d = data && typeof data === 'object' ? data : {}
  const msg =
    (typeof d.message === 'string' && d.message.trim()) ||
    (typeof d.error === 'string' && d.error.trim()) ||
    'You are not on the roster for this group watch. Ask your instructor to add you to attendance or assign this course.'
  return {
    notAllowed: true,
    notAllowedReason: msg,
    knowledgeCheck: null,
    waiting: false,
    groupWatchId: null,
    moduleId: null,
    courseId: null,
    lastKnowledgeCheckId: null,
    skippedByInstructor: false,
    completedByInstructor: false,
    learnerAlreadySubmitted: false,
    learnerSessionMessage: null
  }
}

export default defineStore('main', {
  state: () => {
    return {
      statesArray: [
        { name: "Alabama", abbreviation: "AL" },
        { name: "Alaska", abbreviation: "AK" },
        { name: "Arizona", abbreviation: "AZ" },
        { name: "Arkansas", abbreviation: "AR" },
        { name: "California", abbreviation: "CA" },
        { name: "Colorado", abbreviation: "CO" },
        { name: "Connecticut", abbreviation: "CT" },
        { name: "Delaware", abbreviation: "DE" },
        { name: "Florida", abbreviation: "FL" },
        { name: "Georgia", abbreviation: "GA" },
        { name: "Hawaii", abbreviation: "HI" },
        { name: "Idaho", abbreviation: "ID" },
        { name: "Illinois", abbreviation: "IL" },
        { name: "Indiana", abbreviation: "IN" },
        { name: "Iowa", abbreviation: "IA" },
        { name: "Kansas", abbreviation: "KS" },
        { name: "Kentucky", abbreviation: "KY" },
        { name: "Louisiana", abbreviation: "LA" },
        { name: "Maine", abbreviation: "ME" },
        { name: "Maryland", abbreviation: "MD" },
        { name: "Massachusetts", abbreviation: "MA" },
        { name: "Michigan", abbreviation: "MI" },
        { name: "Minnesota", abbreviation: "MN" },
        { name: "Mississippi", abbreviation: "MS" },
        { name: "Missouri", abbreviation: "MO" },
        { name: "Montana", abbreviation: "MT" },
        { name: "Nebraska", abbreviation: "NE" },
        { name: "Nevada", abbreviation: "NV" },
        { name: "New Hampshire", abbreviation: "NH" },
        { name: "New Jersey", abbreviation: "NJ" },
        { name: "New Mexico", abbreviation: "NM" },
        { name: "New York", abbreviation: "NY" },
        { name: "North Carolina", abbreviation: "NC" },
        { name: "North Dakota", abbreviation: "ND" },
        { name: "Ohio", abbreviation: "OH" },
        { name: "Oklahoma", abbreviation: "OK" },
        { name: "Oregon", abbreviation: "OR" },
        { name: "Pennsylvania", abbreviation: "PA" },
        { name: "Rhode Island", abbreviation: "RI" },
        { name: "South Carolina", abbreviation: "SC" },
        { name: "South Dakota", abbreviation: "SD" },
        { name: "Tennessee", abbreviation: "TN" },
        { name: "Texas", abbreviation: "TX" },
        { name: "Utah", abbreviation: "UT" },
        { name: "Vermont", abbreviation: "VT" },
        { name: "Virginia", abbreviation: "VA" },
        { name: "Washington", abbreviation: "WA" },
        { name: "West Virginia", abbreviation: "WV" },
        { name: "Wisconsin", abbreviation: "WI" },
        { name: "Wyoming", abbreviation: "WY" }
      ],
      mode: import.meta.env.VITE_MODE,
      currentOpenMenu: '',
      courseToDownLoad: {},
      //Course
      showCourseModal: true,
      showPasswordReset: false,
      organizationCourses: [],
      userCourses: [],
      userPacks: [],
      userLearningActivities: [],
      selectedLearningActivity: {},
      activityInsights: [],
      currentCourse: {},
      active: 0,
      nonPublicCourseIds: [],
      showVideo: false,
      videoPreviewLink: '',
      currentVideoTime: '0:00',
      markerTime: 0,
      currentVideo: '',
      progress: [],
      videoCount: 0,
      videoEnded: false,
      selectedFilters: [],
      currentAnswer: '',
      completeVideos: [],
      completeQuestion: [],
      currentModule: [],
      currentModuleID: '',
      currentQuizIndex: 0,
      currentQuizTotal: 0,
      currentQuizScore: 0,
      showQuizResults: false,
      missedQuestions: [],
      isLoadingDashboard: false,
      isLoadingLearningActivities: false,
      showViewer: false,
      url: '',
      currentVideoID: '',
      completedIDs: [],
      currentProgressID: '',
      courseModules: [],
      currentEnrolled: 0,
      hasCompletedCourse: false,
      currentItem: {},
      itemType: '',
      currentUserCourse: {},
      hideNavBar: false,
      showCreditOverlay: true,
      currentPage: '',
      hasClickedExploreOnce: false,
      hasCompletedModule: false,
      organization: {},
      showTermsModal: false,
      isLoadingStatePortal: false,

      //RefReps
      repCourses: [],
      currentRepsCourse: {},
      currentPreviewVideo: {},
      showPreviewInteractive: false,
      currentVideoViewUrl: '',
      currentVideoVTT: 'data:text/vtt,WEBVTT',
      currentVideoForTranslations: {},
      currentVideoDescription: '',
      isCurrentVideo: true,
      hasFinishedRepsCourse: false,

      //Player
      showCourseModal: false,
      isWatchingVideo: false,
      player: null,
      currentVideo: '',
      currentVideoTime: '',
      showCertificate: false,
      videoTitle: '',
      m_id: '',
      v_id: '',

      //Marketplace
      courses: [],
      fullStore: [],
      purchases: [],
      activeCartItems: [],
      videoPreviewLink: '',
      showPreview: false,
      mediaPlaylist: [],
      repCourses: [],
      hasAppliedDiscount: false,
      hasAppliedCredit: false,
      baseCourseUnitPrice: 50.0,
      baseInteractiveUnitPrice: 25.0,
      discountCode: '',
      hasAppliedCredits: false,
      discountAmount: 0,
      discountEligibleCourseIds: [], // Array of course IDs eligible for the current discount code
      showNeedsLogin: false,
      shouldInitializeDashboard: false, // Flag to trigger dashboard initialization when login happens from store
      showPDFViewer: false,
      statePortalShowPDFViewer: false,

      //User
      isLoggedIn: false,
      showLoginType: 'login',
      isLoading: false,
      isWatchingVideo: false,
      currentVideo: '',
      currentVideoTime: '',
      showCourseModal: false,
      user: {},
      baseCredit: {},
      email: '',
      organizationIDToRegister: undefined,
      activeAnnouncements: [],
      activeSideAnnouncements: [],
      activeQuestionnaire: null,
      showQuestionnaire: true,
      showLearnerQuestionnaire: false,
      showOrgSetup: 'register',
      currentRouteEnrollLink: '',
      orgData: null,
      showHelp: true,
      hasCreditAvailable: false,
      currentInstructorCourse: {},
      currentInstructorPack: {},
      currentCoursePage: 'courseList',
      /** Set from dashboard/reports; CourseList opens catalog update modal then clears. */
      pendingCatalogCourseUpdateCourseId: null,
      statePortalCurrentCoursePage: 'courseList',
      statePortalCurrentDisplay: 'dashboard',
      currentDisplay: 'dashboard',
      profileTabIntent: null,
      notifications: [],
      studentCourses: [],
      isGettingMessages: false,
      isGettingCourses: false,
      statePortalIsGettingCourses: false,
      isRefreshingCourses: false,
      isGettingStudents: false,
      isGettingReports: false,
      isGettingResources: false,
      isGettingReportData: false,
      currentCourseForReport: {},
      currentRepsCourseAttempts: [],
      isLoadingOrganizationCertificateHistory: false,
      organizationCertificateHistoryLoaded: false,
      organizationCertificateHistory: [],
      isLoadingOrganizationPackCompletionHistory: false,
      organizationPackCompletionHistory: [],
      totalOrgPackCompletions: 0,
      students: [],
      organizationCustomFilters: [],
      archivedUsers: [],
      studentsForReports: [],
      currentMessage: {},
      allCourses: [],
      statePortalAllCourses: [],
      activeAddedStudentIds: [],
      selectedAddStudentOption: '',
      noStudentsActive: [],
      onlineStudents: [],
      noProgressCourses: [],
      showNewStudent: false,
      /** Prefills Manage Learners search when opened from portal learner roster. */
      studentsViewSearchPrefill: '',
      /** Opens Manage Learners certificates slide-over for a learner ({ name, email? }). */
      studentsViewOpenCertificatesFor: null,
      statePortalShowNewStudent: false,
      companyInviteID: null,
      showInvite: false,
      currentKahootPage: 'home',
      gameIsActive: false,
      activities: [],
      socket: {},
      notificationSocket: {},
      gameVideoPlayer: {},
      gamePlayers: [],
      isShowingNames: true,
      gameInfo: {
        instructor: '',
        pin: 0
      },
      gameHasStarted: false,
      currentGameVideo: '',
      currentGameInteractiveCourse: {},
      currentGameQuestion: {},
      currentGameQuestionAnswers: {},
      gameAnswers: [],
      showWaiting: false,
      gameErrorMessage: '',
      showGameCountdown: true,
      sports: [],
      chart1: {},
      resources: [],
      cCourseID: '',
      reportsShowSupplementalPacks: false,
      reportsContentTypeFilter: 'course',
      averageScores: 0,
      currentCallNumber: 0,
      studentData: [],
      /** Learner-tag filter selections on Reports (same shape as Manage Learners). */
      reportsCustomFilterSelections: [],
      /** Reports table sort: firstName | lastName (same as Manage Learners). */
      reportsSortBy: 'firstName',
      studentLearningActivityData: [],
      studentExamAssignmentData: [],
      studentExamReportData: [],
      currentlyAssignedStudents: [],
      currentlyUnassignedStudents: [],
      showRefreshingData: false,
      showNewYorkDocs: false,
      currentPlayerPosition: 0,
      statePortalCurrentPlayerPosition: 0,
      instructorVideosForPreview: [],
      statePortalInstructorVideosForPreview: [],
      selectedStudentData: [],
      codeIsValid: false,
      codeType: '',
      showFollowUpChoices: false,
      showYouMakeTheCall: false,
      instructorPOVCall: false,
      instructorPOVCallCounter: 0,
      postQuestions: [
        {
          question: 'What sport course did you just complete?',
          choices: [
            'Baseball',
            'Basketball',
            'Football',
            'Softball',
            'Track/Cross Country',
            'Wrestling',
            'Swimming & Diving',
            'Soccer',
            'Girls Lacrosse',
            'Boys Lacrosse',
            'Ice Hockey',
            'Volleyball',
            'Flag Football',
            '4v4 Flag Football',
            'Cheerleading',
            'Officiating Handbook',
            'Field Hockey'
          ],
          answerType: 'multiple-choice',
          canChoose: false,
          required: true,
          isShowing: true
        },
        {
          question: 'In what state do you currently reside?',
          answerType: 'drop-down',
          canChoose: true,
          required: false,
          isShowing: true
        },
        {
          question: 'Which of the following are you?',
          choices: ['High School Student', 'College Student', 'Adult Learner', 'Other'],
          additional: {
            question: 'What grade are you currently in?',
            choices: ['6th', '7th', '8th', '9th', '10th', '11th', '12th'],
            answerType: 'multiple-choice',
            canChoose: true
          },
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question: 'What age group do you fall in?',
          choices: [
            '12-17 years old',
            '18-24 years old',
            '25-34 years old',
            '35-44 years old',
            '45-54 years old',
            '55-64 years old',
            '65-74 years old',
            '75 years or older'
          ],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question: 'Which of the following are you?',
          choices: ['Male', 'Female', 'Prefer to not answer'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question: 'Do you currently play this sport competitively?',
          choices: ['Yes', 'No'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'Prior to this class, have you been trained in or officiated contests in this sport?',
          choices: ['Yes', 'No'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'Now that I have completed this sport course in the RefReps OES, I believe I can apply my knowledge of the rules to make the right calls.',
          choices: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'Now that I have completed this sport course in the RefReps OES, I believe I can determine if a foul, violation, or penalty has been committed.',
          choices: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'Now that I have completed this sport course in the RefReps OES, I believe I can stand in the correct location to make the right call.',
          choices: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'Now that I have completed this sport course in the RefReps OES, I believe I know which zone or area I should be monitoring.',
          choices: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: true,
          isShowing: true
        },
        {
          question:
            'What suggestions do you have for the RefReps team to improve your learning experience?',
          answerType: 'text-box',
          canChoose: true,
          required: false,
          isShowing: true
        },
        {
          question:
            'I plan to find ways to learn more about officiating this sport and find contests in my community to officiate.',
          choices: ['Definitely Yes', 'Highly Likely', 'Maybe, but Probably Not', 'Definitely No'],
          answerType: 'multiple-choice',
          canChoose: true,
          required: false,
          isShowing: true
        }
      ],
      currentPostQuestion: 0,
      showGetStarted: false,
      inactiveOrganizationVideoIds: [],
      inactiveOrganizationModuleIds: [],
      isUserInstructor: false,
      currentMiddleVideoId: '',
      showChallengeIntro: false,
      showCompletionResult: false,
      showResourceModal: false,
      showOHSAADocs: false,
      OHSAAIds: [
        
      ],
      NYIds: ['284e4778-23b9-4a5f-aee6-8ee2227cf871'],
      showCourseResult: false,
      showModuleResult: false,
      viewModuleResultsFromUserClick: false,
      previousItemSelected: {},
      showChallengeVideoPlayer: false,
      challenges: {},
      presentChallengeToUser: false,
      showSurvey: false,
      currentVTT: '',
      activeVideoID: '',
      textTrack: {},
      userNotifications: [],
      showUpdatedCourse: false,
      tomBeardCourse: {},
      tomBeardTotalTime: 0,
      tomBeardCurrentTime: 0,
      tomBeardPlayer: null,
      isPlayingVideo: false,
      isSyncedVideo: false,
      syncedTime: 0,
      isNewVideoLoad: false,
      isFirstClickSync: false,
      lastClickedVideo: '',
      repsPreviewPlayer: null,
      hasTakenFirstChallenge: false,
      hasTakenMiddleChallenge: false,
      hasTakenLastChallenge: false,
      statePortalOrganizations: [],
      statePortalStateOrganizationCount: 0,
      statePortalImportedOrganizationTypes: [],
      statePortalOutreachOrganizations: [],
      statePortalSelectorEntries: [],
      statePortalLearners: [],
      statePortalReportsDeepLink: null,
      statePortalManageUsersGroup: null,
      statePortalManageUsersEntityType: null,
      challengePosition: '',
      challengePacksActive: true,
      captionTrack: null,
      lastClickedVideoSynced: false,
      isShowingPOVCourseView: false,
      isLoadingPOVCourse: false,
      displayCourseChallenge: false,
      isInDevMode: false,
      searchResults: [],
      documents: [],
      videos: [],
      podcasts: [],
      rules: [],
      forums: [],
      showOHSAAGenerateTest: false,
      initialCredit: 0,
      showZipCodeModal: false,
      showZipCodeModal2: false,
      showRenewCertificateModal: false,
      showPOVCertificate: false,
      hasBeenNotifiedAboutRenewal: false,
      hasBeenNotifiedAboutOHSAAHandbookCourse: false,
      isDevLoggedIn: false,
      developerAccess: false,
      userExams: [],
      userResources: [],
      showCopiedToClipboardToast: false,
      copiedToClipboardMessage: '',
      userOrgs: [],
      stateAffiliations: [],
      showExamNotification: false,
      exam_url: '',
      isAddingNewOrganization: false,
      showAddNewOrganizationError: false,
      organizationAddStatus: '',
      showAddOrganization: false,
      orgCode: '',
      isLoadingInstructor: false,
      isLoadingUserNotifications: false,
      completed_videos: [],
      completed_modules: [],
      current_knowledge_check_id: null,
      completed_quizzes: [],
      quiz_attempts: [],
      currentIndex: 0,
      currentLearningActivity: {},
      currentLearningActivityPage: '',
      learningActivityInitialTab: '', // e.g. 'multiAssignLearners' when opening from CourseDetail Assign
      percent_complete: 0,
      current_module: {},
      quiz_questions: [],
      selected_course: {},
      user_course: {},
      currentCourseView: 'player',
      currentCourseMode: 'home',
      current_video: {},
      currentVideoTimeDisplay: '',
      current_note: '',
      course_notes: [],
      showQuizAdvancementModal: false,
      moduleIndex: 0,
      lastModuleActivity: 'video',
      lastActiveVideo: '',
      showExample: false,
      currentVideoPlayer: 'VideoPlayer',
      exampleVideoURL: '',
      previousVideoTime: 0,
      currentModal: '',
      currentModalType: '',
      currentUserOrganization: {},
      active_module: {},
      quiz_score: 0,
      showResults: false,
      module_history: [],
      module_optional_history: [],
      active_quizzes: [],
      didAcknowledgeCheerCourse: false,
      showCheerUpdate: false,
      isLoadingCourse: false,
      showCertificateForDownload: false,
      survey_questions: [],
      survey_message: '',
      userType: '',
      studentData: [],
      isOnline: true,
      currentLoadingVideoId: '',
      showStateInfoMessage: false,
      course_modules: [],
      showInstructorCourseActions: true,
      statePortalShowInstructorCourseActions: true,
      global_settings: {},
      course_settings: null,
      showOptionalMaterial: [],
      isLoadingPreviewVideo: false,
      currentModuleIndex: 0,
      statePortalisLoadingPreviewVideo: false,
      showChallenge: false,
      challenge: null,
      current_challenge: '',
      exam_requests: [],
      showSubmitTicketModal: false,
      ticketNumber: '',
      showTicketConfirmation: false,
      role: '',
      showTicketForm: false,
      orgStudents: [],
      screenShots: [],
      isDragging: false,
      hover: false,
      currentSupportView: 'TopIssues',
      updatedCourse: {},
      errors: [],
      logs: [],
      isHovering: false,
      showNewFeatureBackDrop: false,
      showSupportModal: false,
      requestInstructorPlatformTutorial: false,
      requestPortalPurchasingTutorial: false,
      requestPortalCourseUpgrade: false,
      requestPortalLearnersModal: false,
      requestPortalCoursesModal: false,
      requestPortalCourseIds: [],
      requestPortalNfhsPublicationAssign: null,
      /** Open purchasing portal Purchase History: `{ tab?: 'invoices'|'quotes', focus?: 'nfhs_publication_assign', documentId?: string }` */
      requestPortalPurchaseHistory: null,
      licensingPortalOverlayOpen: false,
      pendingNfhsAddonTier: null,
      nfhsPublicationsSportFilter: null,
      showEducationalCoursesUpgradeModal: false,
      upgradeModalContext: 'supplemental',
      hasShownSupportButton: false,
      showAnalytics: false,
      connect: {
        name: ''
      },
      showCoursePreview: false,
      didSaveSettings: false,
      showSupportButton: true,
      isSavingSettings: false,
      searchText: '',
      lastModified: 0,
      isExiting: false,
      isSaving: false,
      cheerFilter: 'all',
      showNotifications: false,
      showInstructorNotifications: false,
      currentNotificationFilter: 'all',
      isSyncingData: false,
      showNewMessage: false,

      selectedCourseType: '',
      currentChat: {},
      chatStudents: [],
      bookMarkStudents: [],
      currentChatUser: {},
      chats: [],
      isTyping: false,
      showNewChatMessage: false,
      inComingMessage: {
        message_id: '',
        senderID: '',
        senderName: '',
        message: '',
        profileImage: ''
      },
      isInChatRoom: false,
      currentCommunicationView: 'chat',
      messageView: 'read',
      organizationNotifications: [],
      selectedNotification: {},
      preferredLanguage: 'es',
      notificationCameFrom: '',
      isLoadingNotifications: false,
      notificationView: 'list',
      currentTime: 0,
      currentMessageDetailView: 'message',
      postDate: 'Will post immediately',
      showDashboardChoice: false,
      courseListFilter: 'course',
      statePortalCourseListFilter: 'course',
      multiAssignStudents: [],
      currentCourseIDToAssign: '',
      showAssignCourse: false,
      showHelp: false,
      showMultiAssign: false,
      selectedStudent: {},
      willAssign: [],
      isInMultiAssignMode: false,
      showAddSeats: false,
      /** Unread licensing-portal request notifications (synced from RefReps portal). */
      portalRequestNotifCount: 0,
      interests: [],
      showCertificates: false,
      isLoadingPOVCourseData: false,
      unreadMessages: 0,
      unreadNotifications: 0,
      showInstructorNotifications: false,
      instructorNotifications: [],
      showCopied: false,
      emailCopied: '',
      pendingRequests: [],
      addNewRuleContent: false,
      showRulebookSaved: false,
      content: '',
      rulebooks: [],
      currentRulebook: {},
      currentRuleId: '',
      currentSectionId: '',
      currentSectionTitle: '',
      currentRule: {},
      currentSection: {},
      sectionName: '',
      notes: '',
      selectedRulebook: {},
      showingRulebook: false,
      ruleSearch: '',
      showRuleBookOverlay: false,
      nfhsDigital: {
        nextZ: NFHS_PANEL_Z_BASE + 1,
        readers: [],
      },
      readerBackpack: createDefaultReaderBackpackState(),
      showRuleVideo: false,
      statePortalNotifications: [],
      selectedStatePortalNotification: {},
      statePortalMessageView: 'read',
      currentStatePortal: {},
      currentStatePortalMessageDetailView: 'message',
      statePortalCourseTab: 'details',
      statePortalCurrentInstructorCourse: {},
      statePortalCurrentModule: {},
      statePortalFileName: '',
      statePortalUrl: '',
      statePortalCourses: [],
      statePortalGeneralCourseResources: [],
      ruleVideoURL: '',
      showCover: false,
      showNotes: false,
      currentNote: '',
      exampleVideoVTT: '',
      course_tab: 'details',
      /** courseId → unviewed org update count (badges). */
      orgCourseUpdateUnviewedByCourseId: {},
      /** courseId → latest unviewed event summary (course card overlay). */
      orgCourseUpdateUnviewedEventByCourseId: {},
      /** courseId → all unviewed event ids (bulk dismiss on view). */
      orgCourseUpdateUnviewedEventIdsByCourseId: {},
      /** Increment to refresh org update notice/log consumers after view or new update. */
      orgCourseUpdateUnviewedRevision: 0,
      /** eventId → true — locally dismissed; ignored until next full courses fetch. */
      dismissedOrgCourseUpdateEventIds: {},
      isLoadingQuiz: false,
      isInstructor: false,
      isFistLoad: true,
      automaticInstructorLogin: true,
      showNoRefundModal: false,
      /** Global HOLD ON gate (purchase / take / assign). */
      updateHoldOnOpen: false,
      updateHoldOnCourse: null,
      /** @type {'take'|'buy'|'assign'|'add'} */
      updateHoldOnAction: 'assign',
      /** Bumps when take HOLD ON is marked seen so course-card badges re-render. */
      takeHoldOnSeenTick: 0,
      _updateHoldOnResolver: null,
      isLoadingSurvey: false,
      isSubmittingSurvey: false,
      learningActivitiesFilterSport: '',
      learningActivitiesFilterCourse: '',
      currentCertificateDownloading: '',
      isLoadingManualProgress: false,
      isLoadingDragonFlyRegistration: false,
      isRegistering: false,
      exams: [],
      stateExams: [],
      companyExams: [],
      isLoadingCompanyExams: false,
      showExamButton: false,
      currentDashboardView: 'dashboard',
      onlineStore: [],
      selectedStoreCourse: {},
      /** Set when resolving /marketplace/content/:slug so dashboard can open store-details. */
      pendingMarketplaceDetails: false,
      allCourseVideos: [],
      statePortalModuleTab: 'videos',
      module_tab: 'videos',
      currentModule: {},
      isSigningOut: false,
      isSidebarOpen: true,
      showExam: false,
      // todos: [],
      selectedSports: [],
      recentActivity: [],
      isEditingDashboard: false,
      statePortalPreviewVideo: '',
      preCourseVideos: null,
      postCourseVideos: null,
      userHasWatchedPreCourseVideo: false,
      hasAccessedCourse: false,
      previewVideo: '',
      isLoadingOnlineCourses: false,
      newNotificationsSeen: [],
      isSearchActive: false,
      currentExam: {},
      currentExamPage: '',
      currentInstructorExam: {},
      statePortalAllModules: [],
      allModules: [],
      isInstructorPOVCallAnalysis: false,
      currentInstructorPOVCallAnalysisVideo: '',
      selectedModule: {},
      makeTheCall: false,
      totalOrgCompletions: 0,
      showVideoPreview: false,
      coursesToEdit: [],
      currentVideoToEdit: {},
      course_settings: {},
      global_settings: {},
      organizations: [],
      userActivityInsights: [],
      currentInstructorsOrgFutureModules: [],
      stateAssociations: [{
        abbreviation: "AL",
        name: "Alabama High School Athletic Association",
        initials: "AHSAA",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "AK",
        name: "Alaska School Activities Association",
        initials: "ASAA",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "AZ",
        name: "Arizona Interscholastic Association",
        initials: "AIA",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "AR",
        name: "Arkansas Activities Association",
        initials: "AAA-AR",
        color: "#8B0000" // Dark Red
      },
      {
        abbreviation: "CA",
        name: "California Interscholastic Federation",
        initials: "CIF",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "CO",
        name: "Colorado High School Activities Association",
        initials: "CHSAA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "CT",
        name: "Connecticut Interscholastic Athletic Conference",
        initials: "CIAC",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "DE",
        name: "Delaware Interscholastic Athletic Association",
        initials: "DIAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "FL",
        name: "Florida High School Athletic Association",
        initials: "FHSAA",
        color: "#FF8C00" // Dark Orange
      },
      {
        abbreviation: "GA",
        name: "Georgia High School Association",
        initials: "GHSA",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "HI",
        name: "Hawaii High School Athletic Association",
        initials: "HHSAA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "ID",
        name: "Idaho High School Activities Association",
        initials: "IHSAA-ID",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "IL",
        name: "Illinois High School Association",
        initials: "IHSA",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "IN",
        name: "Indiana High School Athletic Association",
        initials: "IHSAA-IN",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "IA",
        name: "Iowa High School Athletic Association",
        initials: "IHSAA-IA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "KS",
        name: "Kansas State High School Activities Association",
        initials: "KSHSAA",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "KY",
        name: "Kentucky High School Athletic Association",
        initials: "KHSAA-KY",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "LA",
        name: "Louisiana High School Athletic Association",
        initials: "LHSAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "ME",
        name: "Maine Principals' Association",
        initials: "MPA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "MD",
        name: "Maryland Public Secondary Schools Athletic Association",
        initials: "MPSSAA",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "MA",
        name: "Massachusetts Interscholastic Athletic Association",
        initials: "MIAA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "MI",
        name: "Michigan High School Athletic Association",
        initials: "MHSAA-MI",
        color: "#FF8C00" // Dark Orange
      },
      {
        abbreviation: "MN",
        name: "Minnesota State High School League",
        initials: "MSHSL",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "MS",
        name: "Mississippi High School Activities Association",
        initials: "MHSAA-MS",
        color: "#8B0000" // Dark Red
      },
      {
        abbreviation: "MO",
        name: "Missouri State High School Activities Association",
        initials: "MSHSAA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "MT",
        name: "Montana High School Association",
        initials: "MHSA",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "NE",
        name: "Nebraska School Activities Association",
        initials: "NSAA",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "NV",
        name: "Nevada Interscholastic Activities Association",
        initials: "NIAA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "NH",
        name: "New Hampshire Interscholastic Athletic Association",
        initials: "NHIAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "NJ",
        name: "New Jersey State Interscholastic Athletic Association",
        initials: "NJSIAA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "NM",
        name: "New Mexico Activities Association",
        initials: "NMAA",
        color: "#FF8C00" // Dark Orange
      },
      {
        abbreviation: "NY",
        name: "New York State Public High School Athletic Association",
        initials: "NYSPHSAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "NC",
        name: "North Carolina High School Athletic Association",
        initials: "NCHSAA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "ND",
        name: "North Dakota High School Activities Association",
        initials: "NDHSAA",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "OH",
        name: "Ohio High School Athletic Association",
        initials: "OHSAA",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "OK",
        name: "Oklahoma Secondary School Activities Association",
        initials: "OSSAA",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "OR",
        name: "Oregon School Activities Association",
        initials: "OSAA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "PA",
        name: "Pennsylvania Interscholastic Athletic Association",
        initials: "PIAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "RI",
        name: "Rhode Island Interscholastic League",
        initials: "RIIL",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "SC",
        name: "South Carolina High School League",
        initials: "SCHSL",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "SD",
        name: "South Dakota High School Activities Association",
        initials: "SDHSAA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "TN",
        name: "Tennessee Secondary School Athletic Association",
        initials: "TSSAA",
        color: "#FFD700" // Gold
      },
      {
        abbreviation: "TX",
        name: "Texas University Interscholastic League",
        initials: "UIL",
        color: "#FF8C00" // Dark Orange
      },
      {
        abbreviation: "UT",
        name: "Utah High School Activities Association",
        initials: "UHSAA",
        color: "#2E8B57" // Sea Green
      },
      {
        abbreviation: "VT",
        name: "Vermont Principals' Association",
        initials: "VPA",
        color: "#4682B4" // Steel Blue
      },
      {
        abbreviation: "VA",
        name: "Virginia High School League",
        initials: "VHSL",
        color: "#B22222" // Firebrick Red
      },
      {
        abbreviation: "WA",
        name: "Washington Interscholastic Activities Association",
        initials: "WIAA-WA",
        color: "#1E90FF" // Dodger Blue
      },
      {
        abbreviation: "WV",
        name: "West Virginia Secondary School Activities Commission",
        initials: "WVSSAC",
        color: "#8B0000" // Dark Red
      },
      {
        abbreviation: "WI",
        name: "Wisconsin Interscholastic Athletic Association",
        initials: "WIAA-WI",
        color: "#FF4500" // Orange Red
      },
      {
        abbreviation: "WY",
        name: "Wyoming High School Activities Association",
        initials: "WHSAA",
        color: "#FFD700" // Gold
      },
    ],
    svgString: [`
      <path id="AK" fill="#D3D3D3" d="M161.1,453.7l-0.3,85.4l1.6,1l3.1,0.2l1.5-1.1h2.6l0.2,2.9l7,6.8l0.5,2.6l3.4-1.9l0.6-0.2l0.3-3.1 l1.5-1.6l1.1-0.2l1.9-1.5l3.1,2.1l0.6,2.9l1.9,1.1l1.1,2.4l3.9,1.8l3.4,6l2.7,3.9l2.3,2.7l1.5,3.7l5,1.8l5.2,2.1l1,4.4l0.5,3.1 l-1,3.4l-1.8,2.3l-1.6-0.8l-1.5-3.1l-2.7-1.5l-1.8-1.1l-0.8,0.8l1.5,2.7l0.2,3.7l-1.1,0.5l-1.9-1.9l-2.1-1.3l0.5,1.6l1.3,1.8 l-0.8,0.8c0,0-0.8-0.3-1.3-1c-0.5-0.6-2.1-3.4-2.1-3.4l-1-2.3c0,0-0.3,1.3-1,1c-0.6-0.3-1.3-1.5-1.3-1.5l1.8-1.9l-1.5-1.5v-5h-0.8 l-0.8,3.4l-1.1,0.5l-1-3.7l-0.6-3.7l-0.8-0.5l0.3,5.7v1.1l-1.5-1.3l-3.6-6l-2.1-0.5l-0.6-3.7l-1.6-2.9l-1.6-1.1v-2.3l2.1-1.3 l-0.5-0.3l-2.6,0.6l-3.4-2.4l-2.6-2.9l-4.8-2.6l-4-2.6l1.3-3.2v-1.6l-1.8,1.6l-2.9,1.1l-3.7-1.1l-5.7-2.4h-5.5l-0.6,0.5l-6.5-3.9 l-2.1-0.3l-2.7-5.8l-3.6,0.3l-3.6,1.5l0.5,4.5l1.1-2.9l1,0.3l-1.5,4.4l3.2-2.7l0.6,1.6l-3.9,4.4l-1.3-0.3l-0.5-1.9l-1.3-0.8 l-1.3,1.1l-2.7-1.8l-3.1,2.1l-1.8,2.1l-3.4,2.1l-4.7-0.2l-0.5-2.1l3.7-0.6v-1.3l-2.3-0.6l1-2.4l2.3-3.9v-1.8l0.2-0.8l4.4-2.3l1,1.3 h2.7l-1.3-2.6l-3.7-0.3l-5,2.7l-2.4,3.4l-1.8,2.6l-1.1,2.3l-4.2,1.5l-3.1,2.6l-0.3,1.6l2.3,1l0.8,2.1l-2.7,3.2l-6.5,4.2l-7.8,4.2 l-2.1,1.1l-5.3,1.1l-5.3,2.3l1.8,1.3l-1.5,1.5l-0.5,1.1l-2.7-1l-3.2,0.2l-0.8,2.3h-1l0.3-2.4l-3.6,1.3l-2.9,1l-3.4-1.3l-2.9,1.9 h-3.2l-2.1,1.3l-1.6,0.8l-2.1-0.3l-2.6-1.1l-2.3,0.6l-1,1l-1.6-1.1v-1.9l3.1-1.3l6.3,0.6l4.4-1.6l2.1-2.1l2.9-0.6l1.8-0.8l2.7,0.2 l1.6,1.3l1-0.3l2.3-2.7l3.1-1l3.4-0.6l1.3-0.3l0.6,0.5h0.8l1.3-3.7l4-1.5l1.9-3.7l2.3-4.5l1.6-1.5l0.3-2.6l-1.6,1.3l-3.4,0.6 l-0.6-2.4l-1.3-0.3l-1,1l-0.2,2.9l-1.5-0.2l-1.5-5.8l-1.3,1.3l-1.1-0.5l-0.3-1.9l-4,0.2l-2.1,1.1l-2.6-0.3l1.5-1.5l0.5-2.6 l-0.6-1.9l1.5-1l1.3-0.2l-0.6-1.8v-4.4l-1-1l-0.8,1.5h-6.1l-1.5-1.3l-0.6-3.9l-2.1-3.6v-1l2.1-0.8l0.2-2.1l1.1-1.1l-0.8-0.5 l-1.3,0.5l-1.1-2.7l1-5l4.5-3.2l2.6-1.6l1.9-3.7l2.7-1.3l2.6,1.1l0.3,2.4l2.4-0.3l3.2-2.4l1.6,0.6l1,0.6h1.6l2.3-1.3l0.8-4.4 c0,0,0.3-2.9,1-3.4c0.6-0.5,1-1,1-1l-1.1-1.9l-2.6,0.8l-3.2,0.8l-1.9-0.5l-3.6-1.8l-5-0.2l-3.6-3.7l0.5-3.9l0.6-2.4l-2.1-1.8 l-1.9-3.7l0.5-0.8l6.8-0.5h2.1l1,1h0.6l-0.2-1.6l3.9-0.6l2.6,0.3l1.5,1.1l-1.5,2.1l-0.5,1.5l2.7,1.6l5,1.8l1.8-1l-2.3-4.4l-1-3.2 l1-0.8l-3.4-1.9l-0.5-1.1l0.5-1.6l-0.8-3.9l-2.9-4.7l-2.4-4.2l2.9-1.9h3.2l1.8,0.6l4.2-0.2l3.7-3.6l1.1-3.1l3.7-2.4l1.6,1l2.7-0.6 l3.7-2.1l1.1-0.2l1,0.8l4.5-0.2l2.7-3.1h1.1l3.6,2.4l1.9,2.1l-0.5,1.1l0.6,1.1l1.6-1.6l3.9,0.3l0.3,3.7l1.9,1.5l7.1,0.6l6.3,4.2 l1.5-1l5.2,2.6l2.1-0.6l1.9-0.8l4.8,1.9L161.1,453.7z M46,482.6l2.1,5.3l-0.2,1l-2.9-0.3l-1.8-4l-1.8-1.5H39l-0.2-2.6l1.8-2.4 l1.1,2.4l1.5,1.5L46,482.6z M43.4,516.1l3.7,0.8l3.7,1l0.8,1l-1.6,3.7l-3.1-0.2l-3.4-3.6L43.4,516.1z M22.7,502l1.1,2.6 l1.1,1.6l-1.1,0.8l-2.1-3.1V502H22.7z M9,575.1l3.4-2.3l3.4-1l2.6,0.3l0.5,1.6l1.9,0.5l1.9-1.9l-0.3-1.6l2.7-0.6l2.9,2.6 l-1.1,1.8l-4.4,1.1l-2.7-0.5l-3.7-1.1l-4.4,1.5l-1.6,0.3L9,575.1z M57.9,570.6l1.6,1.9l2.1-1.6l-1.5-1.3L57.9,570.6z M60.8,573.6l1.1-2.3l2.1,0.3l-0.8,1.9H60.8z M84.4,571.7l1.5,1.8l1-1.1l-0.8-1.9L84.4,571.7z M93.2,559.2l1.1,5.8l2.9,0.8 l5-2.9l4.4-2.6l-1.6-2.4l0.5-2.4l-2.1,1.3l-2.9-0.8l1.6-1.1l1.9,0.8l3.9-1.8l0.5-1.5l-2.4-0.8l0.8-1.9l-2.7,1.9l-4.7,3.6l-4.8,2.9L93.2,559.2z M135.5,539.4l2.4-1.5l-1-1.8l-1.8,1L135.5,539.4z" />
      <path id="HI" fill="#D3D3D3" d="M233.1,519.3l1.9-3.6l2.3-0.3l0.3,0.8l-2.1,3.1H233.1z M243.3,515.6l6.1,2.6l2.1-0.3l1.6-3.9 l-0.6-3.4l-4.2-0.5l-4,1.8L243.3,515.6z M274,525.6l3.7,5.5l2.4-0.3l1.1-0.5l1.5,1.3l3.7-0.2l1-1.5l-2.9-1.8l-1.9-3.7l-2.1-3.6 l-5.8,2.9L274,525.6z M294.2,534.5l1.3-1.9l4.7,1l0.6-0.5l6.1,0.6l-0.3,1.3l-2.6,1.5l-4.4-0.3L294.2,534.5z M299.5,539.7l1.9,3.9 l3.1-1.1l0.3-1.6l-1.6-2.1l-3.7-0.3V539.7z M306.5,538.5l2.3-2.9l4.7,2.4l4.4,1.1l4.4,2.7v1.9l-3.6,1.8l-4.8,1l-2.4-1.5 L306.5,538.5z M323.1,554.1l1.6-1.3l3.4,1.6l7.6,3.6l3.4,2.1l1.6,2.4l1.9,4.4l4,2.6l-0.3,1.3l-3.9,3.2l-4.2,1.5l-1.5-0.6l-3.1,1.8 l-2.4,3.2l-2.3,2.9l-1.8-0.2l-3.6-2.6l-0.3-4.5l0.6-2.4l-1.6-5.7l-2.1-1.8l-0.2-2.6l2.3-1l2.1-3.1l0.5-1l-1.6-1.8L323.1,554.1z" />
      <path id="AL" fill="#D3D3D3" d="M625.6,466.8l-1.6-15.2l-2.7-18.8l0.2-14.1l0.8-31l-0.2-16.7l0.2-6.4l7.8-0.4l27.8-2.6l8.9-0.7 l-0.1,2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1l1.6,4.8l1.5,7l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1l-1.8,4.8l-0.5,3.2 l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8l1,2.5h-6.3l-6.8,0.6l-25.5,2.9l-10.4,1.4l-0.1,3.8l1.8,1.8l2.6,1.9l0.6,7.9 l-5.5,2.6l-2.7-0.3l2.7-1.9v-1l-3.1-6l-2.3-0.6l-1.5,4.4l-1.3,2.7l-0.6-0.2H625.6z" />
      <path id="AR" fill="#D3D3D3" d="M591,345l-3.8,0.9l-6.2-0.5l0.7-3l3.2-2.7l0.5-2.3l-1.8-3l-11,0.5l-20.8,0.9l-23.3,0.7L505,337 l1.6,6.9v8.2l1.4,11l0.2,37.8l2.3,1.9l3-1.4l2.7,1.1l0.4,10.3l22.9-0.1l18.9-0.8l10.1-0.2l1.1-2.1l-0.3-3.5l-1.8-3l1.6-1.5 l-1.6-2.5l0.7-2.5l1.4-5.6l2.5-2.1l-0.7-2.3l3.7-5.4l2.7-1.4l-0.1-1.5l-0.3-1.8l2.9-5.6l2.4-1.3l0.4-3.4l1.8-1.2l0.9-4.2l-1.3-4 l4-2.4l0.6-2l1.2-4.3L591,345z" />
      <path id="AZ" fill="#D3D3D3" d="M137.7,387.5l-2.6,2.2l-0.3,1.5l0.5,1l18.9,10.7l12.1,7.6l14.7,8.6l16.8,10l12.3,2.4l25.1,2.7 l2.5-12.5l3.8-27.2l7-52.9l4.3-31l-24.6-3.7l-27.2-4.6l-33.4-6.3l-2.9,18.1l-0.5,0.5l-1.7,2.6l-2.5-0.1l-1.3-2.7l-2.7-0.3l-0.9-1.1 h-0.9l-0.9,0.6l-1.9,1l-0.1,7l-0.2,1.7l-0.6,12.6l-1.5,2.2l-0.6,3.3l2.7,4.9l1.3,5.8l0.8,1l1,0.6l-0.1,2.3l-1.6,1.4l-3.4,1.7 l-1.9,1.9l-1.5,3.7l-0.6,4.9l-2.9,2.7l-2.1,0.7l-0.1,5.8l-0.5,1.7l0.5,0.8l3.7,0.6l-0.6,2.7l-1.5,2.2L137.7,387.5z" />
      <path id="CA" fill="#D3D3D3" d="M136.9,386.8l3.8-0.5l1.5-2l0.7-1.9l-3.2-0.1l-1.1-1.8l0.8-1.7l0-6.2l2.2-1.3l2.7-2.6l0.4-4.9 l1.6-3.5l1.9-2.1l3.3-1.7l1.3-0.7l0.8-1.5l-0.9-0.9l-1-1.5l-0.9-5.3l-2.9-5.2l0.1-2.8l-2.2-3.2l-15-23.2l-19.4-28.7l-22.4-33 l-12.7-19.5l1.8-7.2l6.8-25.9l8.1-31.4l-12.4-3.3l-13.5-3.4l-12.6-4.1l-7.5-2.1l-11.4-3l-7.1-2.4l-1.6,4.7l-0.2,7.4l-5.2,11.8 l-3.1,2.6l-0.3,1.1l-1.8,0.8l-1.5,4.2l-0.8,3.2l2.7,4.2l1.6,4.2l1.1,3.6l-0.3,6.5l-1.8,3.1l-0.6,5.8l-1,3.7l1.8,3.9l2.7,4.5 l2.3,4.8l1.3,4l-0.3,3.2l-0.3,0.5v2.1l5.7,6.3l-0.5,2.4l-0.6,2.3l-0.6,1.9l0.2,8.2l2.1,3.7l1.9,2.6l2.7,0.5l1,2.7l-1.1,3.6 l-2.1,1.6h-1.1l-0.8,3.9l0.5,2.9l3.2,4.4l1.6,5.3l1.5,4.7l1.3,3.1l3.4,5.8l1.5,2.6l0.5,2.9l1.6,1v2.4l-0.8,1.9l-1.8,7.1l-0.5,1.9 l2.4,2.7l4.2,0.5l4.5,1.8l3.9,2.1h2.9l2.9,3.1l2.6,4.8l1.1,2.3l3.9,2.1l4.8,0.8l1.5,2.1l0.6,3.2l-1.5,0.6l0.3,1l3.2,0.8l2.7,0.2 l2.9,4.7l3.9,4.2l0.8,2.3l2.6,4.2l0.3,3.2v9.4l0.5,1.8l10,1.5l19.7,2.7L136.9,386.8z M48.8,337l1.3,1.5l-0.2,1.3l-3.2-0.1 l-0.6-1.2l-0.6-1.5L48.8,337z M50.7,337l1.2-0.6l3.6,2.1l3.1,1.2l-0.9,0.6l-4.5-0.2l-1.6-1.6L50.7,337z M71.4,356.8l1.8,2.3 l0.8,1l1.5,0.6l0.6-1.5l-1-1.8l-2.7-2l-1.1,0.2V356.8z M70,365.5l1.8,3.2l1.2,1.9l-1.5,0.2l-1.3-1.2c0,0-0.7-1.5-0.7-1.9 s0-2.2,0-2.2L70,365.5z" />
      <path id="CO" fill="#D3D3D3" d="M378.6,256.8l1.4-21.3l-32.1-3.1l-24.5-2.7l-37.3-4.1l-20.7-2.5l-2.6,22.2l-3.2,22.4l-3.8,28 l-1.5,11.1l-0.3,2.8l33.9,3.8l37.7,4.3l32,3.2l16.6,0.8" />
      <path id="CT" fill="#D3D3D3" d="M873.2,180.1l-0.6-4.2l-0.8-4.4l-1.6-6l-4.2,0.9l-21.8,4.8l0.6,3.3l1.5,7.3v8.1l-1.1,2.3l1.8,2.1 l5-3.4l3.6-3.2l1.9-2.1l0.8,0.6l2.7-1.5l5.2-1.1L873.2,180.1z" />
      <path id="DE" fill="#D3D3D3" d="M822.4,230.4l0.6-2.1l0-1.2l-1.3-0.1l-2.1,1.6l-1.5,1.5l1.5,4.2l2.3,5.7l2.1,9.7l1.6,6.3l5-0.2 l6.1-1.2l-2.3-7.4l-1,0.5l-3.6-2.4l-1.8-4.7l-1.9-3.6l-2.3-1l-2.1-3.6L822.4,230.4z" />
      <path id="FL" fill="#D3D3D3" d="M755.4,445.5l2.3,7.3l3.7,9.7l5.3,9.4l3.7,6.3l4.8,5.5l4,3.7l1.6,2.9l-1.1,1.3L779,493l2.9,7.4 l2.9,2.9l2.6,5.3l3.6,5.8l4.5,8.2l1.3,7.6l0.5,12l0.6,1.8l-0.3,3.4l-2.4,1.3l0.3,1.9l-0.6,1.9l0.3,2.4l0.5,1.9l-2.7,3.2l-3.1,1.5 l-3.9,0.2l-1.5,1.6l-2.4,1l-1.3-0.5l-1.1-1l-0.3-2.9l-0.8-3.4l-3.4-5.2l-3.6-2.3l-3.9-0.3l-0.8,1.3l-3.1-4.4l-0.6-3.6l-2.6-4 l-1.8-1.1l-1.6,2.1l-1.8-0.3l-2.1-5l-2.9-3.9l-2.9-5.3l-2.6-3.1l-3.6-3.7l2.1-2.4l3.2-5.5l-0.2-1.6l-4.5-1l-1.6,0.6l0.3,0.6l2.6,1 l-1.5,4.5l-0.8,0.5l-1.8-4l-1.3-4.8l-0.3-2.7l1.5-4.7v-9.5L736,485l-1.3-3.1l-5.2-1.3l-1.9-0.6l-1.6-2.6l-3.4-1.6l-1.1-3.4l-2.7-1 l-2.4-3.7l-4.2-1.5l-2.9-1.5h-2.6l-4,0.8l-0.2,1.9l0.8,1l-0.5,1.1l-3.1-0.2l-3.7,3.6l-3.6,1.9h-3.9l-3.2,1.3l-0.3-2.7l-1.6-1.9 l-2.9-1.1l-1.6-1.5l-8.1-3.9l-7.6-1.8l-4.4,0.6l-6,0.5l-6,2.1l-3.5,0.6l-0.2-8l-2.6-1.9l-1.8-1.8l0.3-3.1l10.2-1.3l25.5-2.9 l6.8-0.6l5.4,0.3l2.6,3.9l1.5,1.5l8.1,0.5l10.8-0.6l21.5-1.3l5.4-0.7l4.6,0l0.2,2.9l3.8,0.8l0.3-4.8l-1.6-4.5l1-0.7l5.1,0.5 L755.4,445.5z M767.9,577.9l2.4-0.6l1.3-0.2l1.5-2.3l2.3-1.6l1.3,0.5l1.7,0.3l0.4,1.1l-3.5,1.2l-4.2,1.5l-2.3,1.2L767.9,577.9z M781.4,572.9l1.2,1.1l2.7-2.1l5.3-4.2l3.7-3.9l2.5-6.6l1-1.7l0.2-3.4l-0.7,0.5l-1,2.8l-1.5,4.6l-3.2,5.3l-4.4,4.2l-3.4,1.9 L781.4,572.9z" />
      <path id="GA" fill="#D3D3D3" d="M689.6,358l-4.8,0.8l-8.4,1.1l-8.6,0.9v2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1l1.6,4.8l1.5,7 l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1l-1.8,4.8l-0.5,3.2l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8l0.6,3.4l2.6,3.9 l1.5,1.5l7.9,0.2l10.8-0.6l21.5-1.3l5.4-0.7l4.6,0l0.2,2.9l2.6,0.8l0.3-4.4l-1.6-4.5l1.1-1.6l5.8,0.8l5,0.3l-0.8-6.3l2.3-10 l1.5-4.2l-0.5-2.6l3.3-6.2l-0.5-1.4l-1.9,0.7l-2.6-1.3l-0.6-2.1l-1.3-3.6l-2.3-2.1l-2.6-0.6l-1.6-4.8l-2.9-6.3l-4.2-1.9l-2.1-1.9 l-1.3-2.6l-2.1-1.9l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5l-3.4-4.9l-3.4,0.2l-4.1-3l-1.3-1.3l-0.3-1.8 l0.8-1.9l2.4-1.2l-1.1-1.2l0.1-0.3l-5.8,1l-7,0.8L689.6,358z" />
      <path id="IA" fill="#D3D3D3" d="M566.6,201.6l0.2,1.9l2.3,1.1l1.1,1.3l0.3,1.3l3.9,3.2l0.7,2.2l-0.8,2.9l-1.5,3.5l-0.8,2.7 l-2.2,1.6l-1.7,0.6l-5.5,1.5l-0.7,2.3l-0.8,2.3l0.6,1.4l1.7,1.7l0,3.7l-2.2,1.6l-0.5,1.5v2.5l-1.5,0.5l-1.7,1.4l-0.5,1.5l0.5,1.7 l-1.4,1.2l-2.3-2.9l-1.5-2.6l-8.3,0.8l-10.2,0.6l-25,0.7l-13,0.2l-9.4,0.2l-1.3,0.1l-1.7-4.5l-0.2-6.6l-1.6-4.1l-0.7-5.3l-2.3-3.7 l-0.9-4.8l-2.7-7.5l-1.1-5.4l-1.4-2.2l-1.6-2.7l1.8-4.3l1.4-5.7l-2.7-2.1l-0.5-2.7l0.9-2.5h1.7h11.5l49.6-0.7l19.9-0.7l1.9,2.7 l1.8,2.6l0.5,0.8l-1.8,2.7l0.5,4.2l2.5,3.9l3,1.8l2.4,0.2L566.6,201.6z" />
      <path id="ID" fill="#D3D3D3" d="M162.1,181c-22.6-4.3-14.1-2.8-21.1-4.4l4.4-17.5l4.3-17.7l1.4-4.2l2.5-5.9l-1.3-2.3l-2.5,0.1 l-0.8-1l0.5-1.1l0.3-3.1l4.5-5.5l1.8-0.5l1.1-1.1l0.6-3.2l0.9-0.7l3.9-5.8l3.9-4.3l0.2-3.8l-3.4-2.6l-1.3-4.4l0.4-9.7l3.7-16.5 l4.5-20.8l3.8-13.5l0.8-3.8l13,2.5l-4.2,21.5l2.9,7.7l-1.1,4.6l2,4.6l3.2,1.7l4.5,9.8l2.7,3.8l0.6,1.1l3.4,1.1l0.5,2.5l-6.9,16.8 l0.3,3.3l2.7,2.9l1.9,0.5l4.8-3.6l0.4-0.5l0.2,0.8l0.3,4.1l2.6,12.9l3.5,2.7l0.4,0.8l2.1,2.4l-0.8,2.8l0.7,3.8l1.9,0.9l2.1-1.6 l2.6-0.5l3.4,1.6l2.5-0.6l3.8-0.2l4,1.6l2.7-0.3l0.9-2.3l2.5-1.6l0.7,1.7l0.6,2.2l2.3,2.5l-3.8,24l-5.1,29l-4.2-0.3l-8.2-1.5 l-9.8-1.8l-12.2-2.4l-12.5-2.5l-8.5-1.8l-9.3-1.7L162.1,181z" />
      <path id="IL" fill="#D3D3D3" d="M617.8,301.5v-3.6l0.3-4.9l2.4-3.1l1.8-3.8l2.6-3.9l-0.4-5.3l-2-3.5l-0.1-3.3l0.7-5.3l-0.8-7.2 l-1.1-15.8l-1.3-15l-0.9-11.6l-0.3-0.9l-0.8-2.6l-1.3-3.7l-1.6-1.8l-1.5-2.6l-0.2-5.5l-9.9,1.3l-27.2,1.7l-8.7-0.4l0.2,2.4l2.3,0.7 l0.9,1.1l0.5,1.8l3.9,3.4l0.7,2.3l-0.7,3.4l-1.8,3.7l-0.7,2.5l-2.3,1.8l-1.8,0.7l-5.3,1.4l-0.7,1.8L562,230l0.7,1.4l1.8,1.6 l-0.2,4.1l-1.8,1.6l-0.7,1.6v2.7l-1.8,0.5l-1.6,1.1l-0.2,1.4l0.2,2.1l-1.7,1.3l-1,2.8l0.5,3.7l2.3,7.3l7.3,7.5l5.5,3.7l-0.2,4.3 l0.9,1.4l6.4,0.5l2.7,1.4l-0.7,3.7l-2.3,5.9l-0.7,3.2l2.3,3.9l6.4,5.3l4.6,0.7l2.1,5l2.1,3.2l-0.9,3l1.6,4.1l1.8,2.1l1.9-0.8 l0.7-2.2l2-1.4l3.2-1.1l3.1,1.2l2.9,1.1l0.8-0.2l-0.1-1.2l-1.1-2.8l0.4-2.4l2.3-1.6l2.4-1l1.2-0.4l-0.6-1.3l-0.8-2.2l1.2-1.3 L617.8,301.5z" />
      <path id="IN" fill="#D3D3D3" d="M618.4,300.9l0.1-2.9l0.5-4.5l2.3-2.9l1.8-3.9l2.6-4.2l-0.5-5.8l-1.8-2.7l-0.3-3.2l0.8-5.5l-0.5-7 l-1.3-16l-1.3-15.4l-1-11.7l3.1,0.9l1.5,1l1.1-0.3l2.1-1.9l2.8-1.6l5.1-0.2l22-2.3l5.6-0.5l1.5,16l4.3,36.8l0.6,5.8L669,271 l1.2,1.8l0.1,1.4l-2.5,1.6l-3.5,1.6l-3.2,0.6l-0.6,4.9l-4.6,3.3l-2.8,4l0.3,2.4l-0.6,1.5h-3.3l-1.6-1.6l-2.5,1.3l-2.7,1.5l0.2,3.1 l-1.2,0.3l-0.5-1l-2.2-1.5l-3.3,1.3l-1.6,3l-1.4-0.8l-1.5-1.6l-4.5,0.5l-5.6,1L618.4,300.9z" />
      <path id="KS" fill="#D3D3D3" d="M503.4,325.1l-12.6,0.2l-46.1-0.5l-44.6-2.1l-24.6-1.3l4.1-64.7l21.8,0.8l40.5,1.4l44.1,0.5h5.1 l3.2,3.2l2.8,0.2l0.9,1.1v2l-1.8,1.6l-0.5,2.6l2.2,3.6l2.5,3.1l2.5,2l1.1,11.2L503.4,325.1z" />
      <path id="KY" fill="#D3D3D3" d="M721.8,297.8l-2.3,2.7l-4.2,3.6L711,310l-1.8,1.8v2.1l-3.9,2.1l-5.7,3.4l-3.5,0.4l-51.9,4.9 l-15.8,1.8l-4.6,0.5l-3.9,0l-0.2,4.2l-8.2,0.1l-7,0.6l-10.4,0.2l1.9-0.2l2.2-1.8l2.1-1.1l0.2-3.2l0.9-1.8l-1.6-2.5l0.8-1.9l2.3-1.8 l2.1-0.6l2.7,1.3l3.6,1.3l1.1-0.3l0.2-2.3l-1.3-2.4l0.3-2.3l1.9-1.5l2.6-0.6l1.6-0.6l-0.8-1.8l-0.6-1.9l1.1-0.8l1.1-3.3l3-1.7 l5.8-1l3.6-0.5l1.5,1.9l1.8,0.8l1.8-3.2l2.9-1.5l1.9,1.6l0.8,1.1l2.1-0.5l-0.2-3.4l2.9-1.6l1.1-0.8l1.1,1.6h4.7l0.8-2.1l-0.3-2.3 l2.9-3.6l4.7-3.9l0.5-4.5l2.7-0.3l3.9-1.8l2.7-1.9l-0.3-1.9l-1.5-1.5l0.6-2.2l4.1-0.2l2.4-0.8l2.9,1.6l1.6,4.4l5.8,0.3l1.8,1.8 l2.1,0.2l2.4-1.5l3.1,0.5l1.3,1.5l2.7-2.6l1.8-1.3h1.6l0.6,2.7l1.8,1l2.4,2.2l0.2,5.5l0.8,1.6l2.6,1.5l0.6,2.3l2.9,3.7l2.6,2.7 L721.8,297.8z" />
      <path id="LA" fill="#D3D3D3" d="M602.2,473l-1-2.6l-1.1-3.1l-3.3-3.5l0.9-6.8l-0.1-1.1l-1.3,0.3l-8.2,0.9l-25,0.5l-0.7-2.4l0.9-8.5 l3.3-5.9l5-8.7l-0.6-2.4l1.3-0.7l0.5-2l-2.3-2.1l-0.1-1.9l-1.8-4.3l-0.5-5.9l-9.7,0.1l-19.2,0.9l-22.2,0l0,9.6l0.7,9.4l0.7,3.9 l2.5,4.1l0.9,5l4.3,5.5l0.2,3.2l0.7,0.7l-0.7,8.5l-3,5l1.6,2.1l-0.7,2.5l-0.7,7.3l-1.4,3.2l0.1,3.6l4.7-1.5l8.1-0.3l10.3,3.6 l6.5,1.1l3.7-1.5l3.2,1.1l3.2,1l0.8-2.1l-3.2-1.1l-2.6,0.5l-2.7-1.6c0,0,0.2-1.3,0.8-1.5c0.6-0.2,3.1-1,3.1-1l1.8,1.5l1.8-1 l3.2,0.6l1.5,2.4l0.3,2.3l4.5,0.3l1.8,1.8l-0.8,1.6l-1.3,0.8l1.6,1.6l8.4,3.6l3.6-1.3l1-2.4l2.6-0.6l1.8-1.5l1.3,1l0.8,2.9 l-2.3,0.8l0.6,0.6l3.4-1.3l2.3-3.4l0.8-0.5l-2.1-0.3l0.8-1.6l-0.2-1.5l2.1-0.5l1.1-1.3l0.6,0.8c0,0-0.2,3.1,0.6,3.1 c0.8,0,4.2,0.6,4.2,0.6l4,1.9l1,1.5h2.9l1.1,1l2.3-3.1v-1.5h-1.3l-3.4-2.7l-5.8-0.8l-3.2-2.3l1.1-2.7l2.3,0.3l0.2-0.6l-1.8-1v-0.5 h3.2l1.8-3.1l-1.3-1.9l-0.3-2.7l-1.5,0.2l-1.9,2.1l-0.6,2.6l-3.1-0.6l-1-1.8l1.8-1.9l2-1.8L602.2,473z" />
      <path id="MA" fill="#D3D3D3" d="M900,173.9l2.2-0.7l0.5-1.7l1,0.1l1,2.3l-1.3,0.5l-3.9,0.1L900,173.9z M890.6,174.7l2.3-2.6h1.6 l1.8,1.5l-2.4,1l-2.2,1L890.6,174.7z M855.8,152.7l17.5-4.2l2.3-0.6l2.1-3.2l3.7-1.7l2.9,4.4l-2.4,5.2l-0.3,1.5l1.9,2.6l1.1-0.8 h1.8l2.3,2.6l3.9,6l3.6,0.5l2.3-1l1.8-1.8l-0.8-2.7l-2.1-1.6l-1.5,0.8l-1-1.3l0.5-0.5l2.1-0.2l1.8,0.8l1.9,2.4l1,2.9l0.3,2.4 l-4.2,1.5l-3.9,1.9l-3.9,4.5l-1.9,1.5v-1l2.4-1.5l0.5-1.8l-0.8-3.1l-2.9,1.5l-0.8,1.5l0.5,2.3l-2.1,1l-2.7-4.5l-3.4-4.4l-2.1-1.8 l-6.5,1.9l-5.1,1.1l-21.8,4.8l-0.4-4.9l0.6-10.6l5.2-0.9L855.8,152.7z" />
      <path id="MD" fill="#D3D3D3" d="M837,255.3l-6.1,1.3l-5.8,0.2l-1.8-7.1l-2.1-9.7l-2.3-5.7l-1.3-4.4l-7.5,1.6l-14.9,2.8l-37.5,7.6 l1.1,5l1,5.7l0.3-0.3l2.1-2.4l2.3-2.6l2.4-0.6l1.5-1.5l1.8-2.6l1.3,0.6l2.9-0.3l2.6-2.1l2-1.5l1.8-0.5l1.6,1.1l2.9,1.5l1.9,1.8 l1.2,1.5l4.1,1.7v2.9l5.5,1.3l1.1,0.5l1.4-2l2.9,2l-1.3,2.5l-0.8,4l-1.8,2.6v2.1l0.6,1.8l5.1,1.4l4.3-0.1l3.1,1l2.1,0.3l1-2.1 l-1.5-2.1v-1.8l-2.4-2.1l-2.1-5.5l1.3-5.3l-0.2-2.1l-1.3-1.3c0,0,1.5-1.6,1.5-2.3c0-0.6,0.5-2.1,0.5-2.1l1.9-1.3l1.9-1.6l0.5,1 l-1.5,1.6l-1.3,3.7l0.3,1.1l1.8,0.3l0.5,5.5l-2.1,1l0.3,3.6l0.5-0.2l1.1-1.9l1.6,1.8l-1.6,1.3l-0.3,3.4l2.6,3.4l3.9,0.5l1.6-0.8 l3.2,4.2l1.4,0.5l6.7-2.8l2-4L837,255.3z M820.3,264.3l1.1,2.5l0.2,1.8l1.1,1.9c0,0,0.9-0.9,0.9-1.2c0-0.3-0.7-3.1-0.7-3.1 l-0.7-2.3L820.3,264.3z" />
      <path id="ME" fill="#D3D3D3" d="M923.2,77.3l1.9,2.1l2.3,3.7v1.9l-2.1,4.7l-1.9,0.6l-3.4,3.1l-4.8,5.5c0,0-0.6,0-1.3,0 c-0.6,0-1-2.1-1-2.1l-1.8,0.2l-1,1.5l-2.4,1.5l-1,1.5l1.6,1.5l-0.5,0.6l-0.5,2.7l-1.9-0.2v-1.6l-0.3-1.3l-1.5,0.3l-1.8-3.2 l-2.1,1.3l1.3,1.5l0.3,1.1l-0.8,1.3l0.3,3.1l0.2,1.6l-1.6,2.6l-2.9,0.5l-0.3,2.9l-5.3,3.1l-1.3,0.5l-1.6-1.5l-3.1,3.6l1,3.2 l-1.5,1.3l-0.2,4.4l-1.1,6.3l-2.5-1.2l-0.5-3.1l-3.9-1.1l-0.3-2.7l-7.3-23.4l-4.2-13.6l1.4-0.1l1.5,0.4v-2.6l0.8-5.5l2.6-4.7l1.5-4 l-1.9-2.4v-6l0.8-1l0.8-2.7l-0.2-1.5l-0.2-4.8l1.8-4.8l2.9-8.9l2.1-4.2h1.3l1.3,0.2v1.1l1.3,2.3l2.7,0.6l0.8-0.8v-1l4-2.9l1.8-1.8 l1.5,0.2l6,2.4l1.9,1l9.1,29.9h6l0.8,1.9l0.2,4.8l2.9,2.3h0.8l0.2-0.5l-0.5-1.1L923.2,77.3z M902.3,107.5l1.5-1.5l1.4,1.1 l0.6,2.4l-1.7,0.9L902.3,107.5z M909,101.6l1.8,1.9c0,0,1.3,0.1,1.3-0.2s0.2-2,0.2-2l0.9-0.8l-0.8-1.8l-2,0.7L909,101.6z" />
      <path id="MI" fill="#D3D3D3" d="M581.6,82.1l1.8-2.1l2.2-0.8l5.4-3.9l2.3-0.6l0.5,0.5l-5.1,5.1l-3.3,1.9l-2.1,0.9L581.6,82.1z M667.8,114.2l0.6,2.5l3.2,0.2l1.3-1.2c0,0-0.1-1.5-0.4-1.6c-0.3-0.2-1.6-1.9-1.6-1.9l-2.2,0.2l-1.6,0.2l-0.3,1.1L667.8,114.2z M697.9,177.2l-3.2-8.2l-2.3-9.1l-2.4-3.2l-2.6-1.8l-1.6,1.1l-3.9,1.8l-1.9,5l-2.7,3.7l-1.1,0.6l-1.5-0.6c0,0-2.6-1.5-2.4-2.1 c0.2-0.6,0.5-5,0.5-5l3.4-1.3l0.8-3.4l0.6-2.6l2.4-1.6l-0.3-10l-1.6-2.3l-1.3-0.8l-0.8-2.1l0.8-0.8l1.6,0.3l0.2-1.6L676,131 l-1.3-2.6h-2.6l-4.5-1.5l-5.5-3.4h-2.7l-0.6,0.6l-1-0.5l-3.1-2.3l-2.9,1.8l-2.9,2.3l0.3,3.6l1,0.3l2.1,0.5l0.5,0.8l-2.6,0.8 l-2.6,0.3l-1.5,1.8l-0.3,2.1l0.3,1.6l0.3,5.5l-3.6,2.1l-0.6-0.2v-4.2l1.3-2.4l0.6-2.4l-0.8-0.8l-1.9,0.8l-1,4.2l-2.7,1.1l-1.8,1.9 l-0.2,1l0.6,0.8l-0.6,2.6l-2.3,0.5v1.1l0.8,2.4l-1.1,6.1l-1.6,4l0.6,4.7l0.5,1.1l-0.8,2.4l-0.3,0.8l-0.3,2.7l3.6,6l2.9,6.5l1.5,4.8 l-0.8,4.7l-1,6l-2.4,5.2l-0.3,2.7l-3.3,3.1l4.4-0.2l21.4-2.3l7.3-1l0.1,1.7l6.9-1.2l10.3-1.5l3.9-0.5l0.1-0.6l0.2-1.5l2.1-3.7 l2-1.7l-0.2-5.1l1.6-1.6l1.1-0.3l0.2-3.6l1.5-3l1.1,0.6l0.2,0.6l0.8,0.2l1.9-1L697.9,177.2z M567.5,111.2l0.7-0.6l2.7-0.8l3.6-2.3 v-1l0.6-0.6l6-1l2.4-1.9l4.4-2.1l0.2-1.3l1.9-2.9l1.8-0.8l1.3-1.8l2.3-2.3l4.4-2.4l4.7-0.5l1.1,1.1l-0.3,1l-3.7,1l-1.5,3.1 l-2.3,0.8l-0.5,2.4l-2.4,3.2l-0.3,2.6l0.8,0.5l1-1.1l3.6-2.9l1.3,1.3h2.3l3.2,1l1.5,1.1l1.5,3.1l2.7,2.7l3.9-0.2l1.5-1l1.6,1.3 l1.6,0.5l1.3-0.8h1.1l1.6-1l4-3.6l3.4-1.1l6.6-0.3l4.5-1.9l2.6-1.3l1.5,0.2v5.7l0.5,0.3l2.9,0.8l1.9-0.5l6.1-1.6l1.1-1.1l1.5,0.5v7 l3.2,3.1l1.3,0.6l1.3,1l-1.3,0.3l-0.8-0.3l-3.7-0.5l-2.1,0.6l-2.3-0.2l-3.2,1.5h-1.8l-5.8-1.3l-5.2,0.2l-1.9,2.6l-7,0.6l-2.4,0.8 l-1.1,3.1l-1.3,1.1l-0.5-0.2l-1.5-1.6l-4.5,2.4h-0.6l-1.1-1.6l-0.8,0.2l-1.9,4.4l-1,4l-3.2,7l-1.2-1l-1.4-1l-1.9-10.3l-3.5-1.4 l-2.1-2.3l-12.1-2.7l-2.9-1l-8.2-2.2l-7.9-1.1L567.5,111.2z" />
      <path id="MN" fill="#D3D3D3" d="M471.9,128.5l-0.5-8.5l-1.8-7.3l-1.8-13.5l-0.5-9.8l-1.8-3.4l-1.6-5v-10.3l0.7-3.9l-1.8-5.5l30.1,0 l0.3-8.2l0.6-0.2l2.3,0.5l1.9,0.8l0.8,5.5l1.5,6.1l1.6,1.6h4.8l0.3,1.5l6.3,0.3v2.1h4.8l0.3-1.3l1.1-1.1l2.3-0.6l1.3,1h2.9l3.9,2.6 l5.3,2.4l2.4,0.5l0.5-1l1.5-0.5l0.5,2.9l2.6,1.3l0.5-0.5l1.3,0.2v2.1l2.6,1h3.1l1.6-0.8l3.2-3.2l2.6-0.5l0.8,1.8l0.5,1.3h1l1-0.8 l8.9-0.3l1.8,3.1h0.6l0.7-1.1l4.4-0.4l-0.6,2.3l-3.9,1.8l-9.2,4.1l-4.8,2l-3.1,2.6l-2.4,3.6l-2.3,3.9l-1.8,0.8l-4.5,5l-1.3,0.2 l-3.8,2.9l-2.8,3.2l-0.2,3l0.2,7.8l-1.6,1.6L530,128l-1.8,5.7l2.5,3.6l0.5,2.5l-1.1,3l-0.2,3.7l0.5,7.1l3.4,4.1h3l2.5,2.3l3.2,1.4 l3.7,5l7.1,5l1.8,2.1l0.2,5.7l-20.6,0.7l-60.2,0.5l-0.3-35.7l-0.5-3l-4.1-3.4l-1.1-1.8v-1.6l2.1-1.6l1.4-1.4L471.9,128.5z" />
      <path id="MO" fill="#D3D3D3" d="M555.8,249.5l-2.5-3.1l-1.1-2.3l-7.8,0.7l-9.8,0.5l-25.4,0.9l-13.5,0.2l-7.9,0.1l-2.3,0.1l1.3,2.5 l-0.2,2.3l2.5,3.9l3.1,4.1l3.1,2.7l2.3,0.2l1.4,0.9v3l-1.8,1.6l-0.5,2.3l2.1,3.4l2.5,3l2.5,1.8l1.4,11.7l-0.7,35.3l0.2,4.7l0.5,5.4 l23.4-0.1l23.2-0.7l20.8-0.8l11.7-0.2l2.2,3.4l-0.7,3.3l-3.1,2.4l-0.6,1.8l5.4,0.5l3.9-0.7l1.7-5.5l0.7-5.9l2.3-2l1.7-1.5l2.1-1 l0.1-2.9l0.6-1.7l-1-1.7l-2.7,0.1l-2.2-2.6l-1.4-4.2l0.8-2.5l-1.9-3.4l-1.8-4.6l-4.8-0.8l-7-5.6l-1.7-4.1l0.8-3.2l2.1-6.1l0.5-2.9 l-1.9-1l-6.9-0.8l-1-1.7l-0.1-4.2l-5.5-3.4l-7-7.8l-2.3-7.3l-0.2-4.2L555.8,249.5z" />
      <path id="MS" fill="#D3D3D3" d="M624.6,467l-0.3,1.3h-5.2l-1.5-0.8l-2.1-0.3l-6.8,1.9l-1.8-0.8l-2.6,4.2l-1.1,0.8l-1.1-2.5 l-1.1-3.9l-3.4-3.2l1.1-7.5l-0.7-0.9l-1.8,0.2l-8.2,0.7l-24.2,0.7l-0.5-1.6l0.7-8l3.4-6.2l5.3-9.1l-0.9-2.1h1.1l0.7-3.2l-2.3-1.8 l0.2-1.8l-2.1-4.6l-0.3-5.3l1.4-2.7l-0.4-4.3l-1.4-3l1.4-1.4l-1.4-2.1l0.5-1.8l0.9-6.2l3-2.7l-0.7-2.1l3.7-5.3l2.7-0.9v-2.5 l-0.7-1.4l2.7-5.3l2.7-1.1l0.1-3.4l8.7-0.1l24.1-1.9l4.6-0.2l0,6.4l0.2,16.7l-0.8,31l-0.2,14.1l2.7,18.8L624.6,467z" />
      <path id="MT" fill="#D3D3D3" d="M356.7,122.3l0.6-11.2l2.3-24.8c0.5-5,1.1-8.5,1.4-15.4l0.9-14.6l-30.7-2.8L302,50l-29.3-4 l-32.3-5.3l-18.4-3.4l-32.7-6.9l-4.5,21.3l3.4,7.5l-1.4,4.6l1.8,4.6l3.2,1.4l4.6,10.8l2.7,3.2l0.5,1.1l3.4,1.1l0.5,2.1l-7.1,17.6 v2.5l2.5,3.2h0.9l4.8-3l0.7-1.1l1.6,0.7l-0.2,5.3l2.7,12.6l3,2.5l0.9,0.7l1.8,2.3l-0.5,3.4l0.7,3.4l1.1,0.9l2.3-2.3h2.7l3.2,1.6 l2.5-0.9h4.1l3.7,1.6l2.7-0.5l0.5-3l3-0.7l1.4,1.4l0.5,3.2l1.8,1.4l1.5-11.6l20.7,3l28.2,4l16.6,1.9l31.4,3.5l11,1.5l1.1-15.4 L356.7,122.3z" />
      <path id="NC" fill="#D3D3D3" d="M832.1,298.5l1.7,4.7l3.6,6.5l2.4,2.4l0.6,2.3l-2.4,0.2l0.8,0.6l-0.3,4.2l-2.6,1.3l-0.6,2.1 l-1.3,2.9l-3.7,1.6l-2.4-0.3l-1.5-0.2l-1.6-1.3l0.3,1.3v1h1.9l0.8,1.3l-1.9,6.3h4.2l0.6,1.6l2.3-2.3l1.3-0.5l-1.9,3.6l-3.1,4.8 h-1.3l-1.1-0.5l-2.7,0.6l-5.2,2.4l-6.5,5.3l-3.4,4.7l-1.9,6.5l-0.5,2.4l-4.7,0.5l-5.5,1.3l-9.9-8.2l-12.6-7.6l-2.9-0.8l-12.6,1.5 l-4.3,0.8l-1.6-3.2l-3-2.1l-16.5,0.5l-7.3,0.8l-9.1,4.5l-6.1,2.6l-1.6,0.3l-5.8,1l-7,0.8l-6.8,0.5l0.5-4.1l1.8-1.5l2.7-0.6l0.6-3.7 l4.2-2.7l3.9-1.5l4.2-3.6l4.4-2.1l0.6-3.1l3.9-3.9l0.6-0.2c0,0,0,1.1,0.8,1.1c0.8,0,1.9,0.3,1.9,0.3l2.3-3.6l2.1-0.6l2.3,0.3 l1.6-3.6l2.9-2.6l0.5-2.1v-4l4.5,0.7l7.1-1.3l15.8-1.9l17.1-2.6l19.9-4l19.7-4.2l11.4-2.8L832.1,298.5z M836,331.5l2.6-2.5 l3.2-2.6l1.5-0.6l0.2-2l-0.6-6.1l-1.5-2.3l-0.6-1.9l0.7-0.2l2.7,5.5l0.4,4.4l-0.2,3.4l-3.4,1.5l-2.8,2.4l-1.1,1.2L836,331.5z" />
      <path id="ND" fill="#D3D3D3" d="M471.3,127.7l-0.4-7.5l-2-7.3l-1.8-13.6l-0.5-9.8l-2-3.1l-1.6-5.4v-10.3l0.7-3.9l-2.1-5.5 l-28.4-0.6l-18.6-0.6l-26.5-1.3l-24.9-1.9l-1.3,14.2l-1.4,15.1l-2.3,24.9l-0.5,11l56.8,3.8L471.3,127.7z" />
      <path id="NE" fill="#D3D3D3" d="M484.2,247l1.4,2.7l0.1,2.1l2.4,3.7l2.7,3.2h-5l-43.5-0.9l-40.8-0.9l-21.2-1l1.1-21.3l-33.4-2.7 l4.3-44l15.5,1L388,190l17.8,1.1l23.8,1.1l10.7-0.5l2.1,2.3l4.8,3l1.1,0.9l4.3-1.4l3.9-0.5l2.7-0.2l1.8,1.4l5,1.6l3,1.6l0.5,1.6 l0.9,2.1h1.8l0.8,0l1,5.2l2.7,8l1.2,4.6l2.1,3.8l0.5,4.9l1.4,4.3l0.5,6.5" />
      <path id="NH" fill="#D3D3D3" d="M880.8,142.4l0.9-1.1l1.1-3.3l-2.5-0.9l-0.5-3.1l-3.9-1.1l-0.3-2.7l-7.3-23.4l-4.6-14.5l-0.9,0 l-0.6,1.6l-0.6-0.5l-1-1l-1.5,1.9l0,5l0.3,5.7l1.9,2.7v4l-3.7,5.1l-2.6,1.1v1.1l1.1,1.8v8.6l-0.8,9.2l-0.2,4.8l1,1.3l-0.2,4.5 l-0.5,1.8l1.5,0.9l16.4-4.7l2.3-0.6l1.5-2.6L880.8,142.4z" />
      <path id="NJ" fill="#D3D3D3" d="M828.2,190.3l-2.1,2.4v3.1l-1.9,3.1l-0.2,1.6l1.3,1.3l-0.2,2.4l-2.3,1.1l0.8,2.7l0.2,1.1l2.7,0.3 l1,2.6l3.6,2.4l2.4,1.6v0.8l-3.2,3.1l-1.6,2.3l-1.5,2.7l-2.3,1.3l-1.2,0.7l-0.2,1.2l-0.6,2.6l1.1,2.2l3.2,2.9l4.8,2.3l4,0.6 l0.2,1.5l-0.8,1l0.3,2.7h0.8l2.1-2.4l0.8-4.8l2.7-4l3.1-6.5l1.1-5.5l-0.6-1.1l-0.2-9.4l-1.6-3.4l-1.1,0.8l-2.7,0.3l-0.5-0.5l1.1-1 l2.1-1.9l0.1-1.1l-0.4-3.4l0.5-2.7l-0.2-2.1l-2.6-1.1l-4.5-1l-3.9-1.1L828.2,190.3z" />
      <path id="NM" fill="#D3D3D3" d="M282.7,431l-0.7-6.1l8.6,0.5l29.5,3.1l28.4,1.4l2-22.3l3.7-55.9l1.1-19.4l2,0.3l0-11.1l-32.2-2.4 l-36.9-4.4l-34.5-4.1l-4.2,30.8l-7,53.2l-3.8,26.9l-2,13.3l15.5,2l1.3-10l16.7,2.6L282.7,431z" />
      <path id="NV" fill="#D3D3D3" d="M140.7,177.6l21,4.5l9.7,1.9l9.3,1.8l6.6,1.6l-0.6,5.9l-3.5,17.3l-4.1,20l-1.9,9.7l-2.2,13.3 l-3.2,16.4l-3.5,15.7l-2,10.2l-2.5,16.8l-0.5,1.1l-1.1,2.5l-1.9-0.1l-1.1-2.7l-2.7-0.5l-1.4-1l-2,0.3l-0.9,0.7l-1.3,1.3l-0.4,7 l-0.5,1.7l-0.4,12.1l-1.3,1.7l-1.9-2.3l-14.5-22.7l-19.4-29L89.6,249l-12.4-18.6l1.6-6.6l7-25.9l7.9-31.3l33.6,8.1l13.7,3" />
      <path id="NY" fill="#D3D3D3" d="M828.9,189.3l-1.1-1l-2.6-0.2l-2.3-1.9l-1.6-6.1l-3.5,0.1l-2.4-2.7l-19.4,4.4l-43,8.7l-7.5,1.2 l-0.7-6.5l1.4-1.1l1.3-1.1l1-1.6l1.8-1.1l1.9-1.8l0.5-1.6l2.1-2.7l1.1-1l-0.2-1l-1.3-3.1l-1.8-0.2l-1.9-6.1l2.9-1.8l4.4-1.5l4-1.3 l3.2-0.5l6.3-0.2l1.9,1.3l1.6,0.2l2.1-1.3l2.6-1.1l5.2-0.5l2.1-1.8l1.8-3.2l1.6-1.9h2.1l1.9-1.1l0.2-2.3l-1.5-2.1l-0.3-1.5l1.1-2.1 v-1.5h-1.8l-1.8-0.8l-0.8-1.1l-0.2-2.6l5.8-5.5l0.6-0.8l1.5-2.9l2.9-4.5l2.7-3.7l2.1-2.4l2.4-1.8l3.1-1.2l5.5-1.3l3.2,0.2l4.5-1.5 l7.6-2.1l0.5,5l2.4,6.5l0.8,5.2l-1,3.9l2.6,4.5l0.8,2.1l-0.8,2.9l2.9,1.3l0.6,0.3l3.1,11l-0.5,5.1l-0.5,10.8l0.8,5.5l0.8,3.6 l1.5,7.3v8.1l-1.1,2.3l1.8,2l0.8,1.7l-1.9,1.8l0.3,1.3l1.3-0.3l1.5-1.3l2.3-2.6l1.1-0.6l1.6,0.6l2.3,0.2l7.9-3.9l2.9-2.7l1.3-1.5 l4.2,1.6l-3.4,3.6l-3.9,2.9l-7.1,5.3l-2.6,1l-5.8,1.9l-4,1.1l-1.2-0.5l-0.2-3.7l0.5-2.7l-0.2-2.1l-2.8-1.7l-4.5-1l-3.9-1.1 L828.9,189.3z" />
      <path id="OH" fill="#D3D3D3" d="M731.4,195l-6.1,4.1l-3.9,2.3l-3.4,3.7l-4,3.9l-3.2,0.8l-2.9,0.5l-5.5,2.6l-2.1,0.2l-3.4-3.1 l-5.2,0.6l-2.6-1.5l-2.4-1.4l-4.9,0.7l-10.2,1.6l-7.8,1.2l1.3,14.6l1.8,13.7l2.6,23.4l0.6,4.8l4.1-0.1l2.4-0.8l3.4,1.5l2.1,4.4 l5.1,0l1.9,2.1l1.8-0.1l2.5-1.3l2.5,0.4l2,1.5l1.7-2.1l2.3-1.3l2.1-0.7l0.6,2.7l1.8,1l3.5,2.3l2.2-0.1l1.1-1.1l-0.1-1.4l1.6-1.5 l0.2-5l1-3.9l1.5-1.4l1.5,0.9l0.8,1.2l1.2-0.2l-0.4-2.4l-0.6-0.6v-2.4l1-1.3l2.3-3.4l1.3-1.5l2.1,0.5l2.3-1.6l3.1-3.4l2.3-3.9 l0.2-5.4l0.5-5v-4.7l-1.1-3.1l1-1.5l0.9-1l-1.4-9.8L731.4,195z" />
      <path id="OK" fill="#D3D3D3" d="M375.3,322.6l-10.7-0.5l-6.4-0.5l0.3,0.2l-0.7,10.4l22,1.4l32.1,1.3l-2.3,24.4l-0.5,17.8l0.2,1.6 l4.3,3.7l2.1,1.1l0.7-0.2l0.7-2.1l1.4,1.8h2.1v-1.4l2.7,1.4l-0.5,3.9l4.1,0.2l2.5,1.1l4.1,0.7l2.5,1.8l2.3-2.1l3.4,0.7l2.5,3.4h0.9 v2.3l2.3,0.7l2.3-2.3l1.8,0.7h2.5l0.9,2.5l4.8,1.8l1.4-0.7l1.8-4.1h1.1l1.1,2.1l4.1,0.7l3.7,1.4l3,0.9l1.8-0.9l0.7-2.5h4.3l2.1,0.9 l2.7-2.1h1.1l0.7,1.6h4.1l1.6-2.1l1.8,0.5l2.1,2.5l3.2,1.8l3.2,0.9l1.9,1.1l-0.4-37.2l-1.4-11l-0.2-8.9l-1.4-6.5l-0.8-7.2l-0.1-3.8 l-12.1,0.3l-46.4-0.5l-45-2.1L375.3,322.6z" />
      <path id="OR" fill="#D3D3D3" d="M140.2,176.8l4.3-17.9l4.7-17.9l1.1-4.2l2.4-5.6l-0.6-1.2l-2.5,0l-1.3-1.7l0.5-1.5l0.5-3.2l4.5-5.5 l1.8-1.1l1.1-1.1l1.5-3.6l4-5.7l3.6-3.9l0.2-3.5l-3.3-2.5l-1.2-4.5l-13.2-3.7l-15.1-3.5l-15.4,0.1l-0.5-1.4l-5.5,2.1l-4.5-0.6 l-2.4-1.6l-1.3,0.7L99,84l-1.7-1.3l-5.3-2h-0.8l-4.3-1.4l-1.9,1.8l-6.2-0.3l-5.9-4.1l0.7-0.8l0.2-7.8l-2.3-3.9l-4.1-0.6 l-0.7-2.5l-2.4-0.5l-5.8,2.1l-2.3,6.5l-3.2,10l-3.2,6.5l-5,14.1l-6.5,13.6l-8.1,12.6l-1.9,2.9l-0.8,8.6l-1.3,6l2.7,3.5l6.7,2.3 l11.6,3.3l7.9,2.5l12.4,3.6l13.3,3.6l13.2,3.6" />
      <path id="PA" fill="#D3D3D3" d="M822.2,226.3l1.1-0.6l2.3-0.6l1.5-2.7l1.6-2.3l3.2-3.1v-0.8l-2.4-1.6l-3.6-2.4l-1-2.6l-2.7-0.3 l-0.2-1.1l-0.8-2.7l2.3-1.1l0.2-2.4l-1.3-1.3l0.2-1.6l1.9-3.1v-3.1l2.3-2.4l0.2-1.1l-2.6-0.2l-2.3-1.9l-2.4-5.3l-3-0.9l-2.3-2.1 l-18.6,4l-43,8.7l-8.9,1.5l-0.5-7.1l-5.5,5.6l-1.3,0.5l-4.2,3l2.9,19.1l2.9,9.5l3.6,19.3l3.3-0.6l11.9-1.5l37.9-7.7l14.9-2.8 l8.3-1.6l0.3-0.2l2.1-1.6L822.2,226.3z" />
      <path id="RI" fill="#D3D3D3" d="M874.1,179.8l-0.5-4.2l-0.8-4.4l-1.7-5.9l5.7-1.5l1.6,1.1l3.4,4.4l2.9,4.4l-2.9,1.5l-1.3-0.2 l-1.1,1.8l-2.4,1.9L874.1,179.8z" />
      <path id="SC" fill="#D3D3D3" d="M761.2,412.9l-1.8,1l-2.6-1.3l-0.6-2.1l-1.3-3.6l-2.3-2.1l-2.6-0.6l-1.6-4.8l-2.7-6l-4.2-1.9 l-2.1-1.9l-1.3-2.6L736,385l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5l-3.4-5.2l-3.4,0.2l-4-2.4l-1.3-1.3 l-0.3-1.8l0.8-1.9l2.3-1l-0.3-2.1l6.1-2.6l9.1-4.5l7.3-0.8l16.5-0.5l2.3,1.9l1.6,3.2l4.4-0.5l12.6-1.5l2.9,0.8l12.6,7.6l10.1,8.1 l-5.4,5.5l-2.6,6.1l-0.5,6.3l-1.6,0.8l-1.1,2.7l-2.4,0.6l-2.1,3.6l-2.7,2.7l-2.3,3.4l-1.6,0.8l-3.6,3.4l-2.9,0.2l1,3.2l-5,5.5 L761.2,412.9z" />
      <path id="SD" fill="#D3D3D3" d="M472.8,203.2l-1-1.1l-1.5-3.6l1.8-3.7l1.1-5.6l-2.6-2.1l-0.3-2.7l0.6-3l2.2-0.8l0.3-5.7l-0.1-30.1 l-0.6-3l-4.1-3.6l-1-2v-1.9l1.9-1.3l1.5-1.9l0.2-2.7l-57.4-1.6l-56.2-3.9l-0.8,5.3l-1.6,15.9l-1.3,17.9l-1.6,24.6l16,1l19.6,1.1 l18,1.3l23.8,1.3l10.7-0.8l2.9,2.3l4.3,3l1,0.8l3.5-0.9l4-0.3l2.7-0.1l3.1,1.2l4.5,1.4l3.1,1.8l0.6,1.9l0.9,1.9l0.7-0.5 L472.8,203.2z" />
      <path id="TN" fill="#D3D3D3" d="M697.1,320.6l-51.9,5l-15.8,1.8l-4.6,0.5l-3.9,0v3.9l-8.4,0.5l-7,0.6l-11.1,0.1l-0.3,5.8l-2.1,6.3 l-1,3l-1.3,4.4l-0.3,2.6l-4,2.3l1.5,3.6l-1,4.4l-1,0.8l7.3-0.2l24.1-1.9l5.3-0.2l8.1-0.5l27.8-2.6l10.2-0.8l8.4-1l8.4-1.1l4.8-0.8 l-0.1-4.5l1.8-1.5l2.7-0.6l0.6-3.7l4.2-2.7l3.9-1.5l4.2-3.6l4.4-2.1l0.9-3.5l4.3-3.9l0.6-0.2c0,0,0,1.1,0.8,1.1s1.9,0.3,1.9,0.3 l2.3-3.6l2.1-0.6l2.3,0.3l1.6-3.6l2.1-2.2l0.6-1l0.2-3.9l-1.5-0.3l-2.4,1.9l-7.9,0.2l-12,1.9L697.1,320.6z" />
      <path id="TX" fill="#D3D3D3" d="M357.1,333.4l22.7,1.1l31.1,1.1l-2.3,23.5l-0.3,18.2l0.1,2.1l4.3,3.8l1.7,0.8l1.8,0.3l0.7-1.3 l0.9,0.9l1.7,0.5l1.6-0.7l1.1,0.4l-0.3,3.4l4.3,1l2.7,0.8l4,0.5l2.2,1.8l3.2-1.6l2.8,0.4l2,2.8l1.1,0.3l-0.2,2l3.1,1.2l2.8-1.8 l1.5,0.4l2.4,0.2l0.4,1.9l4.6,2l2.7-0.2l2-4.1h0.3l1.1,1.9l4.4,1l3.3,1.2l3.3,0.8l2.1-0.8l0.8-2.5h3.7l1.9,0.8l3.1-1.6h0.7l0.4,1.1 h4.3l2.4-1.3l1.7,0.3l1.4,1.9l2.9,1.7l3.5,1.1l2.7,1.4l2.4,1.6l3.3-0.9l1.9,1l0.5,10.1l0.3,9.7l0.7,9.5l0.5,4l2.7,4.6l1.1,4.1 l3.9,6.3l0.5,2.9l0.5,1l-0.7,7.5l-2.7,4.4l1,2.9l-0.4,2.5l-0.8,7.3l-1.4,2.7l0.6,4.4l-5.7,1.6l-9.9,4.5l-1,1.9l-2.6,1.9l-2.1,1.5 l-1.3,0.8l-5.7,5.3l-2.7,2.1l-5.3,3.2l-5.7,2.4l-6.3,3.4l-1.8,1.5l-5.8,3.6l-3.4,0.6l-3.9,5.5l-4,0.3l-1,1.9l2.3,1.9l-1.5,5.5 l-1.3,4.5l-1.1,3.9l-0.8,4.5l0.8,2.4l1.8,7l1,6.1l1.8,2.7l-1,1.5l-3.1,1.9l-5.7-3.9l-5.5-1.1l-1.3,0.5l-3.2-0.6l-4.2-3.1l-5.2-1.1 l-7.6-3.4l-2.1-3.9l-1.3-6.5l-3.2-1.9l-0.6-2.3l0.6-0.6l0.3-3.4l-1.3-0.6l-0.6-1l1.3-4.4l-1.6-2.3l-3.2-1.3l-3.4-4.4l-3.6-6.6 l-4.2-2.6l0.2-1.9l-5.3-12.3l-0.8-4.2l-1.8-1.9l-0.2-1.5l-6-5.3l-2.6-3.1v-1.1l-2.6-2.1l-6.8-1.1l-7.4-0.6l-3.1-2.3l-4.5,1.8 l-3.6,1.5l-2.3,3.2l-1,3.7l-4.4,6.1l-2.4,2.4l-2.6-1l-1.8-1.1l-1.9-0.6l-3.9-2.3v-0.6l-1.8-1.9l-5.2-2.1l-7.4-7.8l-2.3-4.7v-8.1 l-3.2-6.5l-0.5-2.7l-1.6-1l-1.1-2.1l-5-2.1l-1.3-1.6l-7.1-7.9l-1.3-3.2l-4.7-2.3l-1.5-4.4l-2.6-2.9l-1.9-0.5l-0.6-4.7l8,0.7l29,2.7 l29.3,1.6l2.3-23.8l3.9-55.6l1.6-18.7l1.4,0 M457.2,567.3l-0.6-7.1l-2.7-7.2l-0.6-7l1.5-8.2l3.3-6.9l3.5-5.4l3.2-3.6l0.6,0.2 l-4.8,6.6l-4.4,6.5l-2,6.6l-0.3,5.2l0.9,6.1l2.6,7.2l0.5,5.2l0.2,1.5L457.2,567.3z" />
      <path id="UT" fill="#D3D3D3" d="M253,309.3l-24.6-3.5l-26.6-4.9l-33.8-6l1.6-9.2l3.2-15.2L176,254l2.2-13.6l1.9-8.9l3.8-20.5 l3.5-17.5l1.1-5.6l12.7,2.3l12,2.1l10.3,1.8l8.3,1.4l3.7,0.5l-1.5,10.6l-2.3,13.2l7.8,0.9l16.4,1.8l8.2,0.9l-2.1,22l-3.2,22.6 l-3.8,27.8l-1.7,11.1L253,309.3z" />
      <path id="VA" fill="#D3D3D3" d="M828.9,269.2l-0.1-1.9l6.5-2.5l-0.8,3.2l-2.9,3.8l-0.4,4.6l0.5,3.4l-1.8,5l-2.2,1.9l-1.5-4.6 l0.4-5.4l1.6-4.2L828.9,269.2z M831.2,297.5L773,310.1l-37.4,5.3l-6.7-0.4l-2.6,1.9l-7.3,0.2l-8.4,1l-8.9,1l8.5-4.9l0-2.1 l1.5-2.1l10.6-11.5l3.9,4.5l3.8,1l2.5-1.1l2.2-1.3l2.5,1.3l3.9-1.4l1.9-4.6l2.6,0.5l2.9-2.1l1.8,0.5l2.8-3.7l0.3-2.1l-1-1.3l1-1.9 l5.3-12.3l0.6-5.7l1.2-0.5l2.2,2.4l3.9-0.3l1.9-7.6l2.8-0.6l1-2.7l2.6-2.3l1.3-2.3l1.5-3.4l0.1-5.1l9.8,3.8 c0.7,0.3,0.7-4.8,0.7-4.8l4.1,1.4l-0.5,2.6l8.2,2.9l1.3,1.8l-0.9,3.7l-1.3,1.3l-0.5,1.7l0.5,2.4l2,1.3l3.9,1.4l2.9,1l4.9,0.9 l2.2,2.1l3.2,0.4l0.9,1.2l-0.4,4.7l1.4,1.1l-0.5,1.9l1.2,0.8l-0.2,1.4l-2.7-0.1l0.1,1.6l2.3,1.5l0.1,1.4l1.8,1.8l0.5,2.5l-2.6,1.4 l1.6,1.5l5.8-1.7L831.2,297.5z" />
      <path id="VT" fill="#D3D3D3" d="M844.3,153.7l-0.8-5.7l-2.4-10l-0.6-0.3l-2.9-1.3l0.8-2.9l-0.8-2.1l-2.7-4.6l1-3.9l-0.8-5.2 l-2.4-6.5l-0.8-4.9l26.2-6.7l0.3,5.8l1.9,2.7v4l-3.7,4l-2.6,1.1v1.1l1.1,1.8v8.6l-0.8,9.2l-0.2,4.8l1,1.3l-0.2,4.5l-0.5,1.8 l0.7,1.6l-7,1.4L844.3,153.7z" />
      <path id="WA" fill="#D3D3D3" d="M93.6,6.4l4.4,1.5l9.7,2.7l8.6,1.9l20,5.7l23,5.7l15.2,3.4l-1,3.9l-4.1,13.8l-4.5,20.8 l-3.2,16.1l-0.4,9.4l-13.2-3.9l-15.6-3.4l-13.7,0.6l-1.6-1.5l-5.3,1.9l-4-0.3l-2.7-1.8l-1.6,0.5l-4.2-0.2l-1.9-1.4l-4.8-1.7 l-1.4-0.2l-5-1.3l-1.8,1.5l-5.7-0.3l-4.8-3.8l0.2-0.8l0.1-7.9l-2.1-3.9l-4.1-0.7l-0.4-2.4l-2.5-0.6l-2.9-0.5l-1.8,1l-2.3-2.9 l0.3-2.9l2.7-0.3l1.6-4l-2.6-1.1l0.2-3.7l4.4-0.6l-2.7-2.7l-1.5-7.1l0.6-2.9v-7.9l-1.8-3.2l2.3-9.4l2.1,0.5l2.4,2.9l2.7,2.6 l3.2,1.9l4.5,2.1l3.1,0.6l2.9,1.5l3.4,1l2.3-0.2v-2.4l1.3-1.1l2.1-1.3l0.3,1.1l0.3,1.8l-2.3,0.5l-0.3,2.1l1.8,1.5l1.1,2.4l0.6,1.9 l1.5-0.2l0.2-1.3l-1-1.3l-0.5-3.2l0.8-1.8l-0.6-1.5V19l1.8-3.6l-1.1-2.6l-2.4-4.8l0.3-0.8L93.6,6.4z M84.1,12.3l2-0.2 l0.5,1.4l1.5-1.6h2.3l0.8,1.5l-1.5,1.7l0.6,0.8l-0.7,2l-1.4,0.4c0,0-0.9,0.1-0.9-0.2s1.5-2.6,1.5-2.6l-1.7-0.6l-0.3,1.5l-0.7,0.6 l-1.5-2.3L84.1,12.3z" />
      <path id="WI" fill="#D3D3D3" d="M612.9,197.2l0.4-3l-1.6-4.5l-0.6-6.1l-1.1-2.4l1-3.1l0.8-2.9l1.5-2.6l-0.6-3.4l-0.6-3.6l0.5-1.8 l1.9-2.4l0.2-2.7l-0.8-1.3l0.6-2.6l0.5-3.2l2.7-5.7l2.9-6.8l0.2-2.3l-0.3-1l-0.8,0.5l-4.2,6.3l-2.7,4l-1.9,1.8l-0.8,2.3l-1.5,0.8 l-1.1,1.9l-1.5-0.3l-0.2-1.8l1.3-2.4l2.1-4.7l1.8-1.6l1.1-2.3l-1.6-0.9l-1.4-1.4l-1.6-10.3l-3.7-1.1l-1.4-2.3l-12.6-2.7l-2.5-1.1 l-8.2-2.3l-8.2-1.1l-4.2-5.4l-0.5,1.3l-1.1-0.2l-0.6-1.1l-2.7-0.8l-1.1,0.2l-1.8,1l-1-0.6l0.6-1.9l1.9-3.1l1.1-1.1l-1.9-1.5 l-2.1,0.8l-2.9,1.9l-7.4,3.2l-2.9,0.6l-2.9-0.5l-1-0.9l-2.1,2.8l-0.2,2.7v8.5l-1.1,1.6l-5.3,3.9l-2.3,5.9l0.5,0.2l2.5,2.1l0.7,3.2 l-1.8,3.2v3.9l0.5,6.6l3,3h3.4l1.8,3.2l3.4,0.5l3.9,5.7l7.1,4.1l2.1,2.7l0.9,7.4l0.7,3.3l2.3,1.6l0.2,1.4l-2.1,3.4l0.2,3.2l2.5,3.9 l2.5,1.1l3,0.5l1.3,1.4l9.2,0l26.1-1.5L612.9,197.2z" />
      <path id="WV" fill="#D3D3D3" d="M756.4,242.2l1.1,4.9l1.1,6.9l3.6-2.7l2.3-3.1l2.5-0.6l1.5-1.5l1.8-2.6l1.2,0.6l2.9-0.3l2.6-2.1 l2-1.5l1.8-0.5l1.3,1l2.2,1.1l1.9,1.8l1.4,1.3l-0.1,4.7l-5.7-3.1l-4.5-1.8l-0.2,5.3l-0.5,2.1l-1.6,2.7l-0.6,1.6l-3.1,2.4l-0.5,2.3 l-3.4,0.3l-0.3,3.1l-1.1,5.5h-2.6l-1.3-0.8l-1.6-2.7l-1.8,0.2l-0.3,4.4l-2.1,6.6l-5,10.8l0.8,1.3l-0.2,2.7l-2.1,1.9l-1.5-0.3 l-3.2,2.4l-2.6-1l-1.8,4.7c0,0-3.7,0.8-4.4,1c-0.6,0.2-2.4-1.3-2.4-1.3l-2.4,2.3l-2.6,0.6l-2.9-0.8l-1.3-1.3l-2.2-3l-3.1-2 l-2.6-2.7l-2.9-3.7l-0.6-2.3l-2.6-1.5l-0.8-1.6l-0.2-5.3l2.2-0.1l1.9-0.8l0.2-2.7l1.6-1.5l0.2-5l1-3.9l1.3-0.6l1.3,1.1l0.5,1.8 l1.8-1l0.5-1.6l-1.1-1.8v-2.4l1-1.3l2.3-3.4l1.3-1.5l2.1,0.5l2.3-1.6l3.1-3.4l2.3-3.9l0.3-5.7l0.5-5v-4.7l-1.1-3.1l1-1.5l1.3-1.3 l3.5,19.8l4.6-0.8L756.4,242.2z" />
      <path id="WY" fill="#D3D3D3" d="M354.3,143.8l-10.5-0.8l-32.1-3.3l-16.2-2.1l-28.3-4.1l-19.9-3l-1.4,11.2l-3.8,24.3l-5.3,30.4 l-1.5,10.5l-1.7,11.9l6.5,0.9l25.9,2.5l20.6,2.3l36.8,4.1l23.8,2.9l4.5-44.2l1.4-25.4L354.3,143.8z" />
      <circle id="DC" fill="#D3D3D3" stroke="#FFFFFF" stroke-width="1.5" cx="801.3" cy="251.8" r="5" />
      `],
      stateSVGs: [
        { abbreviation: 'AL', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/5d/Alabama_State_Map.svg' }, // Alabama
        { abbreviation: 'AK', svg: 'https://upload.wikimedia.org/wikipedia/commons/e/e6/Alaska_State_Map.svg' }, // Alaska
        { abbreviation: 'AZ', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/9d/Arizona_State_Map.svg' }, // Arizona
        { abbreviation: 'AR', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/9f/Arkansas_State_Map.svg' }, // Arkansas
        { abbreviation: 'CA', svg: 'https://upload.wikimedia.org/wikipedia/commons/1/1e/California_State_Map.svg' }, // California
        { abbreviation: 'CO', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/5b/Colorado_State_Map.svg' }, // Colorado
        { abbreviation: 'CT', svg: 'https://upload.wikimedia.org/wikipedia/commons/f/f0/Connecticut_State_Map.svg' }, // Connecticut
        { abbreviation: 'DE', svg: 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Delaware_State_Map.svg' }, // Delaware
        { abbreviation: 'FL', svg: 'https://upload.wikimedia.org/wikipedia/commons/3/31/Florida_State_Map.svg' }, // Florida
        { abbreviation: 'GA', svg: 'https://upload.wikimedia.org/wikipedia/commons/f/fc/Georgia_State_Map.svg' }, // Georgia
        { abbreviation: 'HI', svg: 'https://upload.wikimedia.org/wikipedia/commons/d/d4/Hawaii_State_Map.svg' }, // Hawaii
        { abbreviation: 'ID', svg: 'https://upload.wikimedia.org/wikipedia/commons/7/76/Idaho_State_Map.svg' }, // Idaho
        { abbreviation: 'IL', svg: 'https://upload.wikimedia.org/wikipedia/commons/c/c3/Illinois_State_Map.svg' }, // Illinois
        { abbreviation: 'IN', svg: 'https://upload.wikimedia.org/wikipedia/commons/1/1b/Indiana_State_Map.svg' }, // Indiana
        { abbreviation: 'IA', svg: 'https://upload.wikimedia.org/wikipedia/commons/0/0c/Iowa_State_Map.svg' }, // Iowa
        { abbreviation: 'KS', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/97/Kansas_State_Map.svg' }, // Kansas
        { abbreviation: 'KY', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/9d/Kentucky_State_Map.svg' }, // Kentucky
        { abbreviation: 'LA', svg: 'https://upload.wikimedia.org/wikipedia/commons/2/29/Louisiana_State_Map.svg' }, // Louisiana
        { abbreviation: 'ME', svg: 'https://upload.wikimedia.org/wikipedia/commons/f/fe/Maine_State_Map.svg' }, // Maine
        { abbreviation: 'MD', svg: 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Maryland_State_Map.svg' }, // Maryland
        { abbreviation: 'MA', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/52/Massachusetts_State_Map.svg' }, // Massachusetts
        { abbreviation: 'MI', svg: 'https://upload.wikimedia.org/wikipedia/commons/f/f1/Michigan_State_Map.svg' }, // Michigan
        { abbreviation: 'MN', svg: 'https://upload.wikimedia.org/wikipedia/commons/8/8b/Minnesota_State_Map.svg' }, // Minnesota
        { abbreviation: 'MS', svg: 'https://upload.wikimedia.org/wikipedia/commons/2/28/Mississippi_State_Map.svg' }, // Mississippi
        { abbreviation: 'MO', svg: 'https://upload.wikimedia.org/wikipedia/commons/8/8d/Missouri_State_Map.svg' }, // Missouri
        { abbreviation: 'MT', svg: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/Montana_State_Map.svg' }, // Montana
        { abbreviation: 'NE', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/91/Nebraska_State_Map.svg' }, // Nebraska
        { abbreviation: 'NV', svg: 'https://upload.wikimedia.org/wikipedia/commons/7/7f/Nevada_State_Map.svg' }, // Nevada
        { abbreviation: 'NH', svg: 'https://upload.wikimedia.org/wikipedia/commons/a/a2/New_Hampshire_State_Map.svg' }, // New Hampshire
        { abbreviation: 'NJ', svg: 'https://upload.wikimedia.org/wikipedia/commons/4/42/New_Jersey_State_Map.svg' }, // New Jersey
        { abbreviation: 'NM', svg: 'https://upload.wikimedia.org/wikipedia/commons/e/e8/New_Mexico_State_Map.svg' }, // New Mexico
        { abbreviation: 'NY', svg: 'https://upload.wikimedia.org/wikipedia/commons/1/16/New_York_State_Map.svg' }, // New York
        { abbreviation: 'NC', svg: 'https://upload.wikimedia.org/wikipedia/commons/9/9e/North_Carolina_State_Map.svg' }, // North Carolina
        { abbreviation: 'ND', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/5b/North_Dakota_State_Map.svg' }, // North Dakota
        { abbreviation: 'OH', svg: 'https://upload.wikimedia.org/wikipedia/commons/4/4d/Ohio_State_Map.svg' }, // Ohio
        { abbreviation: 'OK', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/5d/Oklahoma_State_Map.svg' }, // Oklahoma
        { abbreviation: 'OR', svg: 'https://upload.wikimedia.org/wikipedia/commons/5/55/Oregon_State_Map.svg' }, // Oregon
        { abbreviation: 'PA', svg: 'https://upload.wikimedia.org/wikipedia/commons/3/32/Pennsylvania_State_Map.svg' }, // Pennsylvania
        { abbreviation: 'RI', svg: 'https://upload.wikimedia.org/wikipedia/commons/3/35/Rhode_Island_State_Map.svg' }, // Rhode Island
        { abbreviation: 'SC', svg: 'https://upload.wikimedia.org/wikipedia/commons/1/1d/South_Carolina_State_Map.svg' }, // South Carolina
        { abbreviation: 'SD', svg: 'https://upload.wikimedia.org/wikipedia/commons/6/6e/South_Dakota_State_Map.svg' }, // South Dakota
        { abbreviation: 'TN', svg: 'https://upload.wikimedia.org/wikipedia/commons/8/8f/Tennessee_State_Map.svg' }, // Tennessee
        { abbreviation: 'TX', svg: 'https://upload.wikimedia.org/wikipedia/commons/2/2c/Texas_State_Map.svg' }, // Texas
        { abbreviation: 'UT', svg: 'https://upload.wikimedia.org/wikipedia/commons/f/f6/Utah_State_Map.svg' }, // Utah
        { abbreviation: 'VT', svg: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Vermont_State_Map.svg' }, // Vermont
        { abbreviation: 'VA', svg: 'https://upload.wikimedia.org/wikipedia/commons/6/68/Virginia_State_Map.svg' }, // Virginia
        { abbreviation: 'WA', svg: 'https://upload.wikimedia.org/wikipedia/commons/e/ed/Washington_State_Map.svg' }, // Washington
        { abbreviation: 'WV', svg: 'https://upload.wikimedia.org/wikipedia/commons/6/60/West_Virginia_State_Map.svg' }, // West Virginia
        { abbreviation: 'WI', svg: 'https://upload.wikimedia.org/wikipedia/commons/6/6c/Wisconsin_State_Map.svg' }, // Wisconsin
        { abbreviation: 'WY', svg: 'https://upload.wikimedia.org/wikipedia/commons/3/35/Wyoming_State_Map.svg' } // Wyoming
      ],
      isLoaded: false,
      updateCheerConfirmOverlay: false,
      quizSettings: {
        unlimitedAttempts: false,
        attempts: 0,
        passingScore: 0,
        failAction: 'advance',
        allowQuizRetake: false,
        showMissedQuestions: false,
      },
      currentGroupWatch: {},
      groupWatchActive: false,
      resumeGroupWatchActive: false,
      groupWatchAttendees: [],
      groupWatchCurrentModule: null,
      groupWatchProgress: {
        videosWatched: [],
        moduleCompleted: false
      },
      groupWatchKnowledgeSessionCode: null,
      instructorPendingGroupWatchSessionCode: null,
      groupWatchSessionKnowledgeCheckLog: [],
      learnerGroupWatchKnowledgeJoinCode: null
    }
  },
  getters: {
    currentOrgInstructor() {
      return findCurrentOrgInstructor(this.organization, this.user)
    },
    instructorHasLimitedSportAccess() {
      return instructorHasLimitedSports(this.currentOrgInstructor)
    },
    instructorAllowedSports() {
      return getInstructorAllowedSports(
        this.currentOrgInstructor,
        uniqueSportsFromItems(this.courses)
      )
    },
    instructorCanAccessSport() {
      return (sport) => (
        !this.instructorHasLimitedSportAccess
        || contentSportIsAllowed({ sport }, this.instructorAllowedSports)
      )
    },
    instructorCanAccessContent() {
      return (item, options = {}) => (
        !this.instructorHasLimitedSportAccess
        || contentSportIsAllowed(item, this.instructorAllowedSports, options)
      )
    },
    instructorCanAccessDeveloperPortal() {
      return this.organization?.hasDeveloperPortalAccess === true
    },
    developerToolsOn() {
      return this.developerAccess === true && this.isInDevMode === true
    },
    unlimitedAttempts: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return state.global_settings.quiz.unlimitedAttempts;
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return state.course_settings[courseId].quiz.unlimitedAttempts;
      }
      return true; // Default value
    },
    attempts: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return state.global_settings.quiz.attempts;
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return state.course_settings[courseId].quiz.attempts;
      }
      return 2; // Default value
    },
    passingScore: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return state.global_settings.quiz.passingScore;
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return state.course_settings[courseId].quiz.passingScore;
      }
      return 70; // Default value
    },
    failAction: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return normalizeFailAction(state.global_settings.quiz.failAction);
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return normalizeFailAction(state.course_settings[courseId].quiz.failAction);
      }
      return 'advance'; // Default value
    },
    showMissedQuestions: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return state.global_settings.quiz.showMissedQuestions;
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return state.course_settings[courseId].quiz.showMissedQuestions;
      }
      return true; // Default value
    },
    allowQuizRetake: (state) => (courseId) => {
      if (state.global_settings && state.global_settings.isActive && state.global_settings.quiz) {
        return state.global_settings.quiz.allowQuizRetake;
      }
      if (courseId && state.course_settings && state.course_settings[courseId] && state.course_settings[courseId].quiz) {
        return state.course_settings[courseId].quiz.allowQuizRetake;
      }
      return false; // Default value
    },
    knowledgeChecksEnabled: (state) => userCourseHasKnowledgeChecks(state.user_course),
    isKnowledgeCheckComplete: (state) => (moduleId, knowledgeCheckId = null) => {
      if (!userCourseHasKnowledgeChecks(state.user_course)) {
        return true
      }
      if (!moduleId) {
        return false
      }
      if (knowledgeCheckId == null) {
        return false
      }
      const checks = normalizeKnowledgeCheckProgress(state.user_course?.knowledge_checks)
      return checks.some((item) =>
        progressIsComplete(item) &&
        progressModuleId(item) != null &&
        progressKnowledgeCheckId(item) != null &&
        String(progressModuleId(item)) === String(moduleId) &&
        String(progressKnowledgeCheckId(item)) === String(knowledgeCheckId)
      )
    },
    moduleHasAllKnowledgeChecksComplete: (state) => (module) => {
      if (!userCourseHasKnowledgeChecks(state.user_course)) {
        return true
      }
      if (!module || !module.id) {
        return true
      }
      const raw = Array.isArray(module.knowledge_checks) ? module.knowledge_checks : []
      const required = raw
        .map((kc, idx) => ({ kc, idx }))
        .filter(({ kc }) => kc && kc.active !== false && isSupportedKnowledgeCheckType(kc.type))
      if (!required.length) {
        return true
      }
      const checks = normalizeKnowledgeCheckProgress(state.user_course?.knowledge_checks)
      return required.every(({ kc, idx }) => {
        if (kc.id == null) return false
        return checks.some((item) =>
          progressIsComplete(item) &&
          progressModuleId(item) != null &&
          progressKnowledgeCheckId(item) != null &&
          String(progressModuleId(item)) === String(module.id) &&
          String(progressKnowledgeCheckId(item)) === String(kc.id)
        )
      })
    },
    /** Report tabs: `studentData` filtered by org custom learner tags (roster `students` lookup). */
    filteredStudentDataForReports: (state) => {
      const rows = Array.isArray(state.studentData) ? state.studentData : []
      const orgId = state.organization?.companyID
      if (!orgId) {
        return sortReportRowsByLearnerName(rows, state.reportsSortBy)
      }
      const roster = Array.isArray(state.students) ? state.students : []
      const filtered = applyCustomTagFilterToReportRows(
        rows,
        roster,
        orgId,
        state.reportsCustomFilterSelections,
        state.organizationCustomFilters
      )
      return sortReportRowsByLearnerName(filtered, state.reportsSortBy)
    },
    /** Roster emails matching active report custom-tag filters (org-wide tabs). */
    reportsFilteredRosterEmailSet(state) {
      const orgId = state.organization?.companyID
      if (!orgId) return null
      if (
        !hasActiveCustomTagSelections(
          state.reportsCustomFilterSelections,
          state.organizationCustomFilters
        )
      ) {
        return null
      }
      const roster = Array.isArray(state.students) ? state.students : []
      return filteredRosterLearnerEmailSet(
        roster,
        orgId,
        state.reportsCustomFilterSelections,
        state.organizationCustomFilters
      )
    },
  },
  actions: {
    // Load notes using the notes store
    async loadNotes() {
      const notesStore = useNotesStore()
      await notesStore.getNotes()
    },
    async saveInteractiveProgress(calls) {
      let result = false

      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/updateProgress`, {
          email: this.user.email,
          course_id: this.currentRepsCourse.id,
          progress_id: this.currentItem.id,
          calls: calls,
          productType: 'interactive',
          type: 'video'
        })
        .then(async (response) => {
          if (response.data.repProgress.filter((item) => !item.isComplete) == 0) {
            this.isShowingPOVCourseView = false
            this.showCourseModal = true;
            await this.reloadPOVPack()
          } else {
            this.currentItem = response.data.repProgress.filter((item) => !item.isComplete)[0]
            this.currentCallNumber = 0
          }

          result = true
        })
        .catch(function (error) {
          console.log(error)
          result = false
        })

      return result
    },
    async retakeQuiz() {
      var module_id = this.currentItem.module_id
      var module_questions = this.progress.filter(
        (item) => item.module_id == module_id && (item.type == 'question' || item.type == 'result')
      )

      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/retakeQuiz`, {
          user_id: this.user.id,
          id: this.currentCourse.id,
          module_id: module_id,
          questions: module_questions,
          isExtraRetake: true
        })
        .then((response) => {
          this.currentItem = response.data.progress.filter((item) => !item.isComplete)[0]
          this.progress = response.data.progress
          this.currentQuizTotal = response.data.module_questions.length
          this.currentQuizIndex = 1
          this.showQuizResults = false
          this.hasCompletedCourse = false
          this.hasCompletedModule = false
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    handleDownload(url, fileName) {
      axios
        .get(url, {
          responseType: 'blob'
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: 'application/pdf'
          })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = fileName
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .catch(console.error)
    },
    handleDownloadFromReader() {
      axios
        .get(this.url, {
          responseType: 'blob'
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: 'application/pdf'
          })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = this.fileName
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .catch(console.error)
    },
    setCurrentModule(module_id) {
      this.courseModules = []
      this.currentCourse.videos.forEach((module) => {
        var editedModule = module
        if (module.module_id == this.currentModuleID) {
          editedModule.isVideoOpen = true
        } else {
          editedModule.isVideoOpen = false
        }
        editedModule.showResource = false
        this.courseModules.push(editedModule)
      })
      this.currentProgressID = this.progress[this.videoCount].id
      this.currentVideoID = this.progress[this.videoCount].id
      this.progress.forEach((item) => {
        if (item.isComplete) {
          this.completedIDs.push(item.id)
        }
      })
    },
    async updateRemoteCart() {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/updateShoppingCart`, {
          items: this.activeCartItems
        })
        .then((response) => {
          if (response.data.code == 200) {
            // this.activeCartItems = response.data.items
          }
        })
        .catch((error) => {
          alert(error)
          console.log(error)
        })
    },
    async getStudentData(courseID, companyID) {
      this.showRefreshingData = true
      this.showAnalytics = false
      this.currentInstructorPack = {}
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/get-student-data`, {
          companyID: companyID,
          course_id: courseID
        })
        .then((response) => {
          this.currentInstructorCourse = response.data.currentCourse
          const rows = response.data.data
          this.studentData = Array.isArray(rows)
            ? rows.map((row) => ({
                ...row,
                courseVersionProgressArchive: Array.isArray(row.courseVersionProgressArchive)
                  ? row.courseVersionProgressArchive
                  : [],
              }))
            : []
          this.showRefreshingData = false
        })
      .catch(function (error) {
        alert(error)
        console.log(error)
        this.showRefreshingData = false
      })
      

      
    },
    /**
     * Normalize Mongo/API ids so selects, filters, and strict equality work (string vs ObjectId, $oid, etc.).
     */
    normalizeReportId(val) {
      if (val == null || val === '') return ''
      if (typeof val === 'string' || typeof val === 'number') return String(val)
      if (typeof val === 'object') {
        if (val.$oid != null) return String(val.$oid)
        if (val._id != null) return this.normalizeReportId(val._id)
        if (typeof val.toString === 'function') {
          const ts = val.toString()
          if (ts && ts !== '[object Object]') return ts
        }
      }
      return String(val)
    },
    /**
     * Map `currentPack` from POST /get-student-pack-data into `currentInstructorCourse`-shaped UI (modules from videos).
     */
    normalizePackForInstructorReport(currentPack, packId) {
      const fallbackId = this.normalizeReportId(packId)
      if (!currentPack || typeof currentPack !== 'object') {
        return {
          id: fallbackId,
          name: '',
          sport: '',
          modules: [],
          type: 'pov',
        }
      }
      /** Prefer catalog/request id (`fallbackId`) when set — `currentPack._id` may differ from per-row `pack_id`. */
      const id = fallbackId
        ? fallbackId
        : this.normalizeReportId(
            currentPack._id ?? currentPack.id ?? ''
          )
      const videos = Array.isArray(currentPack.videos) ? currentPack.videos : []
      const modules = videos.map((v, i) => ({
        id: this.normalizeReportId(v.id ?? v._id ?? `pack-video-${i}`),
        title: v.title || v.name || `Video ${i + 1}`,
      }))
      return {
        id,
        name: currentPack.name || currentPack.title || 'Pack',
        sport: currentPack.sport || '',
        modules,
        type: 'pov',
        previewImageLink: currentPack.previewImageLink,
        cover: currentPack.cover,
      }
    },
    /** Supplemental POV pack instructor analytics — POST /get-student-pack-data */
    async getStudentPackData(packID, companyID) {
      this.showRefreshingData = true
      this.showAnalytics = false
      this.currentInstructorPack = {}
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/get-student-pack-data`,
          { companyID, pack_id: packID },
          { withCredentials: true }
        )
        const d = response.data
        const code = d?.code
        const codeNum = Number(code)

        if (code === 404 || code === '404' || codeNum === 404) {
          alert(d.message || 'Pack not found')
          this.studentData = []
          this.currentInstructorCourse = {}
          return
        }
        if (code === 500 || code === '500' || codeNum === 500) {
          alert(d.message || 'Internal server error')
          this.studentData = []
          this.currentInstructorCourse = {}
          return
        }

        const rowsRaw = Array.isArray(d.data)
          ? d.data
          : Array.isArray(d.students)
            ? d.students
            : []
        const packIdNorm = this.normalizeReportId(packID)
        const successExplicit =
          code === 200 ||
          code === '200' ||
          codeNum === 200
        /** Treat HTTP 200 as success when `code` is omitted (some deployments only send `data` + `currentPack`). */
        const successImplicit =
          code == null &&
          response.status === 200 &&
          (rowsRaw.length > 0 || d.currentPack != null)
        if (successExplicit || successImplicit) {
          this.currentInstructorPack = d.currentPack || {}
          this.currentInstructorCourse = this.normalizePackForInstructorReport(
            d.currentPack,
            packID
          )
          /** `course_id` must match the selected catalog id (`packID`); row `pack_id` may differ from `currentPack._id`. */
          this.studentData = rowsRaw.map((row) => ({
            ...row,
            pack_id:
              row.pack_id != null
                ? this.normalizeReportId(row.pack_id)
                : packIdNorm,
            course_id: packIdNorm,
          }))
          return
        }

        this.studentData = []
        this.currentInstructorCourse = {}
      } catch (error) {
        console.error(error)
        const res = error.response
        const ed = res?.data
        if (res?.status === 404 || ed?.code === 404) {
          alert(ed?.message || 'Pack not found')
        } else if (res?.status >= 500 || ed?.code === 500) {
          alert(ed?.message || 'Internal server error')
        } else {
          alert(ed?.message || error.message || 'Failed to load pack report data')
        }
        this.studentData = []
        this.currentInstructorCourse = {}
        this.currentInstructorPack = {}
      } finally {
        this.showRefreshingData = false
      }
    },
    /**
     * Refetch instructor org catalog (POST /get-courses). Mirrors InstructorDashboard course list shaping.
     * @param {string} companyID
     * @param {{ preserveCourseId?: string }} [options] When set, keep this course as `currentInstructorCourse` after refresh.
     */
    async refreshInstructorCourses(companyID, options = {}) {
      const preserveCourseId = options.preserveCourseId
      try {
        const response = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-courses`, {
          companyID
        })
        this.isRefreshingCourses = false
        this.isGettingCourses = false
        const updatedCourses = []
        this.stateExams = response.data.exams
        const unviewedCounts = buildUnviewedCountsByCourseId(response.data)
        const normalizedUnviewedCounts = {}
        Object.entries(unviewedCounts).forEach(([courseId, count]) => {
          if ((Number(count) || 0) > 0) {
            normalizedUnviewedCounts[this.normalizeReportId(courseId)] = 1
          }
        })
        if (Object.keys(normalizedUnviewedCounts).length) {
          this.orgCourseUpdateUnviewedByCourseId = {
            ...this.orgCourseUpdateUnviewedByCourseId,
            ...normalizedUnviewedCounts,
          }
        }
        response.data.courses.forEach((course) => {
          const editedCourse = course
          const rawUnviewed = Number(
            course.unviewedCourseUpdateCount ?? course.unviewed_course_update_count
          ) || 0
          editedCourse.unviewedCourseUpdateCount = rawUnviewed > 0 ? 1 : 0
          editedCourse.unviewed_course_update_count = editedCourse.unviewedCourseUpdateCount
          editedCourse.isActive = !response.data.inActiveCourses.includes(course.id)
          editedCourse.isDroppedDown = false
          if (course.type !== 'course') {
            editedCourse.videoTotal = 25
            editedCourse.type = 'pov'
          } else {
            editedCourse.videoTotal = course.modules.flatMap((module) => module.videos).length
            editedCourse.questionTotal = course.modules.flatMap((module) => module.quiz).length
          }
          if (course.type !== 'course') {
            const tempVideos = course.videos || []
            tempVideos.forEach((video) => {
              video.resources = []
            })
            editedCourse.videos = tempVideos
          }
          updatedCourses.push(editedCourse)
        })
        this.courses = updatedCourses.sort((a, b) => {
          if (a.type !== b.type) {
            return a.type === 'course' ? -1 : 1
          }
          return a.sport.toLowerCase().localeCompare(b.sport.toLowerCase())
        })
        if (this.instructorHasLimitedSportAccess) {
          this.courses = this.courses.filter((course) => this.instructorCanAccessContent(course))
        }
        this.sports = this.courses.map((c) => c.sport)
        if (this.courses.length !== 0) {
          const preservedNorm =
            preserveCourseId != null && preserveCourseId !== ''
              ? this.normalizeReportId(preserveCourseId)
              : ''
          const pick =
            preservedNorm !== ''
              ? this.courses.find((c) => this.normalizeReportId(c.id) === preservedNorm) ||
                this.courses[0]
              : this.courses[0]
          this.currentInstructorCourse = pick
          this.currentCourseToAssign = pick.id
          this.reportsShowSupplementalPacks = false
          this.reportsContentTypeFilter = 'course'
          const firstEducational = this.courses.find((c) => c.type === 'course')
          /** When preserving selection, report APIs must target that course — otherwise `getStudentData` overwrites `currentInstructorCourse` with the first educational course. */
          const reportCourse =
            preservedNorm !== '' ? pick : firstEducational || null
          if (reportCourse) {
            this.cCourseID = reportCourse.id
            await this.loadInstructorReportData(reportCourse.id, companyID)
          } else {
            this.cCourseID = ''
            this.studentData = []
          }
        } else {
          this.currentInstructorCourse = {}
          this.currentCourseToAssign = ''
          this.cCourseID = ''
          this.studentData = []
          if (this.currentCoursePage === 'courseDetail' || this.currentCoursePage === 'courseInteractiveDetail') {
            this.currentCoursePage = 'courseList'
          }
        }
      } catch (error) {
        console.log(error)
        this.isRefreshingCourses = false
        this.isGettingCourses = false
        throw error
      }
    },
    async refreshOrgCourseUpdateUnviewedMap(organizationId, instructorUserId) {
      if (!organizationId || !instructorUserId) {
        this.orgCourseUpdateUnviewedByCourseId = {}
        this.orgCourseUpdateUnviewedEventByCourseId = {}
        this.orgCourseUpdateUnviewedEventIdsByCourseId = {}
        return
      }
      try {
        const { allUnviewed, unviewed } = await fetchUnviewedOrganizationCourseUpdates(axios, {
          organizationId,
          instructorUserId,
        })
        const source = allUnviewed?.length ? allUnviewed : unviewed
        const { countMap, eventMap, eventIdsByCourseId } = buildUnviewedOrgCourseUpdateMaps(
          source,
          {
            normalizeId: (id) => this.normalizeReportId(id),
            dismissedEventIds: this.dismissedOrgCourseUpdateEventIds,
          }
        )
        this.orgCourseUpdateUnviewedByCourseId = countMap
        this.orgCourseUpdateUnviewedEventByCourseId = eventMap
        this.orgCourseUpdateUnviewedEventIdsByCourseId = eventIdsByCourseId
      } catch (error) {
        console.error('refreshOrgCourseUpdateUnviewedMap:', error)
        this.orgCourseUpdateUnviewedByCourseId = {}
        this.orgCourseUpdateUnviewedEventByCourseId = {}
        this.orgCourseUpdateUnviewedEventIdsByCourseId = {}
      }
    },
    clearOrgCourseUpdateUnviewedForCourse(courseId) {
      const norm = this.normalizeReportId(courseId)
      if (!norm) return

      const nextCounts = { ...this.orgCourseUpdateUnviewedByCourseId }
      Object.keys(nextCounts).forEach((key) => {
        if (this.normalizeReportId(key) === norm) {
          delete nextCounts[key]
        }
      })
      this.orgCourseUpdateUnviewedByCourseId = nextCounts

      const nextEvents = { ...this.orgCourseUpdateUnviewedEventByCourseId }
      Object.keys(nextEvents).forEach((key) => {
        if (this.normalizeReportId(key) === norm) {
          delete nextEvents[key]
        }
      })
      this.orgCourseUpdateUnviewedEventByCourseId = nextEvents

      const nextEventIds = { ...this.orgCourseUpdateUnviewedEventIdsByCourseId }
      Object.keys(nextEventIds).forEach((key) => {
        if (this.normalizeReportId(key) === norm) {
          delete nextEventIds[key]
        }
      })
      this.orgCourseUpdateUnviewedEventIdsByCourseId = nextEventIds

      const zeroUnviewed = (course) => ({
        ...course,
        unviewedCourseUpdateCount: 0,
        unviewed_course_update_count: 0,
      })

      this.courses = this.courses.map((course) =>
        this.normalizeReportId(course.id) === norm ? zeroUnviewed(course) : course
      )

      if (Array.isArray(this.allCourses) && this.allCourses.length) {
        this.allCourses = this.allCourses.map((course) =>
          this.normalizeReportId(course.id) === norm ? zeroUnviewed(course) : course
        )
      }

      if (
        this.currentInstructorCourse?.id
        && this.normalizeReportId(this.currentInstructorCourse.id) === norm
      ) {
        this.currentInstructorCourse = {
          ...this.currentInstructorCourse,
          unviewedCourseUpdateCount: 0,
          unviewed_course_update_count: 0,
        }
      }
    },
    courseHasUnviewedOrgUpdate(courseId) {
      const norm = this.normalizeReportId(courseId)
      if (!norm) return false

      const countFromMap = Object.entries(this.orgCourseUpdateUnviewedByCourseId || {}).find(
        ([key]) => this.normalizeReportId(key) === norm
      )?.[1]
      if ((Number(countFromMap) || 0) > 0) return true

      const course = this.courses.find((c) => this.normalizeReportId(c.id) === norm)
      const fromCourse = Number(
        course?.unviewedCourseUpdateCount ?? course?.unviewed_course_update_count
      ) || 0
      return fromCourse > 0
    },
    async markOrgCourseUpdateViewed({ organizationId, instructorUserId, eventId, courseId }) {
      const norm = this.normalizeReportId(courseId)
      const eventIdsToMark = new Set()
      if (eventId) eventIdsToMark.add(eventId)
      if (norm && Array.isArray(this.orgCourseUpdateUnviewedEventIdsByCourseId?.[norm])) {
        this.orgCourseUpdateUnviewedEventIdsByCourseId[norm].forEach((id) => eventIdsToMark.add(id))
      }

      if (organizationId && instructorUserId && courseId) {
        try {
          const { unviewed } = await fetchUnviewedOrganizationCourseUpdates(axios, {
            organizationId,
            instructorUserId,
            courseId,
          })
          unviewed.forEach((event) => {
            if (event?.eventId) eventIdsToMark.add(event.eventId)
          })
        } catch (error) {
          console.error('markOrgCourseUpdateViewed: fetch unviewed for course', error)
        }
      }

      const ids = [...eventIdsToMark]

      if (organizationId && instructorUserId && ids.length) {
        if (courseId) {
          try {
            await markOrganizationCourseUpdateViewed(axios, {
              organizationId,
              instructorUserId,
              eventId: eventId || ids[0],
              courseId,
              markAllForCourse: true,
            })
          } catch (error) {
            console.error('markOrgCourseUpdateViewed (bulk):', error)
          }
        }
        await Promise.allSettled(
          ids.map((id) => markOrganizationCourseUpdateViewed(axios, {
            organizationId,
            instructorUserId,
            eventId: id,
            courseId: courseId || undefined,
          }))
        )
      }

      const dismissed = { ...this.dismissedOrgCourseUpdateEventIds }
      ids.forEach((id) => {
        dismissed[id] = true
      })
      this.dismissedOrgCourseUpdateEventIds = dismissed

      if (courseId) {
        this.clearOrgCourseUpdateUnviewedForCourse(courseId)
      }
      this.orgCourseUpdateUnviewedRevision += 1
    },
    async markAllOrgCourseUpdatesViewed({ organizationId, instructorUserId }) {
      const eventMap = this.orgCourseUpdateUnviewedEventByCourseId || {}
      const entries = Object.values(eventMap).filter((event) => event?.eventId && event?.courseId)
      if (!organizationId || !instructorUserId || !entries.length) {
        return
      }
      await Promise.allSettled(
        entries.map((event) => this.markOrgCourseUpdateViewed({
          organizationId,
          instructorUserId,
          eventId: event.eventId,
          courseId: event.courseId,
        }))
      )
    },
    bumpOrgCourseUpdateUnviewedRevision() {
      this.orgCourseUpdateUnviewedRevision += 1
    },
    applyOrgCourseUpdateUnviewedCountsFromCoursesResponse(responseData) {
      const counts = buildUnviewedCountsByCourseId(responseData)
      if (!Object.keys(counts).length) return
      const normalized = {}
      Object.entries(counts).forEach(([courseId, count]) => {
        const key = this.normalizeReportId(courseId)
        if (key && (Number(count) || 0) > 0) {
          normalized[key] = 1
        }
      })
      this.orgCourseUpdateUnviewedByCourseId = {
        ...this.orgCourseUpdateUnviewedByCourseId,
        ...normalized,
      }
    },
    /**
     * Loads gradebook / insights data for the selected catalog item.
     * Courses: get-student-data + activity insights APIs. Packs: get-student-pack-data only (no activity insights).
     */
    async loadInstructorReportData(courseID, companyID) {
      const course = this.courses.find(
        (c) => this.normalizeReportId(c.id) === this.normalizeReportId(courseID)
      )
      if (!course) return
      if (course.type === 'pov') {
        await this.getStudentPackData(courseID, companyID)
        this.userActivityInsights = []
        this.activityInsights = []
        this.studentExamReportData = []
        await this.getOrganizationPackCompletionHistory(companyID)
        return
      }
      await this.getStudentData(courseID, companyID)
      await this.getActivityInsights(course.name)
      if (this.organizationHasPremiumSeatLicense()) {
        await this.getStudentExamReportData(companyID, courseID)
      } else {
        this.studentExamReportData = []
      }
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/get-activity-insights-by-user`,
          {
            courseName: course.name,
            companyID,
          },
          { withCredentials: true }
        )
        const rows = response.data
        this.userActivityInsights = Array.isArray(rows)
          ? rows.sort((a, b) => a.name.localeCompare(b.name))
          : []
      } catch (error) {
        console.error(error)
      }
    },
    /**
     * Persists whether this organization may use supplemental POV pack reports/grading in instructor analytics.
     * Expects API: POST /toggle-organization-should-see-pack-reports { companyID, shouldSeePackReports }
     */
    async setOrganizationShouldSeePackReports(shouldSeePackReports) {
      const companyID = this.organization?.companyID
      if (!companyID) return
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/toggle-organization-should-see-pack-reports`,
        { companyID, shouldSeePackReports },
        { withCredentials: true }
      )
      const d = response.data
      const ok =
        response.status === 200 &&
        (d?.code === 200 || d?.code == null || d?.success === true)
      if (ok) {
        this.organization.shouldSeePackReports = shouldSeePackReports
        return
      }
      throw new Error(d?.message || 'Could not update organization setting')
    },
    async getStudentLearningActivityData(sport, companyID, activity_id) {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/get-student-learning-activity-data`, {
          sport: sport,
          companyID: companyID,
          activityID: activity_id
        })
        .then((response) => {
          this.studentLearningActivityData = response.data
        })
        .catch(function (error) {
          console.error(error)
        })
    },
    async getStudentAssignedExamData(companyID, examID) {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/get-student-assigned-exam-data`, {
          companyID,
          examID,
        })
        .then((response) => {
          this.studentExamAssignmentData = normalizeAssignedExamDataResponse(response.data)
        })
        .catch(function (error) {
          console.error(error)
        })
    },
    async getStudentExamReportData(organizationId, courseId) {
      if (!organizationId || !courseId) {
        this.studentExamReportData = []
        return
      }
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/get-student-exam-report-data`,
          {
            organizationId,
            courseId,
          },
          { withCredentials: true }
        )
        this.studentExamReportData = Array.isArray(response.data?.exams)
          ? response.data.exams
          : []
      } catch (error) {
        console.error(error)
        this.studentExamReportData = []
      }
    },
    async getActivityInsights(courseName) {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/get-activity-insights`, {
          courseName: courseName,
        })
        .then((response) => {
          this.activityInsights = response.data
        })
        .catch(function (error) {
          console.error(error)
        })
    },
    uniq(a) {
      return Array.from(new Set(a))
    },
    searchContent(event) {
      if (event.target.value == '' || event.target.value == ' ') {
        this.searchResults = []
        return
      }
      axios
        .post(`${import.meta.env.VITE_BASE_LINK}/search-content-by-sport`, {
          text: event.target.value.toLowerCase(),
          sport: this.currentCourse.sport.toLowerCase()
        })
        .then((response) => {
          this.documents = response.data.documents
          this.videos = response.data.videos
          this.podcasts = response.data.podcasts
          this.rules = response.data.rules

          this.searchResults = [...this.documents, ...this.videos, ...this.podcasts, ...this.rules]
        })
        .catch(function (error) {
          alert(error)
        })
    },
    takeAwaySeat(id) {
      axios
        .post(`${import.meta.env.VITE_BASE_LINK}/take-away-seat`, {
          companyID: id,
          user_id: this.user.id,
          course_id: this.currentUserCourse.id
        })
        .then((response) => {
          console.log('Seat removed')
        })
        .catch(function (error) {
          alert(error)
        })
    },
    addUserToOrganization() {
      this.isAddingNewOrganization = true

      axios
        .post(`${import.meta.env.VITE_BASE_LINK}/add-user-to-organization`, {
          joinCode: this.orgCode,
          id: this.user.id
        })
        .then((response) => {
          if (response.data.code == 200) {
            this.isAddingNewOrganization = false
            this.orgCode = ''
            this.showAddOrganization = true
            this.user = response.data.user
            this.userOrgs = response.data.orgs
          } else {
            this.isAddingNewOrganization = false
            this.showAddNewOrganizationError = true
            this.organizationAddStatus = response.data.error
          }
        })
        .catch((error) => {
          this.isAddingNewOrganization = false
            this.showAddNewOrganizationError = true
            this.organizationAddStatus = response.data.error
        })
    },
    async saveVideoCompletion(video) {

      var courseVideos = this.course_modules
        .flatMap((module) => module.videos)
        .map((module) => module.id)
      var completedVideos = this.user_course.completed_videos.map((module) => module.id)

      this.percent_complete = this.compareArrays(courseVideos, completedVideos)

      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/save-video-progress`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          video: video
        }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then((response) => {
          if (response.data.code == 404) {
            return alert(response.data.error)
          }

          if (response.data.code == 200) {
            this.user_course.completed_videos.push(video)
          }

          const lastModuleComplete =
            this.module_history.filter((module) => !module.isComplete).length == 0
          const isCourseNotCheer =
            this.selected_course.id != '4CBED23C-C7FA-49F3-A29C-CD1A5B21CBD3' &&
            this.selected_course.id != '2262D2BD-ED0D-41D3-B738-0B9D8B4DA1D6'
          const isSurveyEmpty = this.user_course.survey == null

          var courseVideos = this.course_modules
        .flatMap((module) => module.videos)
        .map((module) => module.id)
      var completedVideos = this.user_course.completed_videos.map((module) => module.id)
      var courseQuizzes = this.selected_course.modules
      .flatMap((module) => module.quiz)
      .map((module) => module.id)
      var completedQuizzes = this.uniq(this.user_course.quiz_attempts.map((module) => module.id))

      this.percent_complete = this.compareArrays(courseVideos, courseQuizzes, completedVideos, completedQuizzes)
      
          console.log('Video Saved')
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    resolveQuestionnaireCourseId (source = {}) {
      const raw =
        source.courseId ??
        source.course_id ??
        source.completedCourseId ??
        source.completed_course_id ??
        null
      if (raw == null || raw === '') {
        return null
      }
      return raw
    },
    async checkForQuestionnaire(triggerContext = {}) {
      console.log('Checking for Questionnaire')
      const justCompletedCourse = !!triggerContext.justCompletedCourse
      const justCompletedLearningActivity = !!triggerContext.justCompletedLearningActivity
      const completedCourseId = justCompletedCourse
        ? (this.resolveQuestionnaireCourseId(triggerContext) ?? this.selected_course?.id ?? null)
        : null
      const completedActivityId = justCompletedLearningActivity
        ? (triggerContext.activityId ?? triggerContext.activity_id ?? this.selectedLearningActivity?.activity_id ?? null)
        : null
      const body = {
        userId: this.user.id,
        userType: 'learner',
        courseProgress: triggerContext.courseProgress || 0,
        justCompletedCourse,
        justCompletedLearningActivity,
        registrationDate: this.user.registrationDate || null,
      }
      /** Course-completion questionnaires are scoped per course; send the id so the server does not infer the latest completion. */
      if (justCompletedCourse && completedCourseId != null) {
        body.courseId = completedCourseId
        body.course_id = completedCourseId
        body.completedCourseId = completedCourseId
      }
      /** Learning-activity surveys are scoped per activity, the same way course surveys are scoped per course. */
      if (justCompletedLearningActivity && completedActivityId != null) {
        body.activityId = completedActivityId
        body.activity_id = completedActivityId
        body.completedActivityId = completedActivityId
        const activityName = triggerContext.activityName || this.selectedLearningActivity?.name || ''
        if (activityName) {
          body.activityName = activityName
          body.activity_name = activityName
          body.completedActivityName = activityName
        }
      }

      const response = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-active-questionnaires`, body);

      if (response.data.questionnaire && justCompletedLearningActivity) {
        const returnedActivityId =
          response.data.completedActivityId ??
          response.data.activityId ??
          response.data.questionnaire?.completedActivityId ??
          response.data.questionnaire?.activityId ??
          completedActivityId
        const returnedActivityName =
          response.data.completedActivityName ??
          response.data.activity_name ??
          response.data.questionnaire?.completedActivityName ??
          response.data.questionnaire?.activity_name ??
          triggerContext.activityName ??
          this.selectedLearningActivity?.name ??
          null
        this.activeQuestionnaire = {
          ...response.data.questionnaire,
          ...(returnedActivityId != null
            ? { completedActivityId: returnedActivityId, activityId: returnedActivityId }
            : {}),
          ...(returnedActivityName != null && returnedActivityName !== ''
            ? { completedActivityName: returnedActivityName, activity_name: returnedActivityName }
            : {}),
        }
        this.showLearnerQuestionnaire = true
        return
      }

      if (response.data.questionnaire) {
        const returnedCourseId =
          this.resolveQuestionnaireCourseId(response.data) ??
          this.resolveQuestionnaireCourseId(response.data.questionnaire) ??
          completedCourseId
        const returnedCourseName =
          response.data.completedCourseName ??
          response.data.completed_course_name ??
          response.data.course_name ??
          response.data.questionnaire?.completedCourseName ??
          response.data.questionnaire?.course_name ??
          (justCompletedCourse ? this.selected_course?.name : null) ??
          null
        this.activeQuestionnaire = {
          ...response.data.questionnaire,
          ...(returnedCourseId != null
            ? { completedCourseId: returnedCourseId, courseId: returnedCourseId }
            : {}),
          ...(returnedCourseName != null && returnedCourseName !== ''
            ? { completedCourseName: returnedCourseName }
            : {}),
        }
        this.showLearnerQuestionnaire = true;
        return
      }

      if (justCompletedCourse || justCompletedLearningActivity) {
        this.activeQuestionnaire = null
        this.showLearnerQuestionnaire = false
      }
    },
    async saveQuizAttempt(index, attempt) {

      this.isLoadingQuiz = true

      var courseVideos = this.course_modules
        .flatMap((module) => module.videos)
        .map((module) => module.id)
      var completedVideos = this.user_course.completed_videos.map((module) => module.id)
      var courseQuizzes = this.selected_course.modules
      .flatMap((module) => module.quiz)
      .map((module) => module.id)
      var completedQuizzes = this.uniq(this.user_course.quiz_attempts.map((module) => module.id))

      this.percent_complete = this.compareArrays(courseVideos + courseQuizzes, completedVideos + completedQuizzes)

      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/save-quiz-progress`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          attempt: attempt,
          didCompleteModule: this.quiz_score >= this.quizSettings.passingScore,
          module_id: this.module_history[index].id
        }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then((response) => {
          if (response.data.code == 404) {
            return alert(response.data.error)
          }
          if (response.data.code == 200) {

            if (this.quiz_score >= this.quizSettings.passingScore) {
              // Update module history
              this.module_history[index].dateCompleted = moment(String(Date())).format('MM/DD/YYYY hh:mm:ss a')
              this.module_history[index].timestamp = Math.floor(Date.now() / 1000)
              this.module_history[index].timestampCompleted = Math.floor(Date.now() / 1000)
              this.module_history[index].isComplete = true
              this.module_history[index].activeQuiz = null
              
              // Update completed modules
              this.completed_modules.push(this.current_module.id)

              if (this.module_history.filter(module => !module.isComplete).length != 0) {
                  var currentModuleID = this.module_history.filter(module => !module.isComplete)[0].id
                  this.active_module = this.course_modules.filter(module => module.id == currentModuleID)[0];
              }

          }

            this.user_course.quiz_attempts.push(attempt)
            var moduleHistoryIndex = this.module_history.findIndex((obj => obj.id == this.current_module.id))
            this.module_history[moduleHistoryIndex].activeQuiz = null
            
            this.user.quizRequests = response.data.quizRequests
            this.saveModuleProgress(this.module_history[moduleHistoryIndex])
            this.emitter.emit('quizAttemptSaved', { lastScore: this.quiz_score });
            this.isLoadingQuiz = false

          }
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    async saveModuleProgress(module) {

      // Calculate percent complete
      this.percent_complete = this.calculateCoursePercentComplete()
      this.percent_complete = this.capPercentCompleteForCourseModuleCount(
        this.percent_complete,
        this.selected_course?.modules?.length
      )

      console.log('Saving Module Progress')
      // Save module progress
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/save-module-progress`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          module: module,
          percent_complete: this.percent_complete
        }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then(async (response) => {
          if (response.data.code == 404) {
            return alert(response.data.error)
          }
          if (response.data.code == 200) {
            
            // Update completion date if the course is now complete
            if (response.data.completionDate && response.data.completionDate != null) {
              this.user_course.completionDate = response.data.completionDate
            }

            if (response.data.justCompleted == true) {
              console.log('Just Completed Course')
              await this.checkForQuestionnaire({
                justCompletedCourse: true,
                courseId: this.selected_course?.id,
              })
            }
            
            this.broadcastUpdate()
            console.log('Module Saved')
          }
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    async submitKnowledgeCheckAttempt({ moduleId, knowledgeCheckId, attempt, retainCompletion = false }) {
      if (!userCourseHasKnowledgeChecks(this.user_course)) {
        return
      }
      if (moduleId == null || !knowledgeCheckId || !attempt) {
        return
      }
      const attemptBody = { ...attempt }
      delete attemptBody.retainCompletion
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/submit-knowledge-check`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          module_id: moduleId,
          knowledge_check_id: knowledgeCheckId != null ? knowledgeCheckId : null,
          knowledge_check_type: attemptBody.type,
          passed: !!attemptBody.passed,
          score: Number(attemptBody.score || 0),
          total: Number(attemptBody.total || 0),
          retain_completion: !!retainCompletion,
          attempt: attemptBody
        }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then((response) => {
          const checks = response?.data?.knowledge_checks || response?.data?.knowledgeChecks || null
          if (Array.isArray(checks)) {
            this.user_course = {
              ...this.user_course,
              knowledge_checks: checks
            }
          }
          const u = response?.data?.userCourse || response?.data?.user_course || null
          if (u && typeof u === 'object') {
            this.user_course = {
              ...this.user_course,
              ...this.extractUserCourseData(u)
            }
          }
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Instructor: issue or refresh the learner join code for the current group watch (call after start-group-watch or when resuming).
     * POST /create-group-watch-knowledge-session
     * Body: { groupWatchId, moduleId, courseId } — optional knowledgeCheckId, sessionCode (client preview; server may honor or replace).
     * Response: join code may appear as session_code / sessionCode, groupWatchSessionCode, join_code / joinCode.
     */
    async createGroupWatchKnowledgeSession ({ groupWatchId, moduleId, courseId, knowledgeCheckId, sessionCode }) {
      if (!groupWatchId || !moduleId || !courseId) {
        return null
      }
      const body = { groupWatchId, moduleId, courseId }
      if (knowledgeCheckId != null) {
        body.knowledgeCheckId = knowledgeCheckId
      }
      const sc = sessionCode != null ? String(sessionCode).trim().toUpperCase() : ''
      if (sc) {
        body.sessionCode = sc
      }
      const { data } = await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/create-group-watch-knowledge-session`,
        body,
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
      const returned =
        data?.groupWatchSessionCode ??
        data?.group_watch_session_code ??
        data?.session_code ??
        data?.sessionCode ??
        data?.join_code ??
        data?.joinCode ??
        null
      let fromGenericCode = null
      const gc =
        data?.code ??
        null
      if (gc != null && isPlausibleJoinCodeString(gc)) {
        fromGenericCode = String(gc).trim().toUpperCase()
      }
      const normalizedRaw =
        returned != null && String(returned).trim() !== ''
          ? String(returned).trim().toUpperCase()
          : ''
      const normalized =
        normalizedRaw && isPlausibleJoinCodeString(normalizedRaw) ? normalizedRaw : ''
      // Prefer the client-issued code when sent; otherwise use API join fields only if plausible (avoid
      // treating REST error `{ code: 400 }` as a join code). Do not overwrite good resume-from-document codes.
      const resolved =
        sc ||
        normalized ||
        fromGenericCode ||
        null
      if (resolved != null && isPlausibleJoinCodeString(resolved)) {
        this.groupWatchKnowledgeSessionCode = resolved
        const orgId = this.organization?.companyID
        if (orgId && courseId && moduleId) {
          setGroupWatchKnowledgePreviewCode(orgId, courseId, moduleId, resolved)
        }
      }
      const out = this.groupWatchKnowledgeSessionCode
      return out != null ? { code: out } : null
    },
    /** New random code for the pre-watch attendance banner (session-scoped, not localStorage). */
    refreshInstructorPendingGroupWatchSessionCode () {
      this.instructorPendingGroupWatchSessionCode = generateGroupWatchKnowledgeSessionCode()
    },
    clearInstructorPendingGroupWatchSessionCode () {
      this.instructorPendingGroupWatchSessionCode = null
    },
    /**
     * Instructor: set which knowledge check learners see when they join with the current session code.
     * POST /set-active-group-watch-knowledge-session
     * Body: { groupWatchId, knowledgeCheckId }
     * Response: may include { session_code } (or { code }) to echo the same join code—store it if present.
     */
    async setActiveGroupWatchKnowledgeSession ({ groupWatchId, knowledgeCheckId, restart = false }) {
      if (!groupWatchId || knowledgeCheckId == null) {
        return
      }
      const body = { groupWatchId, knowledgeCheckId }
      if (restart) {
        body.restart = true
      }
      const { data } = await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/set-active-group-watch-knowledge-session`,
        body,
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
      const code =
        data?.session_code ??
        data?.sessionCode ??
        data?.code ??
        data?.join_code ??
        data?.joinCode ??
        null
      if (code != null && this.groupWatchKnowledgeSessionCode == null) {
        this.groupWatchKnowledgeSessionCode = String(code).trim().toUpperCase()
      }
    },
    /**
     * Instructor: clear the live knowledge-check activity (learners return to “waiting” until another KC is set).
     * POST /set-active-group-watch-knowledge-session
     * Body: { groupWatchId, knowledgeCheckId: null, clearActive: true } — backend may read either field.
     */
    async clearActiveGroupWatchKnowledgeSession ({ groupWatchId }) {
      if (!groupWatchId) {
        return
      }
      const { data } = await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/set-active-group-watch-knowledge-session`,
        {
          groupWatchId,
          knowledgeCheckId: null,
          clearActive: true
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
      const code =
        data?.session_code ??
        data?.sessionCode ??
        data?.code ??
        data?.join_code ??
        data?.joinCode ??
        null
      if (code != null && this.groupWatchKnowledgeSessionCode == null) {
        this.groupWatchKnowledgeSessionCode = String(code).trim().toUpperCase()
      }
    },
    /**
     * Learner: load knowledge check payload for a live session code (also used for polling).
     * POST /join-group-watch-knowledge-session
     * Body: { code }
     * Response options:
     * - { knowledgeCheck, ... } active KC for learners
     * - { waiting: true } code valid, instructor has not set an activity yet (keep polling)
     * - May include resolution flags: skippedByInstructor, completedByInstructor, learnerMessage (see parseJoinGroupWatchLearnerResolution)
     * - May include learnerAlreadySubmitted when this user already completed this KC for the session (rejoin / refresh)
     * - Neither → treat as invalid / ended (returns null)
     * - Roster / access denied → returns `{ notAllowed: true, notAllowedReason, ... }` (no polling); backend may use 403 or body flags (`notOnRoster`, etc.).
     */
    async joinGroupWatchKnowledgeSession ({ code }) {
      const c = String(code || '').trim().toUpperCase()
      if (!c) {
        return null
      }
      let data
      try {
        const res = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/join-group-watch-knowledge-session`,
          { code: c },
          { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        )
        data = res.data
      } catch (e) {
        const status = e?.response?.status
        const body = e?.response?.data
        if (status === 403 && body && typeof body === 'object' && isJoinGroupWatchRosterDenied(body)) {
          return joinGroupWatchKnowledgeDeniedResult(body)
        }
        if (status === 403) {
          return joinGroupWatchKnowledgeDeniedResult(body || {})
        }
        throw e
      }
      if (isJoinGroupWatchRosterDenied(data)) {
        return joinGroupWatchKnowledgeDeniedResult(data)
      }
      const knowledgeCheck = data?.knowledgeCheck ?? data?.knowledge_check ?? null
      let waiting = !!(data?.waiting ?? data?.waiting_for_instructor ?? data?.waitingForInstructor)
      if (!knowledgeCheck && !waiting) {
        const stillInSession =
          data?.groupWatchId != null ||
          data?.group_watch_id != null ||
          data?.sessionActive === true ||
          data?.session_active === true ||
          data?.codeValid === true ||
          data?.code_valid === true
        if (stillInSession) {
          waiting = true
        } else {
          return null
        }
      }
      const submissionState = parseJoinGroupWatchLearnerSubmissionState(data)
      const resolutionRaw = parseJoinGroupWatchLearnerResolution(data)
      // Self-submission and instructor skip are mutually exclusive in the UI; resumed payloads often echo both.
      const resolution = submissionState.learnerAlreadySubmitted
        ? { ...resolutionRaw, skippedByInstructor: false, completedByInstructor: false }
        : resolutionRaw
      return {
        knowledgeCheck,
        waiting,
        moduleId: data?.moduleId ?? data?.module_id ?? null,
        courseId: data?.courseId ?? data?.course_id ?? null,
        groupWatchId: data?.groupWatchId ?? data?.group_watch_id ?? null,
        lastKnowledgeCheckId: data?.lastKnowledgeCheckId ?? data?.last_knowledge_check_id ?? null,
        activityAttemptId: parseGroupWatchActivityAttemptId(data),
        ...resolution,
        ...submissionState
      }
    },
    setLearnerGroupWatchKnowledgeJoin (code) {
      const c = code != null ? String(code).trim().toUpperCase() : ''
      this.learnerGroupWatchKnowledgeJoinCode = c.length ? c : null
    },
    clearLearnerGroupWatchKnowledgeJoin () {
      this.learnerGroupWatchKnowledgeJoinCode = null
    },
    /**
     * Ping join endpoint when learner may be blocking course KC submission. Clears stored code if session ended.
     * @returns {Promise<boolean>} true if course KC submit should be blocked (still in a live session)
     */
    async shouldBlockCourseKnowledgeCheckSubmit () {
      const code = this.learnerGroupWatchKnowledgeJoinCode
      if (!code) {
        return false
      }
      try {
        const live = await this.joinGroupWatchKnowledgeSession({ code })
        if (!live || live.notAllowed) {
          this.clearLearnerGroupWatchKnowledgeJoin()
          return false
        }
        return true
      } catch (e) {
        const status = e?.response?.status
        if (status === 404 || status === 410 || status === 403) {
          this.clearLearnerGroupWatchKnowledgeJoin()
          return false
        }
        return true
      }
    },
    /**
     * Learner: send attempt payload after Submit so the instructor dashboard can show live counts.
     * POST /submit-group-watch-knowledge-response
     * Body: { code, knowledgeCheckId, submission } — submission mirrors KnowledgeCheckActivity @submitted payload.
     */
    async submitGroupWatchKnowledgeResponse ({ code, knowledgeCheckId, submission }) {
      const c = String(code || '').trim().toUpperCase()
      if (!c || knowledgeCheckId == null || !submission) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/submit-group-watch-knowledge-response`,
        {
          code: c,
          knowledgeCheckId,
          submission
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    /**
     * Learner: keep session presence fresh for facilitator joinedLearners TTL (recommended ~60s while tab visible).
     * POST /group-watch-knowledge-session-heartbeat
     */
    async sendGroupWatchKnowledgeSessionHeartbeat ({ code, groupWatchId }) {
      const c = code != null ? String(code).trim().toUpperCase() : ''
      const body = {}
      if (c) {
        body.code = c
        body.session_code = c
      }
      if (groupWatchId != null && String(groupWatchId).trim() !== '') {
        body.groupWatchId = groupWatchId
      }
      if (!Object.keys(body).length) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/group-watch-knowledge-session-heartbeat`,
        body,
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    /**
     * Learner: remove Group Watch Sessions presence immediately when leaving the UI.
     * POST /group-watch-knowledge-session-leave
     */
    async leaveGroupWatchKnowledgeSession ({ code, groupWatchId }) {
      const c = code != null ? String(code).trim().toUpperCase() : ''
      const body = {}
      if (c) {
        body.code = c
        body.session_code = c
      }
      if (groupWatchId != null && String(groupWatchId).trim() !== '') {
        body.groupWatchId = groupWatchId
      }
      if (!Object.keys(body).length) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/group-watch-knowledge-session-leave`,
        body,
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    /**
     * Instructor: learners who used the join code but are not on the group watch roster (backend-recorded).
     * POST /group-watch-knowledge-blocked-join-attempts
     * Body: { groupWatchId }
     * Response: { attempts: [{ userId, name?, email?, attemptedAt? }] } (or array-only — normalized below)
     */
    async fetchGroupWatchBlockedJoinAttempts ({ groupWatchId }) {
      const id = groupWatchId != null ? String(groupWatchId).trim() : ''
      if (!id) {
        return []
      }
      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/group-watch-knowledge-blocked-join-attempts`,
          { groupWatchId: id },
          { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        )
        const raw = data?.attempts ?? data?.blockedAttempts ?? data?.blocked_attempts ?? data?.rows ?? data
        const list = Array.isArray(raw) ? raw : []
        return list.map((row) => ({
          userId: row.userId ?? row.user_id ?? row.id,
          name: row.name ?? row.displayName ?? row.display_name ?? '',
          email: row.email ?? '',
          attemptedAt: row.attemptedAt ?? row.attempted_at ?? row.lastAttemptAt ?? null
        }))
      } catch (e) {
        if (e?.response?.status === 404) {
          return []
        }
        console.warn('fetchGroupWatchBlockedJoinAttempts', e)
        return []
      }
    },
    /**
     * Instructor: poll aggregate response state for the active interactive knowledge check.
     * POST /group-watch-knowledge-facilitator-state
     * Body: { groupWatchId, knowledgeCheckId }
     * Response (example): {
     *   phase: 'collecting' | 'revealed',
     *   expectedLearnerCount, respondedCount,
     *   respondedLearners: [{ id, name }], pendingLearners, skippedLearners,
     *   aggregates:
     *     quiz — { type: 'quiz', questions: [{ optionCounts: [n0,n1,...] }] }
     *     dnd  — { type: 'dnd', rows: [{ prompt?, choiceLabels?, optionCounts?, choices?: [{ label, count, correct? }] }] }
     *     ftb  — { type: 'ftb', blanks: [{ expected?, choiceLabels?, optionCounts?, choices?: [{ label, count, correct? }] }] }
     *   allAnsweredOrSkipped: boolean,
     *   perLearnerResults: [{ userId, name, score, total, passed, summary? }] // when revealed
     *   joinedLearners?: [{ userId|id, name?, email?, joinedAt?, lastSeenAt? }] — always returned when presence is implemented (possibly [])
     *   joinedLearnersPresenceReported?: true — explicit signal (preferred); UI also treats joinedLearners key as reported.
     * }
     */
    async fetchGroupWatchKnowledgeFacilitatorState ({ groupWatchId, knowledgeCheckId }) {
      if (!groupWatchId || knowledgeCheckId == null) {
        return null
      }
      const { data } = await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/group-watch-knowledge-facilitator-state`,
        {
          groupWatchId,
          knowledgeCheckId
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
      const d = data && typeof data === 'object' ? data : {}
      const nested = d.data && typeof d.data === 'object' && !Array.isArray(d.data) ? d.data : {}
      /**
       * Optional session presence (backend): learners who opened Group Watch Sessions and joined with the live code.
       * If the API omits these fields entirely, the instructor UI shows “session presence not reported”.
       * @see mergeInteractiveFacilitatorApiWithHistory in groupWatchHistory.js
       */
      const joinedRaw =
        d.joinedLearners ??
        d.joined_learners ??
        nested.joinedLearners ??
        nested.joined_learners
      const presenceExplicit =
        d.joinedLearnersPresenceReported === true ||
        d.joined_learners_presence_reported === true ||
        nested.joinedLearnersPresenceReported === true ||
        nested.joined_learners_presence_reported === true
      const sessionJoinKeysFromPayload =
        d.hasOwnProperty('joinedLearners') ||
        d.hasOwnProperty('joined_learners') ||
        nested.hasOwnProperty('joinedLearners') ||
        nested.hasOwnProperty('joined_learners')
      const sessionJoinDataReported =
        presenceExplicit ||
        (sessionJoinKeysFromPayload && joinedRaw !== undefined && joinedRaw !== null)
      const joinedLearnersNormalized = sessionJoinDataReported
        ? (Array.isArray(joinedRaw) ? joinedRaw : [])
        : undefined
      const facilitatorEnvelope =
        (d.facilitatorState && typeof d.facilitatorState === 'object' ? d.facilitatorState : null) ??
        (d.facilitator_state && typeof d.facilitator_state === 'object' ? d.facilitator_state : null) ??
        (nested.facilitatorState && typeof nested.facilitatorState === 'object' ? nested.facilitatorState : null) ??
        (nested.facilitator_state && typeof nested.facilitator_state === 'object' ? nested.facilitator_state : null)
      const gwkState =
        (d.groupWatchKnowledgeFacilitatorState && typeof d.groupWatchKnowledgeFacilitatorState === 'object'
          ? d.groupWatchKnowledgeFacilitatorState
          : null) ??
        (nested.groupWatchKnowledgeFacilitatorState &&
        typeof nested.groupWatchKnowledgeFacilitatorState === 'object'
          ? nested.groupWatchKnowledgeFacilitatorState
          : null)
      const aggregatesRaw =
        d.aggregates ??
        d.aggregate ??
        nested.aggregates ??
        nested.aggregate ??
        (d.results && d.results.aggregates) ??
        (nested.results && nested.results.aggregates) ??
        (d.results && d.results.aggregate) ??
        (nested.results && nested.results.aggregate) ??
        (facilitatorEnvelope && facilitatorEnvelope.aggregates) ??
        (gwkState && gwkState.aggregates) ??
        (d.payload && typeof d.payload === 'object' && d.payload.aggregates) ??
        (nested.payload && typeof nested.payload === 'object' && nested.payload.aggregates) ??
        null
      return {
        knowledgeCheckId:
          d.knowledgeCheckId ??
          d.knowledge_check_id ??
          nested.knowledgeCheckId ??
          nested.knowledge_check_id ??
          knowledgeCheckId,
        phase:
          (d.phase === 'revealed' || nested.phase === 'revealed') ? 'revealed' : 'collecting',
        expectedLearnerCount: Number(
          d.expectedLearnerCount ?? d.expected_learner_count ?? nested.expectedLearnerCount ?? nested.expected_learner_count ?? 0
        ),
        respondedCount: Number(
          d.respondedCount ?? d.responded_count ?? nested.respondedCount ?? nested.responded_count ?? 0
        ),
        aggregates: normalizeGroupWatchFacilitatorAggregates(aggregatesRaw),
        respondedLearners: Array.isArray(d.respondedLearners)
          ? d.respondedLearners
          : Array.isArray(d.responded_learners)
            ? d.responded_learners
            : [],
        pendingLearners: Array.isArray(d.pendingLearners)
          ? d.pendingLearners
          : Array.isArray(d.pending_learners)
            ? d.pending_learners
            : [],
        skippedLearners: Array.isArray(d.skippedLearners)
          ? d.skippedLearners
          : Array.isArray(d.skipped_learners)
            ? d.skipped_learners
            : [],
        perLearnerResults: Array.isArray(d.perLearnerResults)
          ? d.perLearnerResults
          : Array.isArray(d.per_learner_results)
            ? d.per_learner_results
            : [],
        allAnsweredOrSkipped: !!(d.allAnsweredOrSkipped ?? d.all_answered_or_skipped),
        joinedLearners: joinedLearnersNormalized,
        sessionJoinDataReported: !!sessionJoinDataReported
      }
    },
    /**
     * Instructor: treat non-responders as skipped so the session can move to reveal/results.
     * POST /skip-unanswered-group-watch-knowledge-responses
     * Body: { groupWatchId, knowledgeCheckId }
     */
    async skipUnansweredGroupWatchKnowledgeResponses ({ groupWatchId, knowledgeCheckId }) {
      if (!groupWatchId || knowledgeCheckId == null) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/skip-unanswered-group-watch-knowledge-responses`,
        {
          groupWatchId,
          knowledgeCheckId
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    /**
     * Instructor: lock in responses and move to revealed phase (correct answers + learner breakdown).
     * POST /reveal-group-watch-knowledge-check
     * Body: { groupWatchId, knowledgeCheckId }
     */
    async revealGroupWatchKnowledgeCheckResults ({ groupWatchId, knowledgeCheckId }) {
      if (!groupWatchId || knowledgeCheckId == null) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/reveal-group-watch-knowledge-check`,
        {
          groupWatchId,
          knowledgeCheckId
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    /**
     * Instructor: start a fresh live attempt for this KC (learners may submit again; group-watch completion stays credited once).
     * POST /restart-group-watch-knowledge-check
     * Body: { groupWatchId, knowledgeCheckId }
     */
    async restartGroupWatchKnowledgeCheck ({ groupWatchId, knowledgeCheckId }) {
      if (!groupWatchId || knowledgeCheckId == null) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/restart-group-watch-knowledge-check`,
        {
          groupWatchId,
          knowledgeCheckId
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    logGroupWatchKnowledgeCheckEvent ({ knowledgeCheckId, title, type, mode }) {
      if (knowledgeCheckId == null) {
        return
      }
      const id = String(knowledgeCheckId)
      const list = [...(this.groupWatchSessionKnowledgeCheckLog || [])]
      const i = list.findIndex((e) => String(e.knowledgeCheckId) === id)
      const normalizedMode = mode === 'interactive' ? 'interactive' : 'instructorLed'
      const row = {
        knowledgeCheckId,
        title: title || 'Knowledge check',
        type: type || '',
        mode: normalizedMode,
        learnersProgressed: false,
        resultsPublished: false,
        sessionResponsesComplete: false,
        instructorLedCompleted: false,
        updatedAt: Date.now()
      }
      if (i >= 0) {
        const prev = list[i]
        list[i] = {
          ...prev,
          ...row,
          learnersProgressed: !!prev.learnersProgressed,
          resultsPublished: !!prev.resultsPublished,
          sessionResponsesComplete: !!prev.sessionResponsesComplete,
          instructorLedCompleted: !!prev.instructorLedCompleted
        }
      } else {
        list.push(row)
      }
      this.groupWatchSessionKnowledgeCheckLog = list
    },
    markGroupWatchKnowledgeCheckLearnersProgressed (knowledgeCheckId) {
      if (knowledgeCheckId == null) {
        return
      }
      const id = String(knowledgeCheckId)
      this.groupWatchSessionKnowledgeCheckLog = (this.groupWatchSessionKnowledgeCheckLog || []).map((e) =>
        String(e.knowledgeCheckId) === id ? { ...e, learnersProgressed: true, updatedAt: Date.now() } : e
      )
    },
    markGroupWatchKnowledgeCheckResultsPublished (knowledgeCheckId) {
      if (knowledgeCheckId == null) {
        return
      }
      const id = String(knowledgeCheckId)
      this.groupWatchSessionKnowledgeCheckLog = (this.groupWatchSessionKnowledgeCheckLog || []).map((e) =>
        String(e.knowledgeCheckId) === id
          ? { ...e, resultsPublished: true, sessionResponsesComplete: true, updatedAt: Date.now() }
          : e
      )
    },
    /** Interactive: everyone submitted or skipped — counts as completed for End Group Watch summary (separate from sharing results). */
    markGroupWatchKnowledgeCheckSessionResponsesComplete (knowledgeCheckId) {
      if (knowledgeCheckId == null) {
        return
      }
      const id = String(knowledgeCheckId)
      this.groupWatchSessionKnowledgeCheckLog = (this.groupWatchSessionKnowledgeCheckLog || []).map((e) =>
        String(e.knowledgeCheckId) === id ? { ...e, sessionResponsesComplete: true, updatedAt: Date.now() } : e
      )
    },
    /** Instructor-led: you finished the activity on your screen; progression applies when group watch ends. */
    markGroupWatchKnowledgeCheckInstructorLedCompleted (knowledgeCheckId) {
      if (knowledgeCheckId == null) {
        return
      }
      const id = String(knowledgeCheckId)
      this.groupWatchSessionKnowledgeCheckLog = (this.groupWatchSessionKnowledgeCheckLog || []).map((e) =>
        String(e.knowledgeCheckId) === id ? { ...e, instructorLedCompleted: true, updatedAt: Date.now() } : e
      )
    },
    /**
     * Instructor: persist instructor-led KC completion for this group watch (resume / history / parity with learner APIs).
     * POST /record-group-watch-instructor-led-knowledge-check-complete
     * Body: { groupWatchId, moduleId, courseId, knowledgeCheckId }
     */
    async recordGroupWatchInstructorLedKnowledgeCheckComplete ({ groupWatchId, moduleId, courseId, knowledgeCheckId }) {
      if (!groupWatchId || !moduleId || !courseId || knowledgeCheckId == null) {
        return
      }
      await axios.post(
        `${import.meta.env.VITE_BASE_LINK}/record-group-watch-instructor-led-knowledge-check-complete`,
        {
          groupWatchId,
          moduleId,
          courseId,
          knowledgeCheckId
        },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
    },
    clearGroupWatchSessionKnowledgeCheckLog () {
      this.groupWatchSessionKnowledgeCheckLog = []
    },
    /** Replace in-session KC log (e.g. resume group watch from history when the watch document carries a persisted log). */
    replaceGroupWatchSessionKnowledgeCheckLog (entries) {
      this.groupWatchSessionKnowledgeCheckLog = Array.isArray(entries) ? entries.map((e) => ({ ...e })) : []
    },
    async updateModuleHistory() {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/update-module-history`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          module_history: this.module_history,
          completed_modules: this.completed_modules
        })
        .then((response) => {
          console.log('Module History Updated')
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    async updateRole() {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/update-role`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          userType: this.userType
        })
        .then((response) => {
          console.log('Role Updated')
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /** All videos in this module marked complete for the learner (for “only KCs left” routing). */
    moduleVideosAllCompleted (module) {
      if (!module || !Array.isArray(module.videos)) {
        return true
      }
      if (!module.videos.length) {
        return true
      }
      const done = new Set(
        (Array.isArray(this.user_course?.completed_videos) ? this.user_course.completed_videos : [])
          .filter((md) => md && String(md.module_id) === String(module.id))
          .map((v) => String(v.id))
      )
      return module.videos.every((vid) => vid && done.has(String(vid.id)))
    },
    /** True when the learner may open the module quiz (required videos and knowledge checks done). */
    canAccessModuleQuiz (module) {
      if (!module || module.id == null) {
        return false
      }
      const courseMod =
        this.selected_course?.modules?.find((m) => String(m.id) === String(module.id)) ||
        this.course_modules?.find((m) => String(m.id) === String(module.id)) ||
        module
      if (!this.moduleVideosAllCompleted(courseMod)) {
        return false
      }
      return this.moduleHasAllKnowledgeChecksComplete(courseMod)
    },
    syncModuleHistoryKnowledgeCheckFocus (moduleId, knowledgeCheckId) {
      const mh = this.module_history.findIndex((o) => String(o.id) === String(moduleId))
      if (mh === -1) {
        return
      }
      this.module_history[mh].lastActivity = 'knowledgeCheck'
      this.module_history[mh].lastKnowledgeCheckId = knowledgeCheckId != null ? knowledgeCheckId : null
    },
    /**
     * When the UI is on the knowledge-check view, ensure `current_knowledge_check_id` resolves to a row
     * in this module (fixes blank main panel when history had `knowledgeCheck` but no `lastKnowledgeCheckId`).
     */
    ensureCurrentKnowledgeCheckIdForModule (module) {
      if (!module?.id || this.currentCourseView !== 'knowledgeCheck') {
        return
      }
      const rows = buildModuleSidebarItems(
        module,
        buildModuleSidebarOptions(this.user_course)
      )
      const kcRows = rows.filter((r) => r.kind === 'knowledgeCheck' && r.knowledgeCheckId != null)
      if (!kcRows.length) {
        this.current_knowledge_check_id = null
        return
      }
      const curId = this.current_knowledge_check_id
      const curRow = curId != null
        ? kcRows.find((r) => String(r.knowledgeCheckId) === String(curId))
        : null
      if (curRow && !this.isKnowledgeCheckComplete(module.id, curRow.knowledgeCheckId)) {
        return
      }
      const firstIncomplete = kcRows.find((r) => !this.isKnowledgeCheckComplete(module.id, r.knowledgeCheckId))
      if (firstIncomplete) {
        this.current_knowledge_check_id = firstIncomplete.knowledgeCheckId
        this.syncModuleHistoryKnowledgeCheckFocus(module.id, firstIncomplete.knowledgeCheckId)
        return
      }
      if (!curRow) {
        this.current_knowledge_check_id = kcRows[0].knowledgeCheckId
        this.syncModuleHistoryKnowledgeCheckFocus(module.id, kcRows[0].knowledgeCheckId)
      }
    },
    /**
     * If every video in the module is done and at least one knowledge check is still incomplete,
     * open that check instead of leaving the learner on the player with nothing obvious to do.
     */
    maybeOpenKnowledgeCheckWhenPlayerWorkDone (module) {
      if (!module?.id || this.currentCourseView !== 'player') {
        return
      }
      if (!this.moduleVideosAllCompleted(module)) {
        return
      }
      const rows = buildModuleSidebarItems(
        module,
        buildModuleSidebarOptions(this.user_course)
      )
      const firstIncomplete = rows.find((r) =>
        r.kind === 'knowledgeCheck' &&
        r.knowledgeCheckId != null &&
        !this.isKnowledgeCheckComplete(module.id, r.knowledgeCheckId)
      )
      if (!firstIncomplete) {
        return
      }
      this.current_knowledge_check_id = firstIncomplete.knowledgeCheckId
      this.current_video = null
      this.currentCourseView = 'knowledgeCheck'
      this.syncModuleHistoryKnowledgeCheckFocus(module.id, firstIncomplete.knowledgeCheckId)
    },
    syncCurrentModuleIndex (moduleId) {
      if (moduleId == null) {
        this.currentModuleIndex = 0
        return
      }
      const moduleHistoryIndex = this.module_history.findIndex((obj) => obj.id == moduleId)
      this.currentModuleIndex = moduleHistoryIndex >= 0 ? moduleHistoryIndex + 1 : 0
    },
    resolveCourseModuleForActivity (module) {
      if (!module || module.id == null) {
        return null
      }
      return (
        this.selected_course?.modules?.find((m) => String(m.id) === String(module.id)) ||
        this.course_modules?.find((m) => String(m.id) === String(module.id)) ||
        module
      )
    },
    resolveFirstIncompleteModuleActivity (courseMod) {
      if (!courseMod?.id) {
        return { activity: 'player', video: null, knowledgeCheckId: null }
      }
      const rows = buildModuleSidebarItems(
        courseMod,
        buildModuleSidebarOptions(this.user_course)
      )
      for (const row of rows) {
        if (row.kind === 'video' && row.video) {
          const done = (this.user_course?.completed_videos || []).some(
            (entry) =>
              entry &&
              String(entry.module_id) === String(courseMod.id) &&
              String(entry.id) === String(row.video.id)
          )
          if (!done) {
            return { activity: 'player', video: row.video, knowledgeCheckId: null }
          }
        }
        if (row.kind === 'knowledgeCheck' && row.knowledgeCheckId != null) {
          if (!this.isKnowledgeCheckComplete(courseMod.id, row.knowledgeCheckId)) {
            return {
              activity: 'knowledgeCheck',
              video: null,
              knowledgeCheckId: row.knowledgeCheckId,
            }
          }
        }
      }
      if (this.canAccessModuleQuiz(courseMod)) {
        return { activity: 'quiz', video: null, knowledgeCheckId: null }
      }
      const videos = courseMod.videos || []
      return { activity: 'player', video: videos[0] || null, knowledgeCheckId: null }
    },
    prepareModuleQuizQuestions(questions) {
      return orderModuleQuizQuestions(questions)
    },
    applyModuleEntryFromHistory (module, moduleHistoryIndex) {
      if (moduleHistoryIndex === -1 || !module) {
        return
      }
      const historyRow = this.module_history[moduleHistoryIndex]
      if (!historyRow) {
        return
      }
      const courseMod = this.resolveCourseModuleForActivity(module)
      let lastActivity = historyRow.lastActivity

      if (lastActivity === 'quiz' && !this.canAccessModuleQuiz(courseMod)) {
        const fallback = this.resolveFirstIncompleteModuleActivity(courseMod)
        lastActivity = fallback.activity
        if (fallback.activity === 'player') {
          historyRow.lastActivity = 'player'
          if (fallback.video) {
            historyRow.lastActiveVideo = fallback.video
          }
        } else if (fallback.activity === 'knowledgeCheck') {
          historyRow.lastActivity = 'knowledgeCheck'
          historyRow.lastKnowledgeCheckId = fallback.knowledgeCheckId
        }
      }

      if (lastActivity === 'knowledgeCheck') {
        this.current_knowledge_check_id = historyRow.lastKnowledgeCheckId || null
        this.current_video = null
        this.currentCourseView = 'knowledgeCheck'
      } else if (lastActivity === 'player') {
        this.current_knowledge_check_id = null
        const videos = courseMod?.videos || []
        let video = historyRow.lastActiveVideo
        if (!video || !videos.some((v) => v && String(v.id) === String(video.id))) {
          video = videos[0] || null
        }
        this.current_video = video
        this.currentCourseView = 'player'
      } else if (lastActivity === 'quiz') {
        this.current_knowledge_check_id = null
        if (historyRow.activeQuiz != null) {
          this.current_quiz_questions = historyRow.activeQuiz
          this.showResults = false
        } else {
          this.showResults = true
        }
        this.currentCourseView = 'quiz'
      }
    },
    selectModule(module) {
      if (!module) {
        this.currentCourseMode = 'home'
        this.currentCourseView = 'progress'
        this.current_video = null
        return
      }

      // Find the index of the module in the module history array
      var moduleHistoryIndex = this.module_history.findIndex((obj) => obj.id == module.id)
      this.syncCurrentModuleIndex(module.id)

      // Check to see if module is allowed to be accessed
      if (this.currentUserOrganization && Object.keys(this.currentUserOrganization).length > 0 && this.currentUserOrganization.futureModules) {
        for (let i = 0; i < this.currentUserOrganization.futureModules.length; i++) {
          if (this.currentUserOrganization.futureModules[i].moduleId == module.id) {
            if (!this.user_course.completed_modules.includes(module.id) && (this.currentUserOrganization.futureModules[i].releaseDate && new Date(this.currentUserOrganization.futureModules[i].releaseDate) > new Date())) {
              this.currentModalType = 'NotAvailableYet'
              return (this.currentModal = 'ModuleAdvancementModal')
            }
          }
        }
      }
      if (
        !this.user_course.completed_modules.includes(module.id) &&
        this.active_module.id != module.id && this.user_course.sport != 'Cheerleading'
      ) {
        return (this.currentModal = 'ModuleAdvancementModal')
      }

      if (moduleHistoryIndex !== -1) {
        this.applyModuleEntryFromHistory(module, moduleHistoryIndex)
      }

      this.current_module = module
      this.currentCourseMode = 'module'

      var firstModule = this.selected_course.modules[0]
      var middleChallengeModule =
        this.selected_course.modules[Math.ceil(this.selected_course.modules.length / 2) - 1]
      var lastModule = this.selected_course.modules[this.selected_course.modules.length - 1]

      // if (this.isDevLoggedIn || this.isInDevMode) return

      if (this.challenge) {
        var firstModule = this.selected_course.modules[0]
        var middleChallengeModule =
          this.selected_course.modules[Math.ceil(this.selected_course.modules.length / 2) - 1]
        var lastModule = this.selected_course.modules[this.selected_course.modules.length - 1]

        if (module.id == firstModule.id) {
          if (
            !this.challenge.results.map((result) => result.position).includes('first') &&
            !this.user_course.completed_videos
              .map((video) => video.module_id)
              .includes(firstModule.id)
          ) {
            this.displayCourseChallenge = true
          }
        }
        if (module.id == middleChallengeModule.id) {
          if (
            !this.challenge.results.map((result) => result.position).includes('middle') &&
            !this.user_course.completed_videos
              .map((video) => video.module_id)
              .includes(middleChallengeModule.id)
          ) {
            this.displayCourseChallenge = true
          }
        }
        if (module.id == lastModule.id) {
          if (
            !this.challenge.results.map((result) => result.position).includes('last') &&
            !this.user_course.completed_videos
              .map((video) => video.module_id)
              .includes(lastModule.id)
          ) {
            this.displayCourseChallenge = true
          }
        }
      }

      if (this.currentCourseView === 'knowledgeCheck') {
        this.ensureCurrentKnowledgeCheckIdForModule(module)
      } else if (this.currentCourseView === 'player') {
        this.maybeOpenKnowledgeCheckWhenPlayerWorkDone(module)
      }
      if (moduleHistoryIndex !== -1) {
        this.saveModuleProgress(this.module_history[moduleHistoryIndex])
      }
    },
    selectOptionalModule(module) {
      // Find the index of the module in the module history array
      var moduleHistoryIndex = this.module_history.findIndex((obj) => obj.id == module.id)
      this.syncCurrentModuleIndex(module.id)

      if (moduleHistoryIndex !== -1) {
        this.applyModuleEntryFromHistory(module, moduleHistoryIndex)
      }

      this.current_module = module
      this.currentCourseMode = 'module'

      if (this.currentCourseView === 'knowledgeCheck') {
        this.ensureCurrentKnowledgeCheckIdForModule(module)
      } else if (this.currentCourseView === 'player') {
        this.maybeOpenKnowledgeCheckWhenPlayerWorkDone(module)
      }
    },
    isArrayInArray(superset, subset) {
      if (!Array.isArray(superset) || !Array.isArray(subset)) {
        // One or both of the inputs are not arrays
        return false
      }

      return subset.every((val) => superset.includes(val))
    },
    compareArrays(superset, subset) {
      // Convert arrays to sets for easier comparison
      let supersetSet = new Set(superset)
      let subsetSet = new Set(subset)

      // Calculate intersection of the two sets
      let intersection = new Set([...supersetSet].filter((x) => subsetSet.has(x)))

      // If superset is not empty, calculate and return percentage
      if (supersetSet.size) {
        let percentage = (intersection.size / supersetSet.size) * 100
        return percentage
      } else {
        return 0
      }
    },
    /**
     * Same formula as live course progress: intersection of course videos with learner completions,
     * plus required active knowledge checks vs learner KC progress.
     * @param {Array<{ id?: unknown, videos?: unknown[], knowledge_checks?: unknown[] }>} modules
     * @returns {number} 0–100 (not rounded; may be fractional)
     */
    /**
     * @param {unknown[]} modules Course modules (e.g. from `updatedCourse.modules`).
     * @param {object|null} [userCourseLike] Optional learner snapshot (`completed_videos`, `knowledge_checks`).
     *   When omitted, uses `this.user_course` (in-course view).
     */
    calculateCoursePercentCompleteForModules (modules, userCourseLike = null) {
      const uc = userCourseLike != null ? userCourseLike : this.user_course
      const includeKnowledgeChecks = userCourseHasKnowledgeChecks(uc)
      const modList = modulesForLearnerEnrollment(modules, uc)
      const courseVideoIds = modList
        .flatMap((module) => Array.isArray(module.videos) ? module.videos : [])
        .map((video) => {
          if (video == null) return ''
          if (typeof video === 'string' || typeof video === 'number') return String(video)
          return video.id != null ? String(video.id) : ''
        })
        .filter((id) => id !== '')
      const courseVideoIdSet = new Set(courseVideoIds)
      const completedVideoSet = new Set(
        (Array.isArray(uc?.completed_videos) ? uc.completed_videos : [])
          .map((video) => {
            if (video == null) return null
            if (typeof video === 'string' || typeof video === 'number') return String(video)
            return video.id != null ? String(video.id) : null
          })
          .filter((id) => id != null && id !== '')
      )
      const completedVideosInCourse = [...courseVideoIdSet].filter((id) => completedVideoSet.has(id)).length

      let totalRequiredKnowledgeChecks = 0
      let completedRequiredKnowledgeChecks = 0
      if (includeKnowledgeChecks) {
        for (const module of modList) {
          if (!module || module.id == null) {
            continue
          }
          const raw = Array.isArray(module.knowledge_checks) ? module.knowledge_checks : []
          const required = raw
            .map((kc, idx) => ({ kc, idx }))
            .filter(({ kc }) => kc && kc.active !== false && isSupportedKnowledgeCheckType(kc.type))
          totalRequiredKnowledgeChecks += required.length

          const checks = normalizeKnowledgeCheckProgress(uc?.knowledge_checks)
          completedRequiredKnowledgeChecks += required.filter(({ kc, idx }) => {
            if (kc.id == null) return false
            return checks.some((item) =>
              progressIsComplete(item) &&
              progressModuleId(item) != null &&
              progressKnowledgeCheckId(item) != null &&
              String(progressModuleId(item)) === String(module.id) &&
              String(progressKnowledgeCheckId(item)) === String(kc.id)
            )
          }).length
        }
      }

      const totalItems = courseVideoIdSet.size + totalRequiredKnowledgeChecks
      if (!totalItems) {
        return 0
      }
      const completedItems = completedVideosInCourse + completedRequiredKnowledgeChecks
      return (completedItems / totalItems) * 100
    },
    calculateCoursePercentComplete () {
      return this.calculateCoursePercentCompleteForModules(this.course_modules)
    },
    /**
     * Seat-license progress recalc preview (basic/legacy: videos only; premium: videos + KCs).
     * @param {unknown[]} modules
     * @param {object|null} userCourseSnap
     * @param {string|null|undefined} licenseType
     */
    calculateLicenseProgressUpdatePercent(modules, userCourseSnap, licenseType) {
      const raw = calculateProjectedPercentForLicenseType(modules, userCourseSnap, licenseType)
      return this.capPercentCompleteForCourseModuleCount(
        raw,
        Array.isArray(modules) ? modules.length : 0,
        userCourseSnap?.completed_modules
      )
    },
    /**
     * Matches `saveModuleProgress`: if the formula hits 100% but the learner has not yet
     * accumulated every module id in `completed_modules`, cap at 99 until the course is fully marked.
     */
    /**
     * @param {unknown} [completedModulesOverride] Optional `completed_modules` list for the learner
     *   (defaults to store `completed_modules` when in-course).
     */
    capPercentCompleteForCourseModuleCount (percent, courseModuleCount, completedModulesOverride = null) {
      const p = Number(percent) || 0
      if (courseModuleCount == null || !Number.isFinite(Number(courseModuleCount))) {
        return p
      }
      const n = Number(courseModuleCount)
      const completedMods = completedModulesOverride != null ? completedModulesOverride : this.completed_modules
      const completedModIds = (Array.isArray(completedMods) ? completedMods : [])
        .map((m) => {
          if (m == null) return null
          if (typeof m === 'string' || typeof m === 'number') return String(m)
          if (typeof m === 'object' && m.id != null) return String(m.id)
          return null
        })
        .filter((id) => id != null && id !== '')
      if (p === 100 && n > 0 && this.uniq(completedModIds).length !== n) {
        return 99
      }
      return p
    },
    async getOrganizationSettings(companyID) {
      try {
        const response = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-instructor-settings`, {
          companyID: companyID
        });
        this.global_settings = response.data.global_settings;
        this.course_settings = response.data.course_settings;
        this.isLoaded = true;
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    },
    confirmLeavingWebsite(url) {
      if (!url || url.trim() === '') {
          console.error('Invalid URL provided');
          return;
      }
  
      const userConfirmed = confirm("You are now leaving the RefReps platform to visit an outside website.");
      
      if (userConfirmed) {
          try {
              window.open(url, '_blank', 'noopener,noreferrer');
          } catch (error) {
              console.error('Failed to open new window:', error);
              window.location.href = url; // Fallback to current window if popup is blocked
          }
      } else {
          console.log("User cancelled the operation");
      }
  },
  async update_global_settings(settings) {

    this.isSavingSettings = true
    await axios
      .post(`${import.meta.env.VITE_BASE_LINK}/save-instructor-global-settings`, {
        companyID: this.organization.companyID,
        settings: settings,
      })
      .then((response) => {
        if (response.data.code == 200) {
          console.log('Settings Updated')
          this.global_settings = settings
          setTimeout(() => {
            this.didSaveSettings = true
            this.isSavingSettings = false
            setTimeout(() => {
              this.didSaveSettings = false
            }, 2000)
          }, 600)
        } else {
          console.log('Settings Not Updated')
        }
      })
      .catch(function (error) {
        console.log(error)
      })
  },
  async update_course_settings(settings) {

    this.isSavingSettings = true
    await axios
      .post(`${import.meta.env.VITE_BASE_LINK}/save-instructor-course-settings`, {
        companyID: this.organization.companyID,
        courseSettings: settings,
        user_id: this.user.id,
        course_id: this.currentInstructorCourse.id
      })
      .then((response) => {
        if (response.data.code == 200) {
          console.log('Settings Updated')
          setTimeout(() => {
            this.didSaveSettings = true
            this.isSavingSettings = false
            setTimeout(() => {
              this.didSaveSettings = false
            }, 2000)
          }, 600)
        } else {
          console.log('Settings Not Updated')
        }
      })
      .catch(function (error) {
        console.log(error)
      })
  },
    async addNewNote(note) {
      await axios
        .post(`${import.meta.env.VITE_BASE_LINK}/add-new-note`, {
          user_id: this.user.id,
          course_id: this.selected_course.id,
          note: this.current_note
        })
        .then((response) => {
          this.course_notes = response.data.notes
          this.current_note = ''
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    async getOrganizationCertificateHistory(orgId) {
      if (orgId == null || orgId === '') return
      this.isLoadingOrganizationCertificateHistory = true
      try {
        // Mobile Safari can drop an in-flight request when the page updates, and
        // neither then nor catch runs. A timeout guarantees the loading flag clears.
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/retrieve-all-completed-courses-for-organization`,
          { organizationId: orgId },
          { withCredentials: true, timeout: 30000 }
        )
        const rows = response.data?.completedCourses
        this.organizationCertificateHistory = Array.isArray(rows)
          ? rows.filter(course => course.id !== '8710EE19-017A-4848-9509-A353951AB4D8')
          : []
      } catch (e) {
        console.error('getOrganizationCertificateHistory', e)
      } finally {
        this.organizationCertificateHistoryLoaded = true
        this.isLoadingOrganizationCertificateHistory = false
      }
    },
    async getOrganizationPackCompletionHistory(orgId) {
      if (orgId == null || orgId === '') return
      this.isLoadingOrganizationPackCompletionHistory = true
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/retrieve-all-completed-packs-for-organization`,
          { organizationId: orgId },
          { withCredentials: true }
        )
        const rows = response.data?.completedPacks
        this.organizationPackCompletionHistory = Array.isArray(rows) ? rows : []
      } catch (e) {
        console.error('getOrganizationPackCompletionHistory', e)
        this.organizationPackCompletionHistory = []
      } finally {
        this.isLoadingOrganizationPackCompletionHistory = false
      }
    },
    broadcastUpdate() {
      var data = {
        user_id: this.user.id,
        course_id: this.selected_course.id,
        completed_videos: this.completed_videos,
        completed_modules: this.uniq(this.completed_modules),
        completed_quizzes: this.completed_quizzes,
        percent_complete: this.percent_complete,
        current_module: this.current_module,
        quiz_attempts: this.quiz_attempts,
        moduleIndex: this.moduleIndex,
        current_video: this.current_video,
        course_notes: this.course_notes,
        lastActiveVideo: this.lastActiveVideo,
        lastModuleActivity: this.lastModuleActivity,
        current_module: this.current_module,
        module_history: this.module_history,
        module_optional_history: this.module_optional_history,
        active_module: this.active_module,
        userType: this.userType,
        companyID: this.user_course.assignedBy.companyID
          ? this.user_course.assignedBy.companyID
          : null,
        currentCourseView: this.currentCourseView,
        currentCourseMode: this.currentCourseMode,
        course_notes: this.course_notes,
        quiz_score: this.quiz_score,
        current_quiz_questions: this.current_quiz_questions,
        currentLoadingVideoId: this.currentLoadingVideoId,
      }
      this.socket.emit('syncData', {
        data: data,
        id: this.user.id
      })
    },
    getOnlineCourses() {
      this.isLoadingOnlineCourses = true
      // iOS Safari can silently kill in-flight requests when it suspends the page,
      // settling neither handler. Without a timeout the loading flag would stay set
      // and leave a click-blocking overlay over the app.
      axios.post(`${import.meta.env.VITE_BASE_LINK}/getOnlineCourses`, {
        apiKey: import.meta.env.VITE_APIKEY
    }, { timeout: 30000 })
    .then((response) => {
   
        var newCourses = []

        response.data.courses.forEach(course => {
            var editedCourse = course
            editedCourse.productType = 'course'
            editedCourse.author = 'RefReps'
            newCourses.push(editedCourse)
        });

        response.data.repCourses.forEach(course => {
            var editedCourse = course
            editedCourse.productType = 'interactive'
            newCourses.push(editedCourse)
        });

        this.courses = newCourses.sort(function (a, b) {
            var x = a.sport.toLowerCase();
            var y = b.sport.toLowerCase();
            return x < y ? -1 : x > y ? 1 : 0;
        });
        this.onlineStore = newCourses.sort(function (a, b) {
            var x = a.sport.toLowerCase();
            var y = b.sport.toLowerCase();
            return x < y ? -1 : x > y ? 1 : 0;
        });
    })
    .catch((error) => {
        alert(error);
    })
    .finally(() => {
        this.isLoadingOnlineCourses = false
    });
    },
    async markCourseAsUpdatedAfterInstructorManualUpdate(courseId) {
      if (!courseId) return
      try {
        const response = await axios.post(`${import.meta.env.VITE_BASE_LINK}/mark-course-as-updated-after-instructor-manual-update`, {
          courseId,
        }, { withCredentials: true })
        this.updatedCourse = response.data.updatedCourse
      } catch (error) {
        console.error('markCourseAsUpdatedAfterInstructorManualUpdate:', error)
      }
      this.clearLearnerInstructorManualUpdatePending(courseId)
    },
    clearLearnerInstructorManualUpdatePending(courseId) {
      const norm = String(courseId)
      const clearItem = (item) => {
        if (!item || String(item.id) !== norm) return item
        const prev = item.hasANewInstructorManualUpdate ?? item.has_a_new_instructor_manual_update
        const next = typeof prev === 'object' && prev != null
          ? { ...prev, pending: false }
          : { pending: false }
        return {
          ...item,
          hasANewInstructorManualUpdate: next,
          has_a_new_instructor_manual_update: next,
        }
      }
      if (Array.isArray(this.userCourses)) {
        this.userCourses = this.userCourses.map(clearItem)
      }
      if (Array.isArray(this.recentActivity)) {
        this.recentActivity = this.recentActivity.map(clearItem)
      }
    },
    /**
     * If course is inside updateWindowStart–updateWindowEnd, show HOLD ON and wait for confirm/cancel.
     * Take action: once per course per update window. Assign: every time.
     * Missing dates → no gate. Resolves true to proceed, false to abort.
     * @param {object|null|undefined} course
     * @param {{ action?: 'take'|'buy'|'assign'|'add' }} [options]
     * @returns {Promise<boolean>}
     */
    requestCourseUpdateHoldOn (course, options = {}) {
      const action = normalizeHoldOnAction(options?.action)
      const enriched = withCachedCourseUpdateWindow(this.enrichCourseForUpdateHoldOn(course))
      if (!courseUpdateHoldOnApplies(enriched) || !courseNeedsUpdateHoldOn(enriched)) {
        return Promise.resolve(true)
      }
      if (action === 'take' && !shouldShowTakeHoldOn(enriched, this.user)) {
        this.stampUserCourseUpdateWindow(enriched)
        return Promise.resolve(true)
      }
      if (typeof this._updateHoldOnResolver === 'function') {
        this._updateHoldOnResolver(false)
        this._updateHoldOnResolver = null
      }
      this.updateHoldOnCourse = enriched
      this.updateHoldOnAction = action
      this.updateHoldOnOpen = true
      return new Promise((resolve) => {
        this._updateHoldOnResolver = resolve
      })
    },
    async resolveCourseUpdateHoldOn (proceed) {
      const course = this.updateHoldOnCourse
      const action = this.updateHoldOnAction
      this.updateHoldOnOpen = false
      const resolver = this._updateHoldOnResolver
      this._updateHoldOnResolver = null
      this.updateHoldOnCourse = null
      this.updateHoldOnAction = 'assign'
      if (action === 'take' && course) {
        await this.persistTakeHoldOnSeen(course)
      }
      if (typeof resolver === 'function') {
        resolver(Boolean(proceed))
      }
    },
    /**
     * Persist take HOLD ON acknowledgement on the user account for this course window.
     * @param {object|null|undefined} course
     */
    async persistTakeHoldOnSeen (course) {
      const entry = buildTakeHoldOnSeenEntry(course)
      if (!entry) return
      this.stampUserCourseUpdateWindow(course)
      const nextMap = {
        ...(this.user?.courseUpdateHoldOnSeen && typeof this.user.courseUpdateHoldOnSeen === 'object'
          ? this.user.courseUpdateHoldOnSeen
          : {}),
        [entry.courseId]: {
          updateWindowStart: entry.updateWindowStart,
          updateWindowEnd: entry.updateWindowEnd,
          seenAt: entry.seenAt,
        },
      }
      // Optimistic local update so the badge appears immediately.
      if (this.user && typeof this.user === 'object') {
        this.user = {
          ...this.user,
          courseUpdateHoldOnSeen: nextMap,
        }
      }
      this.takeHoldOnSeenTick += 1
      const userId = this.user?.id
      if (!userId) return
      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/set-course-update-hold-on-seen`,
          {
            userId,
            courseId: entry.courseId,
            updateWindowStart: entry.updateWindowStart,
            updateWindowEnd: entry.updateWindowEnd,
          },
          { withCredentials: true }
        )
        const returned = data?.user?.courseUpdateHoldOnSeen
        if (returned && typeof returned === 'object' && this.user) {
          this.user = {
            ...this.user,
            courseUpdateHoldOnSeen: returned,
          }
          this.takeHoldOnSeenTick += 1
        }
      } catch (error) {
        console.error('Could not save course update HOLD ON acknowledgement:', error)
      }
    },
    /**
     * Keep enrollment rows / activity in sync with catalog update-window fields for badges.
     * @param {object|null|undefined} course
     */
    stampUserCourseUpdateWindow (course) {
      if (!course || course.id == null) return
      cacheCourseUpdateWindow(course)
      const { updateWindowStart, updateWindowEnd, sportSeasons } = getCourseUpdateExpectations(course)
      if (!updateWindowStart && !updateWindowEnd) return
      const id = String(course.id)
      const patch = (row) => {
        if (!row || String(row.id) !== id) return row
        return {
          ...row,
          updateWindowStart: updateWindowStart || row.updateWindowStart || null,
          updateWindowEnd: updateWindowEnd || row.updateWindowEnd || null,
          sportSeasons: sportSeasons?.length ? sportSeasons : row.sportSeasons,
        }
      }
      if (Array.isArray(this.userCourses)) {
        this.userCourses = this.userCourses.map(patch)
      }
      if (Array.isArray(this.recentActivity)) {
        this.recentActivity = this.recentActivity.map(patch)
      }
    },
    /**
     * Soft “Update coming soon” pill on learner course cards after take HOLD ON was seen once.
     * @param {object|null|undefined} course
     */
    courseShowsUpdateComingSoonBadge (course) {
      void this.takeHoldOnSeenTick
      let enriched = withCachedCourseUpdateWindow(this.enrichCourseForUpdateHoldOn(course))
      const id = enriched?.id != null ? String(enriched.id) : ''
      const enrollment = id && Array.isArray(this.userCourses)
        ? this.userCourses.find((row) => row && String(row.id) === id)
        : null
      if (enrollment) {
        const ownArchive = Array.isArray(enriched.courseVersionProgressArchive)
          ? enriched.courseVersionProgressArchive
          : []
        enriched = {
          ...enriched,
          type: enriched.type || enrollment.type,
          currentCourseVersion: enriched.currentCourseVersion ?? enrollment.currentCourseVersion,
          publishedCatalogVersion: enriched.publishedCatalogVersion ?? enrollment.publishedCatalogVersion,
          courseVersionProgressArchive: ownArchive.length
            ? ownArchive
            : enrollment.courseVersionProgressArchive,
        }
      }
      return courseShowsUpdateComingSoonBadgeHelper(enriched, this.user)
    },
    /**
     * Enrich a cart/catalog stub with window fields from onlineStore / instructor courses.
     * @param {object|null|undefined} item
     */
    enrichCourseForUpdateHoldOn (item) {
      if (!item || typeof item !== 'object') return item
      const withCache = withCachedCourseUpdateWindow(item)
      if (courseNeedsUpdateHoldOn(withCache)) return withCache
      const id = item.id
      if (id == null || id === '') return withCache
      const catalogs = []
      if (Array.isArray(this.onlineStore)) catalogs.push(...this.onlineStore)
      if (Array.isArray(this.courses)) catalogs.push(...this.courses)
      if (Array.isArray(this.userCourses)) catalogs.push(...this.userCourses)
      if (this.selected_course && typeof this.selected_course === 'object') {
        catalogs.push(this.selected_course)
      }
      if (this.updatedCourse && typeof this.updatedCourse === 'object') {
        catalogs.push(this.updatedCourse)
      }
      const found = catalogs.find((c) => c && String(c.id) === String(id))
      if (!found) return withCache
      return withCachedCourseUpdateWindow({
        ...withCache,
        updateWindowStart: found.updateWindowStart ?? withCache.updateWindowStart,
        updateWindowEnd: found.updateWindowEnd ?? withCache.updateWindowEnd,
        sportSeasons: found.sportSeasons ?? withCache.sportSeasons,
        courseDraft: found.courseDraft ?? withCache.courseDraft,
      })
    },
    /**
     * Sequential HOLD ON for multiple courses (e.g. bulk assign).
     * @param {Array} courses
     * @param {{ action?: 'take'|'assign' }} [options]
     * @returns {Promise<boolean>}
     */
    async ensureCourseUpdateHoldOnForCourses (courses, options = {}) {
      const list = Array.isArray(courses) ? courses : []
      const action = normalizeHoldOnAction(options?.action || 'assign')
      for (const raw of list) {
        const course = this.enrichCourseForUpdateHoldOn(raw)
        const ok = await this.requestCourseUpdateHoldOn(course, { action })
        if (!ok) return false
      }
      return true
    },
    async loadCourse(course, options = {}) {
      let holdCourse = this.enrichCourseForUpdateHoldOn(course)
      try {
        const { data } = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-updated-course`, {
          courseId: course.id,
        })
        this.updatedCourse = data.updatedCourse || {}
        const updated = this.updatedCourse
        if (updated && typeof updated === 'object') {
          holdCourse = {
            ...holdCourse,
            updateWindowStart: updated.updateWindowStart ?? holdCourse?.updateWindowStart,
            updateWindowEnd: updated.updateWindowEnd ?? holdCourse?.updateWindowEnd,
            sportSeasons: updated.sportSeasons ?? holdCourse?.sportSeasons,
            courseDraft: updated.courseDraft ?? holdCourse?.courseDraft,
          }
        }
        cacheCourseUpdateWindow(holdCourse)
        this.stampUserCourseUpdateWindow(holdCourse)
        console.log('Loading course')
      } catch (error) {
        console.error('Error loading course:', error);
      }
      if (!options.skipUpdateHoldOn) {
        const allowed = await this.requestCourseUpdateHoldOn(holdCourse, { action: 'take' })
        if (!allowed) return
      }
      this.isLoadingCourse = true;
      try {
          const {
              data
          } = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-course`, {
              id: course.id
          });
          const {
              userCourse,
              global_settings,
              course_settings,
              quizRequests
          } = data;
          
          const loadedQuizSettings = global_settings?.isActive
            ? global_settings.quiz
            : course_settings?.[course.id]?.quiz || {
                unlimitedAttempts: true,
                attempts: 0,
                passingScore: 70,
                failAction: 'advance',
                allowQuizRetake: true,
                showMissedQuestions: true,
              };
          this.quizSettings = {
            ...loadedQuizSettings,
            failAction: normalizeFailAction(loadedQuizSettings?.failAction),
          };
          this.userHasWatchedPreCourseVideo = (userCourse.hasWatchedPreCourseVideo && userCourse.hasWatchedPreCourseVideo == true) ? true : false;
          this.selectedCourseType = userCourse.assignedBy.companyID || (userCourse.assignedBy.name && userCourse.assignedBy.name === 'DragonFly') ? "assigned" : "purchased";
          this.user.quizRequests = quizRequests;

          if (userCourse.module_history.length === 0) {
              const convertResponse = await this.convertCourse(course.id, this.user.email);
              this.handleCourseResponse(convertResponse, course);
          } else {
              this.handleCourseResponse({
                  data
              }, course);
          }
      } catch (error) {
          console.error('Error loading course:', error);
          this.isLoadingCourse = false;
      }
  },

  async convertCourse(courseId, email) {
      try {
          const {
              data
          } = await axios.post(`${import.meta.env.VITE_BASE_LINK}/convert-course`, {
              id: courseId,
              email
          });
          if (data.code !== 200) throw new Error(data.error);
          return {
              data
          };
      } catch (error) {
          console.error('Error converting course:', error);
          throw error;
      }
  },

  handleCourseResponse(response, course) {
      this.goToEducationalCourse(response, course);
  },

  async goToEducationalCourse(response, course) {

      const {
          data
      } = response;
      const learnerModules = modulesForLearnerEnrollment(
        data.course_modules,
        data.userCourse
      )
      Object.assign(this, {
          currentCourseView: 'progress',
          selected_course: data.course
            ? {
                ...data.course,
                modules: modulesForLearnerEnrollment(
                  data.course.modules,
                  data.userCourse
                ),
              }
            : data.course,
          challenge: data.challenge,
          user_course: data.userCourse,
          course_modules: learnerModules,
          selectedRulebook: data.rulebook,
          lastModified: Math.floor(Date.now() / 1000),
          optional_videos: data.optional_videos,
          module_optional_history: data.userCourse.module_optional_history,
          ...this.extractUserCourseData(data.userCourse),
          isWatchingVideo: true,
          currentVideoTimeDisplay: '0:00',
          module_history: this.processModuleHistory(data)
      });

      await this.updateModuleHistory();
      await this.getPreAndPostCourseVideos(course.id);
      if (this.selected_course){
        await this.loadNotes()
      }
      this.handleFootballCourseSpecifics();
      this.setActiveModule();
      if (this.user_course.assignedBy.companyID) {
        await this.getOrganizationNameFromSelectedCourse(this.user_course.assignedBy.companyID);
      }
      else {
        this.currentUserOrganization = {}
      }

      nextTick(() => {
          if (this.user_course.sport == 'Cheerleading') {
            this.$router.push({
              name: "ny-course",
              params: {
                  id: course.id
              }
          });
          }else{
            this.$router.push({
              name: "course",
              params: {
                  id: course.id
              }
          });
          }
          this.isLoadingCourse = false;
      });
  },

  async getPreAndPostCourseVideos(courseId) {
    await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-pre-and-post-course-videos`, {
      state: this.user.state,
      courseId: courseId
    }).then((response) => {
        this.preCourseVideos = response.data.preCourseVideos;
        this.postCourseVideos = response.data.postCourseVideos;
    }).catch((error) => {
        console.log(error)
    });
  },

  extractUserCourseData(userCourse) {
      const licenseBatch = userCourse.licenseBatch ?? userCourse.license_batch
      return {
          completed_videos: userCourse.completed_videos,
          completed_modules: userCourse.completed_modules,
          completed_quizzes: userCourse.completed_quizzes,
          current_module: userCourse.current_module,
          quiz_attempts: userCourse.quiz_attempts,
          current_video: userCourse.current_video,
          knowledge_checks: normalizeKnowledgeCheckProgress(userCourse.knowledge_checks),
          ...(licenseBatch && typeof licenseBatch === 'object' ? { licenseBatch } : {}),
          course_notes: userCourse.course_notes,
          lastModuleActivity: userCourse.lastModuleActivity,
          lastActiveVideo: userCourse.lastActiveVideo,
          active_quizzes: userCourse.active_quizzes,
          userType: userCourse.userType,
          ...(Array.isArray(userCourse?.certificates)
            ? { certificates: [...userCourse.certificates] }
            : {}),
          ...(userCourse && typeof userCourse === 'object' && 'completed_knowledge_check_ids' in userCourse
            ? { completed_knowledge_check_ids: userCourse.completed_knowledge_check_ids }
            : {})
      };
  },

  processModuleHistory(data) {
      const moduleHistory = data.userCourse.module_history.map(module => this.processModule(module, data.course));
      return this.addMissingModules(moduleHistory, data.course.modules);
  },

  processModule(module, course) {
      if (!module.videos) return module;
      const courseModule = course.modules.find(m => m.id === module.id);
      return {
          ...module,
          lastActivity: 'player',
          lastActiveVideo: courseModule.videos[0],
          completedVideos: [],
          quizResults: [],
          activeQuiz: null,
          dateCompleted: module.isComplete ? moment().format('MM/DD/YYYY hh:mm:ss a') : '',
          timestampCompleted: module.isComplete ? Math.floor(Date.now() / 1000) : 0,
          previousTime: 0,
          timestamp: Math.floor(Date.now() / 1000),
          isRequired: true
      };
  },

  addMissingModules(moduleHistory, courseModules) {
      const existingModuleIds = new Set(moduleHistory.map(m => m.id));
      const missingModules = courseModules
          .filter(module => !existingModuleIds.has(module.id))
          .map(module => ({
              id: module.id,
              lastActivity: 'player',
              lastActiveVideo: module.videos[0],
              completedVideos: [],
              quizResults: [],
              activeQuiz: null,
              isComplete: false,
              dateCompleted: '',
              timestampCompleted: 0,
              previousTime: 0,
              timestamp: 0,
              isRequired: !this.optional_videos.some(m => m.id === module.id)
          }));
      return [...moduleHistory, ...missingModules];
  },

  handleFootballCourseSpecifics() {
      if (this.selected_course.id === 'e5953e86-92a2-4d1c-b3ec-8382d71d76b7') {
          this.module_history = this.module_history
              .filter(module => module.id !== 'CF99E3A0-AB17-423A-AC4D-B82E421E70B6');
          if (this.module_history[0].id === '02A2ADED-5CB3-4855-82A3-C7561269DC3F' &&
              this.module_history[10].id === '84951D9F-FFF4-4C6C-9777-EC99D32DD831') {
              this.moveElement(this.module_history, 10, 0);
          }
      }
  },

  setActiveModule() {
      const incompleteRequiredModule = this.module_history.find(module => !module.isComplete && module.isRequired);
      if (incompleteRequiredModule) {
          this.active_module = this.selected_course.modules.find(module => module.id === incompleteRequiredModule.id) || this.selected_course.modules[0];
          this.handleModuleActivity(incompleteRequiredModule);
      } else {
          const lastModule = this.module_history[this.module_history.length - 1];
          this.active_module = this.selected_course.modules.find(module => module.id === lastModule.id);
          this.currentCourseMode = 'home';
          this.currentModuleIndex = 0;
      }
  },

  handleModuleActivity(module) {
      /** History row `module` vs Pinia `current_module` can disagree after `loadCourse` (e.g. version bump); routing must follow the history row or `selectModule` persists the wrong row and `save-module-progress` can flip completion incorrectly. */
      const fullModule = this.selected_course.modules.find((m) => m.id === module.id) || this.current_module
      const isOptional = this.optional_videos.some((m) => m.id === fullModule.id)
      if (isOptional) {
        this.selectOptionalModule(fullModule)
      } else {
        this.selectModule(fullModule)
      }
  },

  async updateCheerRole(userType) {
      try {
          await axios.post(`${import.meta.env.VITE_BASE_LINK}/update-role`, {
              user_id: this.user.id,
              course_id: this.selectedCourse.id,
              userType
          });
          this.showCheerModal = false;
          nextTick(() => this.loadCourse(this.selectedCourse));
      } catch (error) {
          console.error('Error updating cheer role:', error);
      }
  },
  async reloadPOVPack() {
    this.isLoadingPOVCourseData = true;
    await axios.post(`${import.meta.env.VITE_BASE_LINK}/getRepsCourseData`, {
            id: this.currentCourse.id,
        })
        .then((response) => {
            //   this.mainStore.currentCourse = response.data.userCourse;
            this.progress = response.data.repProgress;
            this.videoCount = response.data.videoCount;
            this.currentRepsCourse = response.data.userCourse;
            this.currentRepsCourseAttempts = response.data.attempts;
        })
        .catch(function (error) {
            alert(error);
        })
        .finally(() => {
            this.isLoadingPOVCourseData = false;
        });
  },
  goToPOV(course) {
    this.isLoadingCourse = true;
    axios
        .post(`${import.meta.env.VITE_BASE_LINK}/getRepsCourseData`, {
            id: course.id,
        })
        .then((response) => {
            this.progress = response.data.repProgress;
            this.videoCount = response.data.videoCount;
            this.currentCourse = response.data.repCourse;
            this.currentRepsCourse = response.data.userCourse;
            this.currentRepsCourseAttempts = response.data.attempts;
            this.currentEnrolled = response.data.currentEnrolled;
            nextTick(() => {
                this.$router.push({
                    name: "interactive-view",
                    params: {
                        id: course.id,
                    },
                });
                this.isLoadingCourse = false;
            });
        })
        .catch(function (error) {
            alert(error);
            this.isLoadingCourse = false;
        });
},
deleteStudent(student) {
  const index = this.students.findIndex(s => s.info.id === student.info.id);
  if (index !== -1) {
    this.students.splice(index, 1);
    this.students.sort((a, b) => a.info.name.localeCompare(b.info.name));
  }
},
restoreStudent(student) {
  this.students.push(student);
  // sort by name
  this.students.sort((a, b) => a.info.name.localeCompare(b.info.name));
},
archiveStudent(student) {
  const index = this.students.findIndex(s => s.info.id === student.info.id);
  if (index !== -1) {
    const archivedStudent = this.students.splice(index, 1)[0];
    this.archivedUsers.push(archivedStudent);
    this.archivedUsers.sort((a, b) => a.info.name.localeCompare(b.info.name));
  }
},
unarchiveStudent(student) {
  const index = this.archivedUsers.findIndex(s => s.info.id === student.info.id);
  if (index !== -1) {
    const unarchivedStudent = this.archivedUsers.splice(index, 1)[0];
    this.students.push(unarchivedStudent);
    this.students.sort((a, b) => a.info.name.localeCompare(b.info.name));
  }
},
async getOrganizationNameFromSelectedCourse(organizationId) {
   await axios
      .post(`${import.meta.env.VITE_BASE_LINK}/getUserOrganizations`, {
          ids: [organizationId],
      })
      .then((response) => {
          if (response.data.code == 200) {
            this.currentUserOrganization = response.data.organizations[0];
          } else {
            alert(response.data.message);
            return null;
          }
      })
      .catch((error) => {
          alert(error);
          return null;
      });
  },
  async getUpdatedFutureModulesForInstructor(organizationId) {
    await axios
      .post(`${import.meta.env.VITE_BASE_LINK}/getUserOrganizations`, {
        ids: [organizationId],
      })
      .then((response) => {
        if (response.data.code == 200) {
          this.currentInstructorsOrgFutureModules = response.data.organizations[0].futureModules;
        } else {
          alert(response.data.message);
          return null;
        }
      })
      .catch((error) => {
        alert(error);
        return null;
      });
  },
  getYouTubeVideoId(url) {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    try {
      const parsed = new URL(trimmed);
      const host = parsed.hostname.replace(/^www\./, '').replace(/^m\./, '');
      if (host === 'youtu.be') {
        return parsed.pathname.split('/').filter(Boolean)[0] || null;
      }
      if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'music.youtube.com') {
        const fromQuery = parsed.searchParams.get('v');
        if (fromQuery) return fromQuery;
        const parts = parsed.pathname.split('/').filter(Boolean);
        const markers = ['embed', 'shorts', 'live', 'v'];
        const markerIndex = parts.findIndex((part) => markers.includes(part));
        if (markerIndex !== -1 && parts[markerIndex + 1]) {
          return parts[markerIndex + 1];
        }
      }
      return null;
    } catch (error) {
      if (trimmed.includes('youtu.be/')) {
        return trimmed.split('youtu.be/')[1].split(/[?&#/]/)[0] || null;
      }
      const match = trimmed.match(/[?&]v=([^&#]+)/);
      return match ? match[1] : null;
    }
  },
  youtubeEmbedUrl(url, { autoplay = false } = {}) {
    const id = this.getYouTubeVideoId(url);
    if (!id) return '';
    const params = new URLSearchParams({
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
    });
    try {
      const parsed = new URL(url);
      const t = parsed.searchParams.get('t') || parsed.searchParams.get('start');
      if (t && /^\d+$/.test(t)) params.set('start', t);
    } catch (error) {
      // Share links that are not a full URL still embed from the video id.
    }
    if (autoplay) {
      params.set('autoplay', '1');
      params.set('mute', '1');
      params.set('loop', '1');
      params.set('playlist', id);
    }
    return `https://www.youtube.com/embed/${id}?${params.toString()}`;
  },
  async addAnnouncementView(announcementId, actionType = 'view') {
    try {
        await axios.post(`${import.meta.env.VITE_BASE_LINK}/addAnnouncementView`, {
            announcementId: announcementId,
            userId: this.user.id,
            actionType: actionType
        });
    } catch (error) {
        console.error('Error adding popup announcement view:', error);
    }
  },
  async fetchOrganizationCustomFilters(organizationId) {
    const id = organizationId != null ? String(organizationId).trim() : ''
    if (!id) return
    try {
      const { data } = await axios.post(`${import.meta.env.VITE_BASE_LINK}/get-custom-filters`, {
        organizationId: id,
      })
      const list = data.filters || data.customFilters || data.data?.filters
      const ok = data.code === 200 || data.code === '200' || data.success === true
      if (ok && Array.isArray(list)) {
        this.setOrganizationCustomFilters(list)
      }
    } catch (e) {
      console.error('fetchOrganizationCustomFilters:', e)
    }
  },
  setOrganizationCustomFilters(filters) {
    if (!Array.isArray(filters)) {
      this.organizationCustomFilters = []
      return
    }
    this.organizationCustomFilters = filters
      .map((f) => normalizeCustomFilter(f))
      .filter(Boolean)
  },
  upsertOrganizationCustomFilter(filter) {
    const f = normalizeCustomFilter(filter)
    if (!f) return
    const list = this.organizationCustomFilters
    const idx = list.findIndex((x) => String(x.id) === String(f.id))
    if (idx === -1) {
      list.push(f)
    } else {
      list[idx] = f
    }
  },
  /** Remove filter from org list and clear that tag key for every learner in memory (after delete-custom-filter API). */
  removeOrganizationCustomFilterAndLearnerTags(organizationId, filterId) {
    const fid = filterId != null ? String(filterId) : ''
    if (!fid) return
    const list = this.organizationCustomFilters
    const idx = list.findIndex((x) => String(x.id) === fid)
    if (idx !== -1) {
      list.splice(idx, 1)
    }
    const strip = (arr) => {
      for (const s of arr) {
        this.patchStudentCustomFilterValue(s, organizationId, fid, null)
      }
    }
    strip(this.students)
    strip(this.archivedUsers)
  },
  patchStudentCustomFilterValue(student, organizationId, filterId, value) {
    if (!student || !student.info) return;
    patchOrgCustomFilterValue(student, organizationId, filterId, value);
  },
  /** After bulk-update-student-custom-filters (API returns counts only). */
  applyBulkCustomFilterAssignment(organizationId, filterId, studentIds, value) {
    const fid = filterId != null ? String(filterId) : '';
    if (!fid || !Array.isArray(studentIds) || !studentIds.length) return;
    const val = value === null || value === undefined ? null : value;
    const idSet = new Set(studentIds.map(String));
    const patchList = (arr) => {
      for (const s of arr) {
        const sid = s?.info?.id;
        if (sid == null) continue;
        if (idSet.has(String(sid))) {
          this.patchStudentCustomFilterValue(s, organizationId, fid, val);
        }
      }
    };
    patchList(this.students);
    patchList(this.archivedUsers);
  },
  promptEducationalCoursesUpgradeIfSupplemental() {
    if (this.organization?.isActive && organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }
    if (this.organization?.isActive && organizationIsSupplemental(this.organization)) {
      this.upgradeModalContext = 'supplemental'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }
    return false
  },
  promptSupplementalPovUpgradeIfPublications() {
    if (this.organization?.isActive && organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-pov'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }
    return false
  },
  organizationHasPremiumSeatLicense() {
    const licenseType = resolveSeatLicenseType(organizationSeatLicenseType(this.organization))
    return licenseType === 'Premium'
  },
  promptExamsUpgradeIfNotPremium() {
    if (!this.organization?.isActive) return false

    // Publications orgs never get Practice Exams until they upgrade off publications-only.
    if (organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-exams'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }

    if (this.organizationHasPremiumSeatLicense()) return false

    this.upgradeModalContext = organizationIsSupplemental(this.organization)
      ? 'supplemental-exams'
      : 'premium'
    this.showEducationalCoursesUpgradeModal = true
    return true
  },
  /** Block opening premium learning activities when org is not on Premium course seats. */
  promptPremiumLearningActivityUpgradeIfNeeded(activity) {
    if (!this.organization?.isActive) return false
    if (this.organizationHasPremiumSeatLicense()) return false
    if (!isPremiumLearningActivity(activity)) return false

    if (organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-activities'
    } else if (organizationIsSupplemental(this.organization)) {
      this.upgradeModalContext = 'supplemental-activities'
    } else {
      this.upgradeModalContext = 'premium-activities'
    }
    this.showEducationalCoursesUpgradeModal = true
    return true
  },
  openInstructorEducationalCoursesTab() {
    if (this.promptEducationalCoursesUpgradeIfSupplemental()) return false
    this.currentDisplay = 'courses'
    this.currentCoursePage = 'courseList'
    this.courseListFilter = 'course'
    this.course_tab = 'details'
    return true
  },
  /** Courses tab + catalog update modal for an outdated org course copy (e.g. missed-question link). */
  openInstructorCatalogCourseUpdateForCourseId(courseId) {
    if (!courseId) return false
    if (!this.openInstructorEducationalCoursesTab()) return false
    this.pendingCatalogCourseUpdateCourseId = courseId
    return true
  },
  openInstructorSupplementalPovTab() {
    if (this.promptSupplementalPovUpgradeIfPublications()) return false
    this.currentDisplay = 'courses'
    this.currentCoursePage = 'courseList'
    this.courseListFilter = 'pov'
    this.course_tab = 'details'
    return true
  },
  openInstructorCoursesOverviewFromDashboard() {
    if (!this.organization?.isActive) return false
    if (organizationIsPublications(this.organization)) {
      return this.openInstructorEducationalCoursesTab()
    }
    if (organizationIsSupplemental(this.organization)) {
      return this.openInstructorSupplementalPovTab()
    }
    return this.openInstructorEducationalCoursesTab()
  },
  openInstructorLearningActivitiesTab() {
    if (this.promptEducationalCoursesUpgradeIfSupplemental()) return false
    this.currentDisplay = 'activities'
    this.currentLearningActivityPage = ''
    this.currentLearningActivity = {}
    return true
  },
  openInstructorExamsTab() {
    if (this.promptExamsUpgradeIfNotPremium()) return false
    this.currentDisplay = 'exams'
    this.currentExamPage = ''
    this.currentInstructorExam = {}
    return true
  },
  promptPublicationsReportsUpgrade() {
    if (this.organization?.isActive && organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-reports'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }
    return false
  },
  promptPublicationsResourcesUpgrade() {
    if (this.organization?.isActive && organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-resources'
      this.showEducationalCoursesUpgradeModal = true
      return true
    }
    return false
  },
  openInstructorReportsTab() {
    if (this.promptPublicationsReportsUpgrade()) return false
    this.currentDisplay = 'reports'
    const companyID = this.organization?.companyID
    if (companyID) {
      this.getOrganizationCertificateHistory(companyID)
      this.getOrganizationPackCompletionHistory(companyID)
    }
    return true
  },
  openInstructorResourcesTab() {
    if (this.promptPublicationsResourcesUpgrade()) return false
    this.currentDisplay = 'resources'
    return true
  },
  guardInstructorPortalAccess() {
    if (this.currentDisplay === 'developer' && !this.instructorCanAccessDeveloperPortal) {
      this.currentDisplay = 'dashboard'
    }
  },
  guardSupplementalEducationalCoursesAccess() {
    if (!this.organization?.isActive) return
    if (this.currentDisplay !== 'courses' || this.courseListFilter !== 'course') return

    if (organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications'
      this.showEducationalCoursesUpgradeModal = true
      this.currentDisplay = 'dashboard'
      this.currentCoursePage = 'courseList'
      return
    }
    if (!organizationIsSupplemental(this.organization)) return

    this.upgradeModalContext = 'supplemental'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
    this.currentCoursePage = 'courseList'
  },
  guardPublicationsSupplementalPovAccess() {
    if (!this.organization?.isActive) return
    if (!organizationIsPublications(this.organization)) return
    if (this.currentDisplay !== 'courses' || this.courseListFilter !== 'pov') return

    this.upgradeModalContext = 'publications-pov'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
    this.currentCoursePage = 'courseList'
  },
  guardSupplementalLearningActivitiesAccess() {
    if (!this.organization?.isActive) return
    if (this.currentDisplay !== 'activities') return

    if (organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications'
      this.showEducationalCoursesUpgradeModal = true
      this.currentDisplay = 'dashboard'
      this.currentLearningActivityPage = ''
      this.currentLearningActivity = {}
      return
    }
    if (!organizationIsSupplemental(this.organization)) return

    this.upgradeModalContext = 'supplemental'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
    this.currentLearningActivityPage = ''
    this.currentLearningActivity = {}
  },
  guardPremiumExamsAccess() {
    if (!this.organization?.isActive) return
    if (this.currentDisplay !== 'exams') return

    if (organizationIsPublications(this.organization)) {
      this.upgradeModalContext = 'publications-exams'
      this.showEducationalCoursesUpgradeModal = true
      this.currentDisplay = 'dashboard'
      this.currentExamPage = ''
      this.currentInstructorExam = {}
      return
    }

    if (this.organizationHasPremiumSeatLicense()) return

    this.upgradeModalContext = organizationIsSupplemental(this.organization)
      ? 'supplemental-exams'
      : 'premium'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
    this.currentExamPage = ''
    this.currentInstructorExam = {}
  },
  guardPublicationsReportsAccess() {
    if (!this.organization?.isActive) return
    if (!organizationIsPublications(this.organization)) return
    if (this.currentDisplay !== 'reports') return

    this.upgradeModalContext = 'publications-reports'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
  },
  guardPublicationsResourcesAccess() {
    if (!this.organization?.isActive) return
    if (!organizationIsPublications(this.organization)) return
    if (this.currentDisplay !== 'resources') return

    this.upgradeModalContext = 'publications-resources'
    this.showEducationalCoursesUpgradeModal = true
    this.currentDisplay = 'dashboard'
  },
  nfhsDigitalState() {
    if (this.nfhsDigital && Array.isArray(this.nfhsDigital.readers)) {
      const readers = this.nfhsDigital.readers
      const valid = readers.filter(
        (reader) => reader?.publication?.id && String(reader.id) !== 'nfhs-home'
      )
      if (valid.length !== readers.length) {
        this.nfhsDigital.readers = valid
      }
      return this.nfhsDigital
    }
    this.nfhsDigital = createDefaultNfhsDigitalState()
    return this.nfhsDigital
  },
  readerBackpackState() {
    if (this.readerBackpack && Array.isArray(this.readerBackpack.items)) {
      return this.readerBackpack
    }
    this.readerBackpack = createDefaultReaderBackpackState()
    return this.readerBackpack
  },
  /** Hydrates from localStorage the first time it is used for this user. */
  ensureReaderBackpackLoaded() {
    const state = this.readerBackpackState()
    const userId = this.user?.id || null
    if (state.loadedFor === userId) return
    state.items = loadBackpackItems(userId)
    state.loadedFor = userId
  },
  persistReaderBackpack() {
    const state = this.readerBackpackState()
    saveBackpackItems(this.user?.id || null, state.items)
  },
  isInReaderBackpack(sourceType, id) {
    this.ensureReaderBackpackLoaded()
    const key = backpackItemKey(sourceType, id)
    return this.readerBackpackState().items.some(
      (item) => backpackItemKey(item.sourceType, item.id) === key
    )
  },
  addPublicationToReaderBackpack(publication) {
    const item = buildPublicationBackpackItem(publication)
    if (!item) return
    this.ensureReaderBackpackLoaded()
    const state = this.readerBackpackState()
    const key = backpackItemKey(item.sourceType, item.id)
    const existingIndex = state.items.findIndex(
      (entry) => backpackItemKey(entry.sourceType, entry.id) === key
    )
    const isNew = existingIndex === -1
    if (!isNew) state.items.splice(existingIndex, 1)
    state.items.unshift(item)
    this.persistReaderBackpack()
    this.flashReaderBackpackCatch()
    if (isNew && !isFavoritePromptSuppressed()) {
      state.savedPromptItem = item
    }
  },
  removeFromReaderBackpack(sourceType, id) {
    this.ensureReaderBackpackLoaded()
    const state = this.readerBackpackState()
    const key = backpackItemKey(sourceType, id)
    state.items = state.items.filter(
      (item) => backpackItemKey(item.sourceType, item.id) !== key
    )
    this.persistReaderBackpack()
  },
  flashReaderBackpackCatch() {
    const state = this.readerBackpackState()
    state.justCaught = true
    setTimeout(() => {
      const next = this.readerBackpackState()
      next.justCaught = false
    }, 1000)
  },
  toggleReaderBackpackVault(open) {
    this.ensureReaderBackpackLoaded()
    const state = this.readerBackpackState()
    state.isOpen = open == null ? !state.isOpen : Boolean(open)
  },
  dismissReaderBackpackSavedPrompt() {
    this.readerBackpackState().savedPromptItem = null
  },
  /**
   * Vault row click. A reader that is still in the pool is restored here so its
   * iframe is reused; otherwise the publications host opens it from pendingOpen.
   */
  requestReaderBackpackOpen(item) {
    const state = this.readerBackpackState()
    state.isOpen = false
    if (!item) {
      state.pendingOpen = null
      return
    }
    const readerId = String(item.id)
    const existing = this.nfhsDigitalState().readers.find(
      (reader) => String(reader.id) === readerId
    )
    if (existing) {
      state.pendingOpen = null
      this.openNfhsDigitalWindow({
        publication: item.payload || { id: readerId, title: item.title, image: item.image },
        mode: 'read',
      })
      return
    }
    state.pendingOpen = item
  },
  clearReaderBackpackPendingOpen() {
    this.readerBackpackState().pendingOpen = null
  },
  beginBackpackDockDrag(publication, pointer, stageFrom) {
    const item = buildPublicationBackpackItem(publication)
    if (!item) return
    this.ensureReaderBackpackLoaded()
    const state = this.readerBackpackState()
    state.isDragging = true
    state.isOpen = false
    state.dropHover = false
    state.dragItem = item
    state.pointer = pointer ? { x: pointer.x, y: pointer.y } : null
    state.stageFrom = stageFrom || null
  },
  updateBackpackDockDrag(pointer) {
    const state = this.readerBackpackState()
    if (!state.isDragging) return
    state.pointer = pointer ? { x: pointer.x, y: pointer.y } : null
  },
  setBackpackDropHover(hover) {
    const state = this.readerBackpackState()
    if (!state.isDragging) return
    state.dropHover = Boolean(hover)
  },
  /** @returns {boolean} true when the item was stashed. */
  endBackpackDockDrag({ commit = true } = {}) {
    const state = this.readerBackpackState()
    const stashed = Boolean(commit && state.dropHover && state.dragItem)
    if (stashed) {
      this.addPublicationToReaderBackpack(state.dragItem.payload)
    }
    state.isDragging = false
    state.dropHover = false
    state.dragItem = null
    state.pointer = null
    state.stageFrom = null
    return stashed
  },
  /** @param {{ publication?: object, mode?: 'read'|'preview' }} options */
  openNfhsDigitalWindow(options = {}) {
    const publication = options.publication
    if (!publication?.id) return null
    const state = this.nfhsDigitalState()
    const id = String(publication.id)
    const existing = state.readers.find((reader) => String(reader.id) === id)
    if (existing) {
      // Focus only for an already-open reader. Do not rewrite a live read
      // iframe URL: remounting destroys the NFHS SPA holding the login.
      // Preview is the store listing, so it is safe to switch into preview.
      const reopened = Boolean(existing.collapsed)
      existing.publication = mergeNfhsPublication(existing.publication, publication)
      if (options.mode === 'preview') {
        existing.mode = 'preview'
      } else if (existing.collapsed && options.mode === 'read') {
        existing.mode = 'read'
      }
      existing.collapsed = false
      this.bringNfhsDigitalReaderToFront(id)
      if (reopened) {
        recordNfhsBookOpen({
          bookId: id,
          bookTitle: existing.publication?.title || publication.title || publication.name,
          mode: existing.mode,
        })
      }
      return existing.id
    }
    const mode = options.mode === 'preview' ? 'preview' : 'read'
    const stackIndex = state.readers.filter((reader) => !reader.collapsed).length
    const zIndex = Math.max(NFHS_PANEL_Z_BASE, Number(state.nextZ) || NFHS_PANEL_Z_BASE) + 1
    const reader = createNfhsDigitalReader({ publication, mode }, stackIndex, zIndex)
    if (!reader) return null
    recordNfhsBookOpen({
      bookId: id,
      bookTitle: publication.title || publication.name,
      mode,
    })
    state.nextZ = zIndex
    state.readers.push(reader)
    return reader.id
  },
  patchNfhsDigitalReader(id, patch) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    Object.assign(reader, patch)
  },
  bringNfhsDigitalReaderToFront(id) {
    const state = this.nfhsDigitalState()
    const reader = state.readers.find((item) => item.id === id)
    if (!reader) return
    const zIndex = Math.max(NFHS_PANEL_Z_BASE, Number(state.nextZ) || NFHS_PANEL_Z_BASE) + 1
    reader.zIndex = zIndex
    state.nextZ = zIndex
  },
  collapseNfhsDigitalReader(id) {
    const state = this.nfhsDigitalState()
    const reader = state.readers.find((item) => item.id === id)
    // Only read mode docks to a thumbnail, matching State Portal.
    if (!reader || reader.mode !== 'read') return
    const { vh } = viewportSize()
    reader.collapsed = true
    reader.maximized = false
    reader.dockFloating = false
    reader.dockLeft = null
    reader.dockTop = nextDockTop(state.readers, reader.top, vh, reader.id)
    this.bringNfhsDigitalReaderToFront(id)
  },
  expandNfhsDigitalReader(id) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    const { vw, vh } = viewportSize()
    Object.assign(reader, clampNfhsPanel(reader, vw, vh), {
      collapsed: false,
      dockFloating: false,
    })
    this.bringNfhsDigitalReaderToFront(id)
  },
  maximizeNfhsDigitalReader(id) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    const { vw, vh } = viewportSize()
    if (reader.maximized && reader.restore) {
      Object.assign(reader, clampNfhsPanel(reader.restore, vw, vh), {
        collapsed: false,
        maximized: false,
        restore: null,
      })
      this.bringNfhsDigitalReaderToFront(id)
      return
    }
    reader.restore = {
      left: reader.left,
      top: reader.top,
      width: reader.width,
      height: reader.height,
    }
    Object.assign(reader, defaultMaximizedPanel(vw, vh), {
      collapsed: false,
      maximized: true,
    })
    this.bringNfhsDigitalReaderToFront(id)
  },
  moveNfhsDigitalReader(id, left, top) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    const { vw, vh } = viewportSize()
    Object.assign(reader, clampNfhsPanel({ ...reader, left, top }, vw, vh), {
      maximized: false,
    })
  },
  resizeNfhsDigitalReader(id, next) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    const { vw, vh } = viewportSize()
    Object.assign(reader, clampNfhsPanel({ ...reader, ...next }, vw, vh), {
      maximized: false,
    })
  },
  moveNfhsDigitalDock(id, left, top) {
    const reader = this.nfhsDigitalState().readers.find((item) => item.id === id)
    if (!reader) return
    const { vw, vh } = viewportSize()
    reader.dockFloating = true
    reader.dockLeft = clampNumber(left, NFHS_INSET, Math.max(NFHS_INSET, vw - NFHS_DOCK_WIDTH - NFHS_INSET))
    reader.dockTop = clampNumber(top, NFHS_INSET, Math.max(NFHS_INSET, vh - NFHS_DOCK_HEIGHT - NFHS_INSET))
  },
  /** Hard close, matching State Portal: splice the reader so its iframe unmounts. */
  closeNfhsDigitalReader(id) {
    const state = this.nfhsDigitalState()
    const idx = state.readers.findIndex((reader) => String(reader.id) === String(id))
    if (idx < 0) return
    state.readers.splice(idx, 1)
  },
  hideNfhsDigitalWindow() {
    const expanded = [...this.nfhsDigitalState().readers]
      .filter((reader) => !reader.collapsed)
      .sort((a, b) => b.zIndex - a.zIndex)[0]
    if (expanded) {
      this.collapseNfhsDigitalReader(expanded.id)
    }
  },
  restoreNfhsDigitalWindow() {
    const collapsed = [...this.nfhsDigitalState().readers]
      .filter((reader) => reader.collapsed)
      .sort((a, b) => b.zIndex - a.zIndex)[0]
    if (collapsed) {
      collapsed.collapsed = false
      this.bringNfhsDigitalReaderToFront(collapsed.id)
    }
  },
  closeNfhsDigitalSession() {
    requestNfhsEmbedLogout()
    this.nfhsDigital = createDefaultNfhsDigitalState()
  },
},
  
  persist: {
    enabled: true,
    strategies: [
      {
        // Safari's Web Storage quota is small enough that a plain sessionStorage write can throw
        // QuotaExceededError from inside the persist subscriber, aborting Vue's update flush.
        storage: safeSessionStorage,
        // Do not persist in-course payloads (selected_course, modules, history, notes, etc.).
        // Those are reloaded via loadCourse → GET /get-course and blow past localStorage quota.
        paths: [
          'interests',
          'user',
          'hasShownSupportButton',
          'userResources',
          'current_view',
          'hasBeenNotifiedAboutOHSAAHandbookCourse',
          'hasBeenNotifiedAboutRenewal',
          'isInDevMode',
          'codeType',
          'codeIsValid',
          'activeCartItems',
          'showCreditOverlay',
          'userCourses',
          'hasClickedExploreOnce',
          'hasAppliedDiscount',
          'discountCode',
          'hasAppliedCredits',
          'discountAmount',
          'didAcknowledgeCheerCourse',
          'currentChatUser',
          'newNotificationsSeen',
        ]
      }
    ]
  }
})